// Context
export const standardIdentity1Values = [
    { name: 'Reality', code: '0' },
    { name: 'Exercise', code: '1' },
    { name: 'Simulation', code: '2' },
] as const;
export type StandardIdentity1 = (typeof standardIdentity1Values)[number];
export type StandardIdentity1Code = (typeof standardIdentity1Values)[number]['code'];

// Standard Identity
export const standardIdentity2Values = [
    { code: '0', name: 'Pending' },
    { code: '1', name: 'Unknown' },
    { code: '2', name: 'Assumed Friend' },
    { code: '3', name: 'Friend' },
    { code: '4', name: 'Neutral' },
    { code: '5', name: 'Suspect/Joker' },
    { code: '6', name: 'Hostile/Faker' },
] as const;
export type StandardIdentity2 = (typeof standardIdentity2Values)[number];
export type StandardIdentity2Code = (typeof standardIdentity2Values)[number]['code'];

export const statusValues = [
    { code: '0', name: 'Present' },
    { code: '1', name: 'Planned/Anticipated/Suspect' },
    { code: '2', name: 'Present/Fully capable' },
    { code: '3', name: 'Present/Damaged' },
    { code: '4', name: 'Present/Destroyed' },
    { code: '5', name: 'Present/Full to capacity' },
] as const;
export type StatusCode = (typeof statusValues)[number]['code'];

export const headquartersTaskforceDummyValues = [
    { code: '0', name: 'Not Applicable' },
    { code: '1', name: 'Feint/Dummy' },
    { code: '2', name: 'Headquarters' },
    { code: '3', name: 'Feint/Dummy Headquarters' },
    { code: '4', name: 'Task Force' },
    { code: '5', name: 'Feint/Dummy Task Force' },
    { code: '6', name: 'Task Force Headquarters' },
    { code: '7', name: 'Feint/Dummy Task Force Headquarters' },
] as const;
export type HeadquartersTaskforceDummyCode =
    (typeof headquartersTaskforceDummyValues)[number]['code'];

// For Land
export const echelonValues = [
    { code: '00', name: 'Unspecified' },
    { code: '11', name: 'Team/Crew' },
    { code: '12', name: 'Squad' },
    { code: '13', name: 'Section' },
    { code: '14', name: 'Platoon/Detachment' },
    { code: '15', name: 'Company/Battery/Troop' },
    { code: '16', name: 'Battalion/Squadron' },
    { code: '17', name: 'Regiment/Group' },
    { code: '18', name: 'Brigade' },
    { code: '21', name: 'Division' },
    { code: '22', name: 'Corps/MEF' },
    { code: '23', name: 'Army' },
    { code: '24', name: 'Army Group/Front' },
    { code: '25', name: 'Region/Theater' },
    { code: '26', name: 'Command' },
] as const;
export type EchelonCode = (typeof echelonValues)[number]['code'];
export type Echelon = (typeof echelonValues)[number];

// For Land Asset
export const mobilityValues = [
    { code: '00', name: 'Unspecified' },
    { code: '31', name: 'Wheeled limited cross country' },
    { code: '32', name: 'Wheeled cross country' },
    { code: '33', name: 'Tracked' },
    { code: '34', name: 'Wheeled and tracked combination' },
    { code: '35', name: 'Towed' },
    { code: '36', name: 'Railway' },
    { code: '37', name: 'Pack animals' },
    { code: '41', name: 'Over snow (prime mover)' },
    { code: '42', name: 'Sled' },
    { code: '51', name: 'Barge' },
    { code: '52', name: 'Amphibious' },
] as const;
export type MobilityCode = (typeof mobilityValues)[number]['code'];
export type Mobility = (typeof mobilityValues)[number];

// For sea surface and sea subsurface
export const towedArrayValues = [
    { code: '00', name: 'Unspecified' },
    { code: '61', name: 'Short towed array' },
    { code: '62', name: 'Long towed array' },
] as const;
export type TowedArrayCode = (typeof towedArrayValues)[number]['code'];
export type TowedArray = (typeof towedArrayValues)[number];

export const airIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Military', code: '110000' },
    { entity: 'Military', entityType: 'Fixed Wing', code: '110100' },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Medical Evacuation (MEDEVAC)',
        code: '110101',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Attack/Strike',
        code: '110102',
    },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Bomber', code: '110103' },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Fighter', code: '110104' },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Fighter/Bomber',
        code: '110105',
    },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Cargo', code: '110107' },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Electronic Combat (EC)/Jammer',
        code: '110108',
    },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Tanker', code: '110109' },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Patrol', code: '110110' },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Reconnaissance',
        code: '110111',
    },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Trainer', code: '110112' },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Utility', code: '110113' },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Vertical or Short Take-off and Landing (VSTOL)',
        code: '110114',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Airborne Command Post (ACP)',
        code: '110115',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Airborne Early Warning (AEW)',
        code: '110116',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Antisurface Warfare',
        code: '110117',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Antisubmarine Warfare',
        code: '110118',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Communications',
        code: '110119',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Combat Search and Rescue (CSAR)',
        code: '110120',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Electronic Support (ES)',
        code: '110121',
    },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Government', code: '110122' },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Mine Countermeasures (MCM)',
        code: '110123',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Personnel Recovery',
        code: '110124',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Search and Rescue',
        code: '110125',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Special Operations Forces',
        code: '110126',
    },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Ultra Light', code: '110127' },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Photographic Reconnaissance',
        code: '110128',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Very Important Person (VIP)',
        code: '110129',
    },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Suppression of Enemy Air Defense',
        code: '110130',
    },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Passenger', code: '110131' },
    { entity: 'Military', entityType: 'Fixed Wing', entitySubtype: 'Escort', code: '110132' },
    {
        entity: 'Military',
        entityType: 'Fixed Wing',
        entitySubtype: 'Electronic Attack (EA)',
        code: '110133',
    },
    { entity: 'Military', entityType: 'Rotary Wing', code: '110200' },
    {
        entity: 'Military',
        entityType:
            'Unmanned Aircraft (UA) / Unmanned Aerial Vehicle (UAV) / Unmanned Aircraft System (UAS) / Remotely Piloted Vehicle (RPV)',
        code: '110300',
    },
    { entity: 'Military', entityType: 'Vertical–Takeoff UAV (VT–UAV)', code: '110400' },
    { entity: 'Military', entityType: 'Lighter Than Air', code: '110500' },
    { entity: 'Military', entityType: 'Airship', code: '110600' },
    { entity: 'Military', entityType: 'Tethered Lighter than Air', code: '110700' },
    { entity: 'Civilian', code: '120000' },
    { entity: 'Civilian', entityType: 'Fixed Wing', code: '120100' },
    { entity: 'Civilian', entityType: 'Rotary Wing', code: '120200' },
    {
        entity: 'Civilian',
        entityType:
            'Unmanned Aircraft (UA) / Unmanned Aerial Vehicle (UAV) / Unmanned Aircraft System (UAS) / Remotely Piloted Vehicle (RPV)',
        code: '120300',
    },
    { entity: 'Civilian', entityType: 'Lighter Than Air', code: '120400' },
    { entity: 'Civilian', entityType: 'Airship', code: '120500' },
    { entity: 'Civilian', entityType: 'Tethered Lighter than Air', code: '120600' },
    { entity: 'Weapon', code: '130000' },
    { entity: 'Weapon', entityType: 'Bomb', code: '130100' },
    { entity: 'Weapon', entityType: 'Decoy', code: '130200' },
    { entity: 'Manual Track', code: '140000' },
] as const;
export type AirIcon = (typeof airIconValues)[number];

export const airModifier1Values = [
    { modifier: 'Not Applicable', category: '', code: '00' },
    { modifier: 'Attack/Strike', category: 'Military Aircraft Type', code: '01' },
    { modifier: 'Bomber', category: 'Military Aircraft Type', code: '02' },
    { modifier: 'Cargo', category: 'Aircraft Type', code: '03' },
    { modifier: 'Fighter', category: 'Military Aircraft Type', code: '04' },
    { modifier: 'Interceptor', category: 'Military Aircraft Type', code: '05' },
    { modifier: 'Tanker', category: 'Aircraft Type', code: '06' },
    { modifier: 'Utility', category: 'Aircraft Type', code: '07' },
    {
        modifier:
            'Vertical or Short Take-off and Landing (VSTOL)/ Vertical Take-off and Landing (VTOL)',
        category: 'Aircraft Type',
        code: '08',
    },
    { modifier: 'Passenger', category: 'Aircraft Type', code: '09' },
    { modifier: 'Ultra Light', category: 'Aircraft Type', code: '10' },
    { modifier: 'Airborne Command Post (ACP)', category: 'Military Aircraft Type', code: '11' },
    { modifier: 'Airborne Early Warning (AEW)', category: 'Military Aircraft Type', code: '12' },
    { modifier: 'Government', category: 'Aircraft Type', code: '13' },
    { modifier: 'Medical Evacuation (MEDEVAC)', category: 'Mission Area', code: '14' },
    { modifier: 'Escort', category: 'Military Mission Area', code: '15' },
    { modifier: 'Electronic Combat (EC)/Jammer', category: 'Military Mission Area', code: '16' },
    { modifier: 'Patrol', category: 'Mission Area', code: '17' },
    { modifier: 'Reconnaissance', category: 'Mission Area', code: '18' },
    { modifier: 'Trainer', category: 'Mission Area', code: '19' },
    { modifier: 'Photographic (Reconnaissance)', category: 'Mission Area', code: '20' },
    { modifier: 'Personnel Recovery', category: 'Mission Area', code: '21' },
    { modifier: 'Antisubmarine Warfare', category: 'Military Mission Area', code: '22' },
    { modifier: 'Communications', category: 'Mission Area', code: '23' },
    { modifier: 'Electronic Support (ES)', category: 'Military Mission Area', code: '24' },
    { modifier: 'Mine Countermeasures (MCM)', category: 'Military Mission Area', code: '25' },
    { modifier: 'Search and Rescue', category: 'Mission Area', code: '26' },
    { modifier: 'Special Operations Forces', category: 'Military Mission Area', code: '27' },
    { modifier: 'Surface Warfare', category: 'Military Mission Area', code: '28' },
    { modifier: 'Very Important Person (VIP) Transport', category: 'Mission Area', code: '29' },
    { modifier: 'Combat Search and Rescue (CSAR)', category: 'Military Mission Area', code: '30' },
    {
        modifier: 'Suppression of Enemy Air Defenses',
        category: 'Military Mission Area',
        code: '31',
    },
    { modifier: 'Antisurface Warfare', category: 'Military Mission Area', code: '32' },
    { modifier: 'Fighter/Bomber', category: 'Military Aircraft Type', code: '33' },
    { modifier: 'Intensive Care', category: 'Mission Area', code: '34' },
    { modifier: 'Electronic Attack (EA)', category: 'Military Mission Area', code: '35' },
    { modifier: 'Multimission', category: 'Mission Area', code: '36' },
    { modifier: 'Hijacking/Hijacked', category: 'Crime', code: '37' },
    { modifier: 'ASW Helo- LAMPS', category: 'Mission Area', code: '38' },
    { modifier: 'ASW Helo – SH-60R', category: 'Mission Area', code: '39' },
    { modifier: 'Hijacker', category: 'Crime', code: '40' },
    { modifier: 'Cyberspace', category: 'Capability', code: '41' },
] as const;
export type AirModifier1 = (typeof airModifier1Values)[number];

export const airModifier2Values = [
    { modifier: 'Not Applicable', category: '', code: '00' },
    { modifier: 'Heavy', category: 'Cargo/Transport Capacity', code: '01' },
    { modifier: 'Medium', category: 'Cargo/Transport Capacity', code: '02' },
    { modifier: 'Light', category: 'Cargo/Transport Capacity', code: '03' },
    { modifier: 'Boom–Only', category: 'Re-Fueling Capability', code: '04' },
    { modifier: 'Drogue–Only', category: 'Re-Fueling Capability', code: '05' },
    { modifier: 'Boom and Drogue', category: 'Re-Fueling Capability', code: '06' },
    { modifier: 'Close Range', category: 'Range', code: '07' },
    { modifier: 'Short Range', category: 'Range', code: '08' },
    { modifier: 'Medium Range', category: 'Range', code: '09' },
    { modifier: 'Long Range', category: 'Range', code: '10' },
    { modifier: 'Downlinked', category: 'Track Link Availability', code: '11' },
    { modifier: 'Cyberspace', category: 'Capability', code: '12' },
] as const;
export type AirModifier2 = (typeof airModifier2Values)[number];

export const airMissileIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Missile', code: '110000' },
] as const;
export type AirMissileIcon = (typeof airMissileIconValues)[number];

export const airMissileModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Air', category: 'Launch Origin', code: '01' },
    { modifier: 'Surface', category: 'Launch Origin', code: '02' },
    { modifier: 'Subsurface', category: 'Launch Origin', code: '03' },
    { modifier: 'Space', category: 'Launch Origin', code: '04' },
    { modifier: 'Anti–Ballistic', category: 'Missile Class', code: '05' },
    { modifier: 'Ballistic', category: 'Missile Class', code: '06' },
    { modifier: 'Cruise', category: 'Missile Class', code: '07' },
    { modifier: 'Interceptor', category: 'Missile Class', code: '08' },
] as const;
export type AirMissileModifier1 = (typeof airMissileModifier1Values)[number];

export const airMissileModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Air', category: 'Missile Destination', code: '01' },
    { modifier: 'Surface', category: 'Missile Destination', code: '02' },
    { modifier: 'Subsurface', category: 'Missile Destination', code: '03' },
    { modifier: 'Space', category: 'Missile Destination', code: '04' },
    { modifier: 'Launched', category: 'Missile Status', code: '05' },
    { modifier: 'Missile', category: 'Missile Status', code: '06' },
    { modifier: 'Patriot', category: 'Missile Type-BMD', code: '07' },
    { modifier: 'Standard Missile-2 (SM-2)', category: 'Missile Type-AAW', code: '08' },
    { modifier: 'Standard Missile-6 (SM-6)', category: 'Missile Type-AAW', code: '09' },
    { modifier: 'Evolved Sea Sparrow Missile (ESSM)', category: 'Missile Type-AAW', code: '10' },
    { modifier: 'Rolling Airframe Missile (RAM)', category: 'Missile Type-AAW', code: '11' },
    { modifier: 'Short Range', category: 'Missile Range', code: '12' },
    { modifier: 'Medium Range', category: 'Missile Range', code: '13' },
    { modifier: 'Intermediate Range', category: 'Missile Range', code: '14' },
    { modifier: 'Long Range', category: 'Missile Range', code: '15' },
    { modifier: 'Intercontinental', category: 'Missile Range', code: '16' },
] as const;
export type AirMissileModifier2 = (typeof airMissileModifier2Values)[number];

export type AirMissileSymbol = {
    standardIdentity1: StandardIdentity1Code;
    standardIdentity2: StandardIdentity2Code;
    symbolSet: '02';
    status: StatusCode;
    headquartersTaskforceDummy: HeadquartersTaskforceDummyCode;
    icon: AirMissileIcon['code'];
    iconModifier1: AirMissileModifier1['code'];
    iconModifier2: AirMissileModifier2['code'];
};

export const spaceIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Military', code: '110000' },
    { entity: 'Military', entityType: 'Space Vehicle', code: '110100' },
    { entity: 'Military', entityType: 'Re–Entry Vehicle', code: '110200' },
    { entity: 'Military', entityType: 'Planet Lander', code: '110300' },
    { entity: 'Military', entityType: 'Orbiter Shuttle', code: '110400' },
    { entity: 'Military', entityType: 'Capsule', code: '110500' },
    { entity: 'Military', entityType: 'Satellite, General', code: '110600' },
    { entity: 'Military', entityType: 'Satellite', code: '110700' },
    { entity: 'Military', entityType: 'Antisatellite Weapon', code: '110800' },
    { entity: 'Military', entityType: 'Astronomical Satellite', code: '110900' },
    { entity: 'Military', entityType: 'Biosatellite', code: '111000' },
    { entity: 'Military', entityType: 'Communications Satellite', code: '111100' },
    { entity: 'Military', entityType: 'Earth Observation Satellite', code: '111200' },
    { entity: 'Military', entityType: 'Miniaturized Satellite', code: '111300' },
    { entity: 'Military', entityType: 'Navigational Satellite', code: '111400' },
    { entity: 'Military', entityType: 'Reconnaissance Satellite', code: '111500' },
    { entity: 'Military', entityType: 'Space Station', code: '111600' },
    { entity: 'Military', entityType: 'Tethered Satellite', code: '111700' },
    { entity: 'Military', entityType: 'Weather Satellite', code: '111800' },
    { entity: 'Military', entityType: 'Space Launched Vehicle (SLV)', code: '111900' },
    { entity: 'Civilian', code: '120000' },
    { entity: 'Civilian', entityType: 'Orbiter Shuttle', code: '120100' },
    { entity: 'Civilian', entityType: 'Capsule', code: '120200' },
    { entity: 'Civilian', entityType: 'Satellite', code: '120300' },
    { entity: 'Civilian', entityType: 'Astronomical Satellite', code: '120400' },
    { entity: 'Civilian', entityType: 'Biosatellite', code: '120500' },
    { entity: 'Civilian', entityType: 'Communications Satellite', code: '120600' },
    { entity: 'Civilian', entityType: 'Earth Observation Satellite', code: '120700' },
    { entity: 'Civilian', entityType: 'Miniaturized Satellite', code: '120800' },
    { entity: 'Civilian', entityType: 'Navigational Satellite', code: '120900' },
    { entity: 'Civilian', entityType: 'Space Station', code: '121000' },
    { entity: 'Civilian', entityType: 'Tethered Satellite', code: '121100' },
    { entity: 'Civilian', entityType: 'Weather Satellite', code: '121200' },
    { entity: 'Manual Track', code: '130000' },
] as const;
export type spaceIcon = (typeof spaceIconValues)[number];

export const spaceModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Low Earth Orbit (LEO)', category: 'Orbit', code: '01' },
    { modifier: 'Medium Earth Orbit  (MEO)', category: 'Orbit', code: '02' },
    { modifier: 'High Earth Orbit  (HEO)', category: 'Orbit', code: '03' },
    { modifier: 'Geosynchronous Orbit (GSO)', category: 'Orbit', code: '04' },
    { modifier: 'Geostationary Orbit (GO)', category: 'Orbit', code: '05' },
    { modifier: 'Molniya Orbit (MO)', category: 'Orbit', code: '06' },
    { modifier: 'Cyberspace', category: 'Capability', code: '07' },
] as const;
export type SpaceModifier1 = (typeof spaceModifier1Values)[number];

export const spaceModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Optical', category: 'Sensor', code: '01' },
    { modifier: 'Infrared', category: 'Sensor', code: '02' },
    { modifier: 'Radar', category: 'Sensor', code: '03' },
    { modifier: 'Signals Intelligence (SIGINT)', category: 'Sensor', code: '04' },
    { modifier: 'Cyberspace', category: 'Capability', code: '05' },
] as const;
export type SpaceModifier2 = (typeof spaceModifier2Values)[number];

export const spaceMissileIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Missile', code: '110000' },
] as const;
export type SpacemissileIcon = (typeof spaceMissileIconValues)[number];

export const spacemissileModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Short Range', category: 'Missile Range', code: '01' },
    { modifier: 'Medium Range', category: 'Missile Range', code: '02' },
    { modifier: 'Intermediate Range', category: 'Missile Range', code: '03' },
    { modifier: 'Long Range', category: 'Missile Range', code: '04' },
    { modifier: 'Intercontinental', category: 'Missile Range', code: '05' },
    { modifier: 'Arrow', category: 'Missile Type-BMD', code: '06' },
    { modifier: 'Ground-Based Interceptor (GBI)', category: 'Missile Type-BMD', code: '07' },
    { modifier: 'Patriot', category: 'Missile Type-BMD', code: '08' },
    {
        modifier: 'Standard Missile Terminal Phase (SM-T)',
        category: 'Missile Type-BMD',
        code: '09',
    },
    { modifier: 'Standard Missile – 3 (SM-3)', category: 'Missile Type-BMD', code: '10' },
    {
        modifier: 'Terminal High Altitude Area Defense (THAAD)',
        category: 'Missile Type-BMD',
        code: '11',
    },
    { modifier: 'Space', category: 'Launch Origin', code: '12' },
    { modifier: 'Close Range (CRBM)', category: 'Missile Range', code: '13' },
    { modifier: 'Debris', category: 'Missile Type-BMD', code: '14' },
    { modifier: 'Unknown', category: 'Missile Type-BMD', code: '15' },
] as const;
export type SpacemissileModifier2 = (typeof spacemissileModifier2Values)[number];

export const landUnitIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Command and Control', code: '110000' },
    { entity: 'Command and Control', entityType: 'Broadcast Transmitter Antennae', code: '110100' },
    { entity: 'Command and Control', entityType: 'Civil Affairs', code: '110200' },
    { entity: 'Command and Control', entityType: 'Civil–Military Cooperation', code: '110300' },
    { entity: 'Command and Control', entityType: 'Information Operations', code: '110400' },
    { entity: 'Command and Control', entityType: 'Liaison', code: '110500' },
    {
        entity: 'Command and Control',
        entityType: 'Military Information Support Operations (MISO)',
        code: '110600',
    },
    {
        entity: 'Command and Control',
        entityType: 'Military Information Support Operations (MISO)',
        entitySubtype: 'Broadcast Transmitter Antennae',
        code: '110601',
    },
    { entity: 'Command and Control', entityType: 'Radio', code: '110700' },
    { entity: 'Command and Control', entityType: 'Radio Relay', code: '110800' },
    { entity: 'Command and Control', entityType: 'Radio Teletype Center', code: '110900' },
    { entity: 'Command and Control', entityType: 'Signal', code: '111000' },
    { entity: 'Command and Control', entityType: 'Signal', entitySubtype: 'Radio', code: '111001' },
    {
        entity: 'Command and Control',
        entityType: 'Signal',
        entitySubtype: 'Radio Relay',
        code: '111002',
    },
    {
        entity: 'Command and Control',
        entityType: 'Signal',
        entitySubtype: 'Teletype',
        code: '111003',
    },
    {
        entity: 'Command and Control',
        entityType: 'Signal',
        entitySubtype: 'Tactical Satellite',
        code: '111004',
    },
    {
        entity: 'Command and Control',
        entityType: 'Signal',
        entitySubtype: 'Video Imagery (Combat Camera)',
        code: '111005',
    },
    { entity: 'Command and Control', entityType: 'Tactical Satellite', code: '111100' },
    { entity: 'Command and Control', entityType: 'Video Imagery (Combat Camera)', code: '111200' },
    { entity: 'Command and Control', entityType: 'Space', code: '111300' },
    { entity: 'Command and Control', entityType: 'Special Troops', code: '111400' },
    { entity: 'Movement and Maneuver', code: '120000' },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Air Assault with Organic Lift',
        code: '120100',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Air Traffic Services/Airfield Operations',
        code: '120200',
    },
    { entity: 'Movement and Maneuver', entityType: 'Antitank/Antiarmor', code: '120400' },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Antitank/Antiarmor',
        entitySubtype: 'Armored',
        code: '120401',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Antitank/Antiarmor',
        entitySubtype: 'Motorized',
        code: '120402',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Armor/Armored/Mechanized/Self–Propelled/ Tracked',
        code: '120500',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Armor/Armored/Mechanized/Self–Propelled/ Tracked',
        entitySubtype: 'Reconnaissance/Cavalry/Scout',
        code: '120501',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Armor/Armored/Mechanized/Self–Propelled/ Tracked',
        entitySubtype: 'Amphibious',
        code: '120502',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Army Aviation/Aviation Rotary Wing',
        code: '120600',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Army Aviation/Aviation Rotary Wing',
        entitySubtype: 'Reconnaissance',
        code: '120601',
    },
    { entity: 'Movement and Maneuver', entityType: 'Aviation Composite', code: '120700' },
    { entity: 'Movement and Maneuver', entityType: 'Aviation Fixed Wing', code: '120800' },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Aviation Fixed Wing',
        entitySubtype: 'Reconnaissance',
        code: '120801',
    },
    { entity: 'Movement and Maneuver', entityType: 'Combat', code: '120900' },
    { entity: 'Movement and Maneuver', entityType: 'Combined Arms', code: '121000' },
    { entity: 'Movement and Maneuver', entityType: 'Infantry', code: '121100' },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Infantry',
        entitySubtype: 'Amphibious',
        code: '121101',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Infantry',
        entitySubtype: 'Armored/Mechanized/Tracked',
        code: '121102',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Infantry',
        entitySubtype: 'Main Gun System',
        code: '121103',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Infantry',
        entitySubtype: 'Motorized',
        code: '121104',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Infantry',
        entitySubtype: 'Infantry Fighting Vehicle',
        code: '121105',
    },
    { entity: 'Movement and Maneuver', entityType: 'Observer', code: '121200' },
    { entity: 'Movement and Maneuver', entityType: 'Reconnaissance/Cavalry/Scout', code: '121300' },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Reconnaissance/Cavalry/Scout',
        entitySubtype: 'Reconnaissance and Surveillance',
        code: '121301',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Reconnaissance/Cavalry/Scout',
        entitySubtype: 'Marine',
        code: '121302',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Reconnaissance/Cavalry/Scout',
        entitySubtype: 'Motorized',
        code: '121303',
    },
    { entity: 'Movement and Maneuver', entityType: 'Sea Air Land (SEAL)', code: '121400' },
    { entity: 'Movement and Maneuver', entityType: 'Sniper', code: '121500' },
    { entity: 'Movement and Maneuver', entityType: 'Surveillance', code: '121600' },
    { entity: 'Movement and Maneuver', entityType: 'Special Forces', code: '121700' },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Special Operations Forces (SOF)',
        code: '121800',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Special Operations Forces (SOF)',
        entitySubtype: 'Fixed Wing MISO',
        code: '121801',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Special Operations Forces (SOF)',
        entitySubtype: 'Ground',
        code: '121802',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Special Operations Forces (SOF)',
        entitySubtype: 'Special Boat',
        code: '121803',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Special Operations Forces (SOF)',
        entitySubtype: 'Special SSNR',
        code: '121804',
    },
    {
        entity: 'Movement and Maneuver',
        entityType: 'Special Operations Forces (SOF)',
        entitySubtype: 'Underwater Demolition Team',
        code: '121805',
    },
    { entity: 'Movement and Maneuver', entityType: 'Unmanned Aerial Systems', code: '121900' },
    { entity: 'Movement and Maneuver', entityType: 'Ranger', code: '122000' },
    { entity: 'Fires', code: '130000' },
    { entity: 'Fires', entityType: 'Air Defense', code: '130100' },
    {
        entity: 'Fires',
        entityType: 'Air Defense',
        entitySubtype: 'Main Gun System',
        code: '130101',
    },
    { entity: 'Fires', entityType: 'Air Defense', entitySubtype: 'Missile', code: '130102' },
    {
        entity: 'Fires',
        entityType: 'Air Defense',
        entitySubtype: 'Air and Missile Defense',
        code: '130103',
    },
    { entity: 'Fires', entityType: 'Air/Land Naval Gunfire Liaison', code: '130200' },
    { entity: 'Fires', entityType: 'Field Artillery', code: '130300' },
    {
        entity: 'Fires',
        entityType: 'Field Artillery',
        entitySubtype: 'Self–propelled',
        code: '130301',
    },
    {
        entity: 'Fires',
        entityType: 'Field Artillery',
        entitySubtype: 'Target Acquisition',
        code: '130302',
    },
    { entity: 'Fires', entityType: 'Field Artillery Observer', code: '130400' },
    { entity: 'Fires', entityType: 'Joint Fire Support', code: '130500' },
    { entity: 'Fires', entityType: 'Meteorological', code: '130600' },
    { entity: 'Fires', entityType: 'Missile', code: '130700' },
    { entity: 'Fires', entityType: 'Mortar', code: '130800' },
    {
        entity: 'Fires',
        entityType: 'Mortar',
        entitySubtype: 'Armored/Mechanized/Tracked',
        code: '130801',
    },
    {
        entity: 'Fires',
        entityType: 'Mortar',
        entitySubtype: 'Self-Propelled Wheeled',
        code: '130802',
    },
    { entity: 'Fires', entityType: 'Mortar', entitySubtype: 'Towed', code: '130803' },
    { entity: 'Fires', entityType: 'Survey', code: '130900' },
    { entity: 'Protection', code: '140000' },
    {
        entity: 'Protection',
        entityType: 'Chemical Biological Radiological Nuclear Defense',
        code: '140100',
    },
    {
        entity: 'Protection',
        entityType: 'Chemical Biological Radiological Nuclear Defense',
        entitySubtype: 'Mechanized',
        code: '140101',
    },
    {
        entity: 'Protection',
        entityType: 'Chemical Biological Radiological Nuclear Defense',
        entitySubtype: 'Motorized',
        code: '140102',
    },
    {
        entity: 'Protection',
        entityType: 'Chemical Biological Radiological Nuclear Defense',
        entitySubtype: 'Reconnaissance',
        code: '140103',
    },
    {
        entity: 'Protection',
        entityType: 'Chemical Biological Radiological Nuclear Defense',
        entitySubtype: 'Reconnaissance Armored',
        code: '140104',
    },
    {
        entity: 'Protection',
        entityType: 'Chemical Biological Radiological Nuclear Defense',
        entitySubtype: 'Reconnaissance Equipped',
        code: '140105',
    },
    {
        entity: 'Protection',
        entityType: 'Chemical Biological Radiological Nuclear Defense',
        entitySubtype: 'Chemical, Biological, Radiological, Nuclear, and High-Yield Explosives',
        code: '140106',
    },
    { entity: 'Protection', entityType: 'Combat Support (Maneuver Enhancement)', code: '140200' },
    { entity: 'Protection', entityType: 'Criminal Investigation Division', code: '140300' },
    { entity: 'Protection', entityType: 'Diving', code: '140400' },
    { entity: 'Protection', entityType: 'Dog', code: '140500' },
    { entity: 'Protection', entityType: 'Drilling', code: '140600' },
    { entity: 'Protection', entityType: 'Engineer', code: '140700' },
    { entity: 'Protection', entityType: 'Engineer', entitySubtype: 'Mechanized', code: '140701' },
    { entity: 'Protection', entityType: 'Engineer', entitySubtype: 'Motorized', code: '140702' },
    {
        entity: 'Protection',
        entityType: 'Engineer',
        entitySubtype: 'Reconnaissance',
        code: '140703',
    },
    { entity: 'Protection', entityType: 'Explosive Ordnance Disposal (EOD)', code: '140800' },
    { entity: 'Protection', entityType: 'Field Camp Construction', code: '140900' },
    { entity: 'Protection', entityType: 'Fire Fighting/Fire Protection', code: '141000' },
    {
        entity: 'Protection',
        entityType: 'Geospatial Support/Geospatial Information Support',
        code: '141100',
    },
    { entity: 'Protection', entityType: 'Military Police', code: '141200' },
    { entity: 'Protection', entityType: 'Mine', code: '141300' },
    { entity: 'Protection', entityType: 'Mine Clearing', code: '141400' },
    { entity: 'Protection', entityType: 'Mine Launching', code: '141500' },
    { entity: 'Protection', entityType: 'Mine Laying', code: '141600' },
    { entity: 'Protection', entityType: 'Security', code: '141700' },
    { entity: 'Protection', entityType: 'Security', entitySubtype: 'Mechanized', code: '141701' },
    { entity: 'Protection', entityType: 'Security', entitySubtype: 'Motorized', code: '141702' },
    { entity: 'Protection', entityType: 'Search and Rescue', code: '141800' },
    { entity: 'Protection', entityType: 'Security Police (Air)', code: '141900' },
    { entity: 'Protection', entityType: 'Shore Patrol', code: '142000' },
    { entity: 'Protection', entityType: 'Topographic/Geospatial', code: '142100' },
    { entity: 'Protection', entityType: 'Missile Defense', code: '142200' },
    { entity: 'Intelligence', code: '150000' },
    { entity: 'Intelligence', entityType: 'Analysis', code: '150100' },
    { entity: 'Intelligence', entityType: 'Counterintelligence', code: '150200' },
    { entity: 'Intelligence', entityType: 'Direction Finding', code: '150300' },
    { entity: 'Intelligence', entityType: 'Electronic Ranging', code: '150400' },
    { entity: 'Intelligence', entityType: 'Electronic Warfare', code: '150500' },
    {
        entity: 'Intelligence',
        entityType: 'Electronic Warfare',
        entitySubtype: 'Analysis',
        code: '150501',
    },
    {
        entity: 'Intelligence',
        entityType: 'Electronic Warfare',
        entitySubtype: 'Direction Finding',
        code: '150502',
    },
    {
        entity: 'Intelligence',
        entityType: 'Electronic Warfare',
        entitySubtype: 'Intercept',
        code: '150503',
    },
    {
        entity: 'Intelligence',
        entityType: 'Electronic Warfare',
        entitySubtype: 'Jamming',
        code: '150504',
    },
    {
        entity: 'Intelligence',
        entityType: 'Electronic Warfare',
        entitySubtype: 'Search',
        code: '150505',
    },
    { entity: 'Intelligence', entityType: 'Intercept (Search and Recording)', code: '150600' },
    { entity: 'Intelligence', entityType: 'Interrogation', code: '150700' },
    { entity: 'Intelligence', entityType: 'Jamming', code: '150800' },
    { entity: 'Intelligence', entityType: 'Joint Intelligence Center', code: '150900' },
    { entity: 'Intelligence', entityType: 'Military Intelligence', code: '151000' },
    { entity: 'Intelligence', entityType: 'Search', code: '151100' },
    { entity: 'Intelligence', entityType: 'Sensor', code: '151200' },
    { entity: 'Intelligence', entityType: 'Military History', code: '151300' },
    { entity: 'Sustainment', code: '160000' },
    { entity: 'Sustainment', entityType: 'Administrative', code: '160100' },
    { entity: 'Sustainment', entityType: 'All Classes of Supply', code: '160200' },
    {
        entity: 'Sustainment',
        entityType: 'Airport of Debarkation/Airport of Embarkation',
        code: '160300',
    },
    { entity: 'Sustainment', entityType: 'Ammunition', code: '160400' },
    { entity: 'Sustainment', entityType: 'Band', code: '160500' },
    { entity: 'Sustainment', entityType: 'Band', entitySubtype: 'Army Music', code: '160501' },
    { entity: 'Sustainment', entityType: 'Combat Service Support', code: '160600' },
    { entity: 'Sustainment', entityType: 'Finance', code: '160700' },
    { entity: 'Sustainment', entityType: 'Judge Advocate General', code: '160800' },
    { entity: 'Sustainment', entityType: 'Labor', code: '160900' },
    { entity: 'Sustainment', entityType: 'Laundry/Bath', code: '161000' },
    { entity: 'Sustainment', entityType: 'Maintenance', code: '161100' },
    { entity: 'Sustainment', entityType: 'Material', code: '161200' },
    { entity: 'Sustainment', entityType: 'Medical', code: '161300' },
    { entity: 'Sustainment', entityType: 'Medical Treatment Facility', code: '161400' },
    { entity: 'Sustainment', entityType: 'Morale, Welfare and Recreation', code: '161500' },
    { entity: 'Sustainment', entityType: 'Mortuary Affairs', code: '161600' },
    { entity: 'Sustainment', entityType: 'Multiple Classes of Supply', code: '161700' },
    { entity: 'Sustainment', entityType: 'NATO Supply Class I', code: '161800' },
    { entity: 'Sustainment', entityType: 'NATO Supply Class III', code: '162000' },
    { entity: 'Sustainment', entityType: 'NATO Supply Class IV', code: '162100' },
    { entity: 'Sustainment', entityType: 'Ordnance', code: '162300' },
    { entity: 'Sustainment', entityType: 'Personnel Services', code: '162400' },
    { entity: 'Sustainment', entityType: 'Petroleum, Oil and Lubricants', code: '162500' },
    { entity: 'Sustainment', entityType: 'Public Affairs/Public Information', code: '162800' },
    { entity: 'Sustainment', entityType: 'Quartermaster', code: '162900' },
    { entity: 'Sustainment', entityType: 'Railhead', code: '163000' },
    { entity: 'Sustainment', entityType: 'Religious Support', code: '163100' },
    { entity: 'Sustainment', entityType: 'Replacement Holding Unit', code: '163200' },
    {
        entity: 'Sustainment',
        entityType: 'Sea Port of Debarkation/Sea Port of Embarkation',
        code: '163300',
    },
    { entity: 'Sustainment', entityType: 'Joint Information Bureau (JIB)', code: '163500' },
    { entity: 'Sustainment', entityType: 'Transportation', code: '163600' },
    {
        entity: 'Sustainment',
        entityType: 'Transportation',
        entitySubtype: 'Floating Craft',
        code: '163601',
    },
    { entity: 'Sustainment', entityType: 'US Supply Class I', code: '163700' },
    { entity: 'Sustainment', entityType: 'US Supply Class IV', code: '164000' },
    { entity: 'Sustainment', entityType: 'US Supply Class VI', code: '164200' },
    { entity: 'Sustainment', entityType: 'US Supply Class VII', code: '164300' },
    { entity: 'Sustainment', entityType: 'US Supply Class VIII', code: '164400' },
    { entity: 'Sustainment', entityType: 'US Supply Class IX', code: '164500' },
    { entity: 'Sustainment', entityType: 'US Supply Class X', code: '164600' },
    { entity: 'Sustainment', entityType: 'Water Purification', code: '164800' },
    { entity: 'Sustainment', entityType: 'Broadcast', code: '164900' },
    { entity: 'Sustainment', entityType: 'NATO Supply Class - ALL', code: '165000' },
    { entity: 'Sustainment', entityType: 'Interpreter/Translator', code: '165100' },
    { entity: 'Sustainment', entityType: 'Support', code: '165200' },
    { entity: 'Sustainment', entityType: 'Army Field Support', code: '165300' },
    { entity: 'Sustainment', entityType: 'Contracting Services', code: '165400' },
    { entity: 'Sustainment', entityType: 'Parachute Rigger', code: '165500' },
    { entity: 'Naval', code: '170000' },
    { entity: 'Naval', entityType: 'Naval', code: '170100' },
    { entity: 'Named Headquarters', code: '180000' },
    {
        entity: 'Named Headquarters',
        entityType: 'Allied Command Europe Rapid Reaction Corps (ARRC)',
        code: '180100',
    },
    { entity: 'Named Headquarters', entityType: 'Allied Command Operations', code: '180200' },
    {
        entity: 'Named Headquarters',
        entityType: 'International Security Assistance Force (ISAF)',
        code: '180300',
    },
    { entity: 'Named Headquarters', entityType: 'Multinational (MN)', code: '180400' },
    { entity: 'Emergency Operation', code: '190000' },
    { entity: 'Law Enforcement', code: '200000' },
    {
        entity: 'Law Enforcement',
        entityType:
            'Bureau of Alcohol, Tobacco, Firearms and Explosives (ATF) (Department of Justice)',
        code: '200100',
    },
    { entity: 'Law Enforcement', entityType: 'Border Patrol', code: '200200' },
    { entity: 'Law Enforcement', entityType: 'Customs Service', code: '200300' },
    {
        entity: 'Law Enforcement',
        entityType: 'Drug Enforcement Administration (DEA)',
        code: '200400',
    },
    { entity: 'Law Enforcement', entityType: 'Department of Justice (DOJ)', code: '200500' },
    {
        entity: 'Law Enforcement',
        entityType: 'Federal Bureau of Investigation (FBI)',
        code: '200600',
    },
    { entity: 'Law Enforcement', entityType: 'Police', code: '200700' },
    { entity: 'Law Enforcement', entityType: 'Prison', code: '200800' },
    {
        entity: 'Law Enforcement',
        entityType: 'United States Secret Service (Treas) (USSS)',
        code: '200900',
    },
    {
        entity: 'Law Enforcement',
        entityType: 'Transportation Security Administration (TSA)',
        code: '201000',
    },
    { entity: 'Law Enforcement', entityType: 'Coast Guard', code: '201100' },
    { entity: 'Law Enforcement', entityType: 'US Marshals Service', code: '201200' },
    { entity: 'Law Enforcement', entityType: 'Internal Security Force', code: '201300' },
] as const;
export type LandunitIcon = (typeof landUnitIconValues)[number];

export const landunitModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Tactical Satellite Communications', category: 'Capability', code: '01' },
    { modifier: 'Area', category: 'Capability', code: '02' },
    { modifier: 'Attack', category: 'Capability', code: '03' },
    { modifier: 'Biological', category: 'Capability', code: '04' },
    { modifier: 'Border', category: 'Capability', code: '05' },
    { modifier: 'Bridging', category: 'Capability', code: '06' },
    { modifier: 'Chemical', category: 'Capability', code: '07' },
    { modifier: 'Close Protection', category: 'Capability', code: '08' },
    { modifier: 'Combat', category: 'Capability', code: '09' },
    { modifier: 'Command and Control', category: 'Capability', code: '10' },
    { modifier: 'Communications Contingency Package', category: 'Capability', code: '11' },
    { modifier: 'Construction', category: 'Capability', code: '12' },
    { modifier: 'Cross Cultural Communication', category: 'Capability', code: '13' },
    { modifier: 'Crowd and Riot Control', category: 'Capability', code: '14' },
    { modifier: 'Decontamination', category: 'Capability', code: '15' },
    { modifier: 'Detention', category: 'Capability', code: '16' },
    { modifier: 'Direct Communications', category: 'Capability', code: '17' },
    { modifier: 'Diving', category: 'Capability', code: '18' },
    { modifier: 'Division', category: 'Capability', code: '19' },
    { modifier: 'Dog', category: 'Capability', code: '20' },
    { modifier: 'Drilling', category: 'Capability', code: '21' },
    { modifier: 'Electro–Optical', category: 'Capability', code: '22' },
    { modifier: 'Enhanced', category: 'Capability', code: '23' },
    { modifier: 'Explosive Ordnance Disposal (EOD)', category: 'Capability', code: '24' },
    { modifier: 'Fire Direction Center', category: 'Capability', code: '25' },
    { modifier: 'Force', category: 'Capability', code: '26' },
    { modifier: 'Forward', category: 'Capability', code: '27' },
    { modifier: 'Ground Station Module', category: 'Capability', code: '28' },
    { modifier: 'Landing Support', category: 'Capability', code: '29' },
    { modifier: 'Maintenance', category: 'Capability', code: '31' },
    { modifier: 'Meteorological', category: 'Capability', code: '32' },
    { modifier: 'Mine Countermeasure', category: 'Capability', code: '33' },
    { modifier: 'Missile', category: 'Capability', code: '34' },
    { modifier: 'Mobile Advisor and Support', category: 'Capability', code: '35' },
    { modifier: 'Mobile Subscriber Asset', category: 'Capability', code: '36' },
    { modifier: 'Mobility Support', category: 'Capability', code: '37' },
    { modifier: 'Multinational', category: 'Capability', code: '39' },
    { modifier: 'Multinational Specialized Unit', category: 'Capability', code: '40' },
    { modifier: 'Multiple Rocket Launcher', category: 'Capability', code: '41' },
    { modifier: 'NATO Medical Role 1', category: 'Capability', code: '42' },
    { modifier: 'NATO Medical Role 2', category: 'Capability', code: '43' },
    { modifier: 'NATO Medical Role 3', category: 'Capability', code: '44' },
    { modifier: 'NATO Medical Role 4', category: 'Capability', code: '45' },
    { modifier: 'Naval', category: 'Capability', code: '46' },
    { modifier: 'Unmanned Aerial Systems (UAS)', category: 'Capability', code: '47' },
    { modifier: 'Nuclear', category: 'Capability', code: '48' },
    { modifier: 'Operations', category: 'Capability', code: '49' },
    { modifier: 'Radar', category: 'Capability', code: '50' },
    {
        modifier: 'Radio Frequency Identification (RFID) Interrogator / Sensor',
        category: 'Capability',
        code: '51',
    },
    { modifier: 'Radiological', category: 'Capability', code: '52' },
    { modifier: 'Search and Rescue', category: 'Capability', code: '53' },
    { modifier: 'Security', category: 'Capability', code: '54' },
    { modifier: 'Sensor', category: 'Capability', code: '55' },
    { modifier: 'Weapons', category: 'Capability', code: '56' },
    { modifier: 'Signals Intelligence', category: 'Capability', code: '57' },
    { modifier: 'Armored', category: 'Capability', code: '58' },
    { modifier: 'Single Rocket Launcher', category: 'Capability', code: '59' },
    { modifier: 'Smoke', category: 'Capability', code: '60' },
    { modifier: 'Sniper', category: 'Capability', code: '61' },
    { modifier: 'Sound Ranging', category: 'Capability', code: '62' },
    { modifier: 'Special Operations Forces (SOF)', category: 'Capability', code: '63' },
    { modifier: 'Special Weapons and Tactics', category: 'Capability', code: '64' },
    { modifier: 'Survey', category: 'Capability', code: '65' },
    { modifier: 'Tactical Exploitation', category: 'Capability', code: '66' },
    { modifier: 'Target Acquisition', category: 'Capability', code: '67' },
    { modifier: 'Topographic/Geospatial', category: 'Capability', code: '68' },
    { modifier: 'Utility', category: 'Capability', code: '69' },
    { modifier: 'Video Imagery (Combat Camera)', category: 'Capability', code: '70' },
    { modifier: 'Mobility Assault', category: 'Capability', code: '71' },
    { modifier: 'Amphibious Warfare Ship', category: 'Capability', code: '72' },
    { modifier: 'Load Handling System', category: 'Capability', code: '73' },
    { modifier: 'Palletized Load System', category: 'Capability', code: '74' },
    { modifier: 'Medevac', category: 'Capability', code: '75' },
    { modifier: 'Cyberspace', category: 'Capability', code: '76' },
    { modifier: 'Support', category: 'Capability', code: '77' },
    { modifier: 'Aviation', category: 'Capability', code: '78' },
    { modifier: 'Route, Reconnaissance, and Clearance', category: 'Capability', code: '79' },
    { modifier: 'Tilt-Rotor', category: 'Capability', code: '80' },
    { modifier: 'Command Post Node', category: 'Capability', code: '81' },
    { modifier: 'Joint Network Node', category: 'Capability', code: '82' },
    { modifier: 'Retransmission Site', category: 'Capability', code: '83' },
    { modifier: 'Assault', category: 'Capability', code: '84' },
    { modifier: 'Weapons', category: 'Capability', code: '85' },
    { modifier: 'Criminal Investigation Division', category: 'Capability', code: '86' },
    { modifier: 'Digital', category: 'Capability', code: '87' },
    { modifier: 'Network or Network Operations', category: 'Capability', code: '88' },
    {
        modifier: 'Airfield, Aerial Port of Debarkation, or Aerial Port of Embarkation',
        category: 'Capability',
        code: '89',
    },
    { modifier: 'Pipeline', category: 'Capability', code: '90' },
    { modifier: 'Postal', category: 'Capability', code: '91' },
    { modifier: 'Water', category: 'Capability', code: '92' },
    { modifier: 'Independent Command', category: 'Capability', code: '93' },
    { modifier: 'Theater', category: 'Capability', code: '94' },
    { modifier: 'Army or Theater Army', category: 'Capability', code: '95' },
    { modifier: 'Corps', category: 'Capability', code: '96' },
    { modifier: 'Brigade', category: 'Capability', code: '97' },
    { modifier: 'Headquarters or headquarters staff element', category: 'Capability', code: '98' },
] as const;
export type LandunitModifier1 = (typeof landunitModifier1Values)[number];

export const landunitModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Airborne', category: 'Mobility', code: '01' },
    { modifier: 'Arctic', category: 'Mobility', code: '02' },
    { modifier: 'Battle Damage Repair', category: 'Capability', code: '03' },
    { modifier: 'Bicycle Equipped', category: 'Mobility', code: '04' },
    { modifier: 'Casualty Staging', category: 'Capability', code: '05' },
    { modifier: 'Clearing', category: 'Capability', code: '06' },
    { modifier: 'Close Range', category: 'Capability', code: '07' },
    { modifier: 'Control', category: 'Capability', code: '08' },
    { modifier: 'Decontamination', category: 'Capability', code: '09' },
    { modifier: 'Demolition', category: 'Capability', code: '10' },
    { modifier: 'Dental', category: 'Capability', code: '11' },
    { modifier: 'Digital', category: 'Capability', code: '12' },
    {
        modifier: 'Enhanced Position Location Reporting System (EPLRS)',
        category: 'Capability',
        code: '13',
    },
    { modifier: 'Heavy', category: 'Capability', code: '15' },
    { modifier: 'High Altitude', category: 'Capability', code: '16' },
    { modifier: 'Intermodal', category: 'Capability', code: '17' },
    { modifier: 'Intensive Care', category: 'Capability', code: '18' },
    { modifier: 'Light', category: 'Capability', code: '19' },
    { modifier: 'Laboratory', category: 'Capability', code: '20' },
    { modifier: 'Launcher', category: 'Capability', code: '21' },
    { modifier: 'Long Range', category: 'Capability', code: '22' },
    { modifier: 'Low Altitude', category: 'Capability', code: '23' },
    { modifier: 'Medium', category: 'Capability', code: '24' },
    { modifier: 'Medium Altitude', category: 'Capability', code: '25' },
    { modifier: 'Medium Range', category: 'Capability', code: '26' },
    { modifier: 'Mountain', category: 'Capability', code: '27' },
    { modifier: 'High to Medium Altitude', category: 'Capability', code: '28' },
    { modifier: 'Multi–Channel', category: 'Capability', code: '29' },
    { modifier: 'Optical (Flash)', category: 'Capability', code: '30' },
    { modifier: 'Pack Animal', category: 'Capability', code: '31' },
    { modifier: 'Patient Evacuation Coordination', category: 'Capability', code: '32' },
    { modifier: 'Preventive Maintenance', category: 'Capability', code: '33' },
    { modifier: 'Psychological', category: 'Capability', code: '34' },
    { modifier: 'Radio Relay Line of Sight', category: 'Capability', code: '35' },
    { modifier: 'Railroad', category: 'Mobility', code: '36' },
    { modifier: 'Recovery (Unmanned Systems)', category: 'Capability', code: '37' },
    { modifier: 'Recovery (Maintenance)', category: 'Capability', code: '38' },
    { modifier: 'Rescue Coordination Center', category: 'Capability', code: '39' },
    { modifier: 'Riverine', category: 'Mobility', code: '40' },
    { modifier: 'Single Channel', category: 'Capability', code: '41' },
    { modifier: 'Ski', category: 'Capability', code: '42' },
    { modifier: 'Short Range', category: 'Capability', code: '43' },
    { modifier: 'Strategic', category: 'Capability', code: '44' },
    { modifier: 'Support', category: 'Capability', code: '45' },
    { modifier: 'Tactical', category: 'Capability', code: '46' },
    { modifier: 'Towed', category: 'Mobility', code: '47' },
    { modifier: 'Troop', category: 'Capability', code: '48' },
    {
        modifier: 'Vertical or Short Take–Off and Landing (VTOL/VSTOL)',
        category: 'Mobility',
        code: '49',
    },
    { modifier: 'Veterinary', category: 'Capability', code: '50' },
    { modifier: 'Wheeled', category: 'Mobility', code: '51' },
    { modifier: 'High to Low Altitude', category: 'Capability', code: '52' },
    { modifier: 'Medium to Low Altitude', category: 'Capability', code: '53' },
    { modifier: 'Attack', category: 'Capability', code: '54' },
    { modifier: 'Refuel', category: 'Capability', code: '55' },
    { modifier: 'Utility', category: 'Capability', code: '56' },
    { modifier: 'Combat Search and Rescue', category: 'Capability', code: '57' },
    { modifier: 'Guerilla', category: 'Capability', code: '58' },
    { modifier: 'Air Assault', category: 'Mobility', code: '59' },
    { modifier: 'Amphibious', category: 'Mobility', code: '60' },
    { modifier: 'Very Heavy', category: 'Capability', code: '61' },
    { modifier: 'Supply', category: 'Capability', code: '62' },
    { modifier: 'Cyberspace', category: 'Capability', code: '63' },
    { modifier: 'Navy Barge, Self-Propelled', category: 'Mobility', code: '64' },
    { modifier: 'Navy Barge, Not Self-Propelled', category: 'Mobility', code: '65' },
    { modifier: 'Launch', category: 'Mobility', code: '66' },
    { modifier: 'Landing Craft', category: 'Mobility', code: '67' },
    { modifier: 'Landing Ship', category: 'Mobility', code: '68' },
    { modifier: 'Service Craft/Yard', category: 'Mobility', code: '69' },
    { modifier: 'Tug Harbor', category: 'Mobility', code: '70' },
    { modifier: 'Ocean Going Tug Boat', category: 'Mobility', code: '71' },
    { modifier: 'Surface Deployment and Distribution Command', category: 'Capability', code: '72' },
    { modifier: 'Noncombatant Generic Vessel', category: 'Mobility', code: '73' },
    { modifier: 'Composite', category: 'Capability', code: '74' },
    { modifier: 'Shelter', category: 'Capability', code: '75' },
    { modifier: 'Light and Medium', category: 'Capability', code: '76' },
    { modifier: 'Self-propelled', category: 'Capability', code: '77' },
] as const;
export type LandunitModifier2 = (typeof landunitModifier2Values)[number];

export const landCivilianIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Civilian', code: '110000' },
    { entity: 'Civilian', entityType: 'Environmental Protection', code: '110100' },
    { entity: 'Civilian', entityType: 'Governmental Organization', code: '110200' },
    { entity: 'Civilian', entityType: 'Individual', code: '110300' },
    { entity: 'Civilian', entityType: 'Organization or Group', code: '110400' },
    { entity: 'Civilian', entityType: 'Killing Victim', code: '110500' },
    { entity: 'Civilian', entityType: 'Killing Victims', code: '110600' },
    { entity: 'Civilian', entityType: 'Victim of an Attempted Crime', code: '110700' },
    { entity: 'Civilian', entityType: 'Spy', code: '110800' },
    { entity: 'Civilian', entityType: 'Composite Loss', code: '110900' },
    { entity: 'Civilian', entityType: 'Emergency Medical Operation', code: '111000' },
] as const;
export type LandcivilianIcon = (typeof landCivilianIconValues)[number];

export const landcivilianModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Assassination', category: 'Crime', code: '01' },
    { modifier: 'Execution (Wrongful Killing)', category: 'Crime', code: '02' },
    { modifier: 'Murder Victims', category: 'Crime', code: '03' },
    { modifier: 'Hijacking/Hijacked', category: 'Crime', code: '04' },
    { modifier: 'Kidnapping', category: 'Crime', code: '05' },
    { modifier: 'Piracy', category: 'Crime', code: '06' },
    { modifier: 'Rape', category: 'Crime', code: '07' },
    { modifier: 'Civilian', category: 'Organization', code: '08' },
    {
        modifier: 'Displaced Person(s), Refugee(s) and Evacuee(s)',
        category: 'Organization',
        code: '09',
    },
    { modifier: 'Foreign Fighter(s)', category: 'Organization', code: '10' },
    { modifier: 'Gang Member or Gang', category: 'Organization', code: '11' },
    { modifier: 'Government Organization', category: 'Organization', code: '12' },
    { modifier: 'Leader or Leadership', category: 'Organization', code: '13' },
    {
        modifier: 'Nongovernmental Organization Member or Nongovernmental Organization',
        category: 'Organization',
        code: '14',
    },
    { modifier: 'Unwilling/Coerced Recruit', category: 'Organization', code: '15' },
    { modifier: 'Willing Recruit', category: 'Organization', code: '16' },
    { modifier: 'Religious or Religious Organization', category: 'Organization', code: '17' },
    { modifier: 'Targeted Individual or Organization', category: 'Organization', code: '18' },
    { modifier: 'Terrorist or Terrorist Organization', category: 'Organization', code: '19' },
    { modifier: 'Speaker', category: 'Organization', code: '20' },
    { modifier: 'Accident', category: 'Composite Loss', code: '21' },
    { modifier: 'Combat', category: 'Composite Loss', code: '22' },
    { modifier: 'Other', category: 'Composite Loss', code: '23' },
    { modifier: 'Loot', category: 'Crime', code: '24' },
    { modifier: 'Hijacker', category: 'Crime', code: '25' },
    { modifier: 'Cyberspace', category: 'Capability', code: '26' },
] as const;
export type LandcivilianModifer1 = (typeof landcivilianModifier1Values)[number];

export const landcivilianModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Leader or Leadership', category: 'Organization', code: '01' },
    { modifier: 'Cyberspace', category: 'Capability', code: '02' },
] as const;
export type LandcivilianModifer2 = (typeof landcivilianModifier2Values)[number];

export const landAssetIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Weapon/Weapon System', code: '110000' },
    { entity: 'Weapon/Weapon System', entityType: 'Rifle', code: '110100' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Rifle',
        entitySubtype: 'Single Shot Rifle',
        code: '110101',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Rifle',
        entitySubtype: 'Semiautomatic Rifle',
        code: '110102',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Rifle',
        entitySubtype: 'Automatic Rifle',
        code: '110103',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Machine Gun', code: '110200' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Machine Gun',
        entitySubtype: 'Light',
        code: '110201',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Machine Gun',
        entitySubtype: 'Medium',
        code: '110202',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Machine Gun',
        entitySubtype: 'Heavy',
        code: '110203',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Grenade Launcher', code: '110300' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Grenade Launcher',
        entitySubtype: 'Light',
        code: '110301',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Grenade Launcher',
        entitySubtype: 'Medium',
        code: '110302',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Grenade Launcher',
        entitySubtype: 'Heavy',
        code: '110303',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Flame Thrower', code: '110400' },
    { entity: 'Weapon/Weapon System', entityType: 'Air Defense Gun', code: '110500' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Gun',
        entitySubtype: 'Light',
        code: '110501',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Gun',
        entitySubtype: 'Medium',
        code: '110502',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Gun',
        entitySubtype: 'Heavy',
        code: '110503',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Antitank Gun', code: '110600' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Antitank Gun',
        entitySubtype: 'Light',
        code: '110601',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Antitank Gun',
        entitySubtype: 'Medium',
        code: '110602',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Antitank Gun',
        entitySubtype: 'Heavy',
        code: '110603',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Direct Fire Gun', code: '110700' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Direct Fire Gun',
        entitySubtype: 'Light',
        code: '110701',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Direct Fire Gun',
        entitySubtype: 'Medium',
        code: '110702',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Direct Fire Gun',
        entitySubtype: 'Heavy',
        code: '110703',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Recoilless Gun', code: '110800' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Recoilless Gun',
        entitySubtype: 'Light',
        code: '110801',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Recoilless Gun',
        entitySubtype: 'Medium',
        code: '110802',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Recoilless Gun',
        entitySubtype: 'Heavy',
        code: '110803',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Howitzer', code: '110900' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Howitzer',
        entitySubtype: 'Light',
        code: '110901',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Howitzer',
        entitySubtype: 'Medium',
        code: '110902',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Howitzer',
        entitySubtype: 'Heavy',
        code: '110903',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Missile Launcher', code: '111000' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Missile Launcher',
        entitySubtype: 'Light',
        code: '111001',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Missile Launcher',
        entitySubtype: 'Medium',
        code: '111002',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Missile Launcher',
        entitySubtype: 'Heavy',
        code: '111003',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Air Defense Missile Launcher', code: '111100' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Missile Launcher',
        entitySubtype: 'Light',
        code: '111101',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Missile Launcher',
        entitySubtype: 'Light, Light Transporter-Launcher and Radar (TLAR)',
        code: '111102',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Missile Launcher',
        entitySubtype: 'Light, Light Tactical Landing Approach Radar (TELAR)',
        code: '111103',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Missile Launcher',
        entitySubtype: 'Medium',
        code: '111104',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Missile Launcher',
        entitySubtype: 'Medium, TLAR',
        code: '111105',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Missile Launcher',
        entitySubtype: 'Medium, TELAR',
        code: '111106',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Missile Launcher',
        entitySubtype: 'Heavy',
        code: '111107',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Missile Launcher',
        entitySubtype: 'Heavy, TLAR',
        code: '111108',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Air Defense Missile Launcher',
        entitySubtype: 'Heavy, TELAR',
        code: '111109',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Antitank Missile Launcher', code: '111200' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Antitank Missile Launcher',
        entitySubtype: 'Light',
        code: '111201',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Antitank Missile Launcher',
        entitySubtype: 'Medium',
        code: '111202',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Antitank Missile Launcher',
        entitySubtype: 'Heavy',
        code: '111203',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Surface-to-Surface Missile Launcher',
        code: '111300',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Surface-to-Surface Missile Launcher',
        entitySubtype: 'Light',
        code: '111301',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Surface-to-Surface Missile Launcher',
        entitySubtype: 'Medium',
        code: '111302',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Surface-to-Surface Missile Launcher',
        entitySubtype: 'Heavy',
        code: '111303',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Mortar', code: '111400' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Mortar',
        entitySubtype: 'Light',
        code: '111401',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Mortar',
        entitySubtype: 'Medium',
        code: '111402',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Mortar',
        entitySubtype: 'Heavy',
        code: '111403',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Single Rocket Launcher', code: '111500' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Single Rocket Launcher',
        entitySubtype: 'Light',
        code: '111501',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Single Rocket Launcher',
        entitySubtype: 'Medium',
        code: '111502',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Single Rocket Launcher',
        entitySubtype: 'Heavy',
        code: '111503',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Multiple Rocket Launcher', code: '111600' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Multiple Rocket Launcher',
        entitySubtype: 'Light',
        code: '111601',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Multiple Rocket Launcher',
        entitySubtype: 'Medium',
        code: '111602',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Multiple Rocket Launcher',
        entitySubtype: 'Heavy',
        code: '111603',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Antitank Rocket Launcher', code: ' 111700' },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Antitank Rocket Launcher',
        entitySubtype: 'Light',
        code: '111701',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Antitank Rocket Launcher',
        entitySubtype: 'Medium',
        code: '111702',
    },
    {
        entity: 'Weapon/Weapon System',
        entityType: 'Antitank Rocket Launcher',
        entitySubtype: 'Heavy',
        code: '111703',
    },
    { entity: 'Weapon/Weapon System', entityType: 'Nonlethal Weapon', code: '111800' },
    { entity: 'Weapon/Weapon System', entityType: 'Taser', code: '111900' },
    { entity: 'Weapon/Weapon System', entityType: 'Water Cannon', code: '112000' },
    { entity: 'Vehicle', code: '120000' },
    { entity: 'Vehicle', entityType: 'Armored', code: '120100' },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Armored Fighting Vehicle',
        code: '120101',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Armored Fighting Vehicle Command and Control',
        code: '120102',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Armored Personnel Carrier',
        code: '120103',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Armored Personnel Carrier Ambulance',
        code: '120104',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Armored Protected Vehicle',
        code: '120105',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Armored Protected Vehicle Recovery',
        code: '120106',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Armored Protected Vehicle Medical Evacuation',
        code: '120107',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Armored Personnel Carrier, Recovery',
        code: '120108',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Combat Service Support Vehicle',
        code: '120109',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Light Wheeled Armored Vehicle',
        code: '120110',
    },
    {
        entity: 'Vehicle',
        entityType: 'Armored',
        entitySubtype: 'Light Armor Reconnaissance',
        code: '120111',
    },
    { entity: 'Vehicle', entityType: 'Tank', code: '120200' },
    { entity: 'Vehicle', entityType: 'Tank', entitySubtype: 'Light', code: '120201' },
    { entity: 'Vehicle', entityType: 'Tank', entitySubtype: 'Medium', code: '120202' },
    { entity: 'Vehicle', entityType: 'Tank', entitySubtype: 'Heavy', code: '120203' },
    { entity: 'Vehicle', entityType: 'Tank Recovery Vehicle', code: '120300' },
    {
        entity: 'Vehicle',
        entityType: 'Tank Recovery Vehicle',
        entitySubtype: 'Light',
        code: '120301',
    },
    {
        entity: 'Vehicle',
        entityType: 'Tank Recovery Vehicle',
        entitySubtype: 'Medium',
        code: '120302',
    },
    {
        entity: 'Vehicle',
        entityType: 'Tank Recovery Vehicle',
        entitySubtype: 'Heavy',
        code: '120303',
    },
    { entity: 'Engineer Vehicles and Asset', code: '130000' },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Bridge', code: '130100' },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Bridge Mounted on Utility Vehicle',
        code: '130200',
    },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Fixed Bridge', code: '130300' },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Floating Bridge', code: '130400' },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Folding Girder Bridge', code: '130500' },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Hollow Deck Bridge', code: '130600' },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Drill', code: '130700' },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Drill',
        entitySubtype: 'Drill Mounted on Utility Vehicle',
        code: '130701',
    },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Earthmover', code: '130800' },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Earthmover',
        entitySubtype: 'Multifunctional Earthmover/Digger',
        code: '130801',
    },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Mine Clearing Asset', code: '130900' },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Mine Clearing Asset',
        entitySubtype: 'Trailer Mounted',
        code: '130901',
    },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Mine Clearing Asset',
        entitySubtype: 'Mine Clearing Asset on Tank Chassis',
        code: '130902',
    },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Mine Clearing Asset',
        entitySubtype: 'Assault Breacher Vehicle (ABV) with Combat Dozer Blade',
        code: '130903',
    },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Mine Clearing Asset',
        entitySubtype: 'Medium Capability Asset',
        code: '130904',
    },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Mine Clearing Asset',
        entitySubtype: 'Heavy Capability Asset',
        code: '130905',
    },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Mine Laying Asset', code: '131000' },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Mine Laying Asset',
        entitySubtype: 'Mine Laying Asset on Utility Vehicle',
        code: '131001',
    },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Mine Laying Asset',
        entitySubtype: 'Armored Carrier with Volcano',
        code: '131002',
    },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Mine Laying Asset',
        entitySubtype: 'Truck Mounted with Volcano',
        code: '131003',
    },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Dozer', code: '131100' },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Dozer',
        entitySubtype: 'Dozer, Armored',
        code: '131101',
    },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Armored Assault', code: '131200' },
    {
        entity: 'Engineer Vehicles and Asset',
        entityType: 'Armored Engineer Recon Vehicle (AERV)',
        code: '131300',
    },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Backhoe', code: '131400' },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Construction Vehicle', code: '131500' },
    { entity: 'Engineer Vehicles and Asset', entityType: 'Ferry Transporter', code: '131600' },
    { entity: 'Utility Vehicle', code: '140000' },
    { entity: 'Utility Vehicle', entityType: 'Vehicle', code: '140100' },
    { entity: 'Utility Vehicle', entityType: 'Medical', code: '140200' },
    { entity: 'Utility Vehicle', entityType: 'Medical Evacuation', code: '140300' },
    { entity: 'Utility Vehicle', entityType: 'Mobile Emergency Physician', code: '140400' },
    { entity: 'Utility Vehicle', entityType: 'Bus', code: '140500' },
    { entity: 'Utility Vehicle', entityType: 'Semi–Trailer and Truck', code: '140600' },
    {
        entity: 'Utility Vehicle',
        entityType: 'Semi–Trailer and Truck',
        entitySubtype: 'Light',
        code: '140601',
    },
    {
        entity: 'Utility Vehicle',
        entityType: 'Semi–Trailer and Truck',
        entitySubtype: 'Medium',
        code: '140602',
    },
    {
        entity: 'Utility Vehicle',
        entityType: 'Semi–Trailer and Truck',
        entitySubtype: 'Heavy',
        code: '140603',
    },
    { entity: 'Utility Vehicle', entityType: 'Limited Cross Country Truck', code: '140700' },
    { entity: 'Utility Vehicle', entityType: 'Cross Country Truck', code: '140800' },
    { entity: 'Utility Vehicle', entityType: 'Petroleum, Oil and Lubricant', code: '140900' },
    { entity: 'Utility Vehicle', entityType: 'Water', code: '141000' },
    { entity: 'Utility Vehicle', entityType: 'Amphibious Utility Wheeled Vehicle', code: '141100' },
    { entity: 'Utility Vehicle', entityType: 'Tow Truck', code: '141200' },
    { entity: 'Utility Vehicle', entityType: 'Tow Truck', entitySubtype: 'Light', code: '141201' },
    { entity: 'Utility Vehicle', entityType: 'Tow Truck', entitySubtype: 'Heavy', code: '141202' },
    { entity: 'Train', code: '150000' },
    { entity: 'Train', entityType: 'Locomotive', code: '150100' },
    { entity: 'Train', entityType: 'Railcar', code: '150200' },
    { entity: 'Civilian Vehicle', code: '160000' },
    { entity: 'Civilian Vehicle', entityType: 'Automobile', code: '160100' },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Automobile',
        entitySubtype: 'Compact',
        code: '160101',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Automobile',
        entitySubtype: 'Midsize',
        code: '160102',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Automobile',
        entitySubtype: 'Sedan',
        code: '160103',
    },
    { entity: 'Civilian Vehicle', entityType: 'Open-Bed Truck', code: '160200' },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Open-Bed Truck',
        entitySubtype: 'Pickup',
        code: '160201',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Open-Bed Truck',
        entitySubtype: 'Small',
        code: '160202',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Open-Bed Truck',
        entitySubtype: 'Large',
        code: '160203',
    },
    { entity: 'Civilian Vehicle', entityType: 'Multiple Passenger Vehicle', code: '160300' },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Multiple Passenger Vehicle',
        entitySubtype: 'Van',
        code: '160301',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Multiple Passenger Vehicle',
        entitySubtype: 'Small Bus',
        code: '160302',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Multiple Passenger Vehicle',
        entitySubtype: 'Large Bus',
        code: '160303',
    },
    { entity: 'Civilian Vehicle', entityType: 'Utility Vehicle', code: '160400' },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Utility Vehicle',
        entitySubtype: 'Sport Utility Vehicle (SUV)',
        code: '160401',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Utility Vehicle',
        entitySubtype: 'Small Box Truck',
        code: '160402',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Utility Vehicle',
        entitySubtype: 'Large Box Truck',
        code: '160403',
    },
    { entity: 'Civilian Vehicle', entityType: 'Jeep Type Vehicle', code: '160500' },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Jeep Type Vehicle',
        entitySubtype: 'Small/Light',
        code: '160501',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Jeep Type Vehicle',
        entitySubtype: 'Medium',
        code: '160502',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Jeep Type Vehicle',
        entitySubtype: 'Large/Heavy',
        code: '160503',
    },
    { entity: 'Civilian Vehicle', entityType: 'Tractor Trailer Truck with Box', code: '160600' },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Tractor Trailer Truck with Box',
        entitySubtype: 'Small/Light',
        code: '160601',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Tractor Trailer Truck with Box',
        entitySubtype: 'Medium',
        code: '160602',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Tractor Trailer Truck with Box',
        entitySubtype: 'Large/Heavy',
        code: '160603',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Tractor Trailer Truck with Flatbed Trailer',
        code: '160700',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Tractor Trailer Truck with Flatbed Trailer',
        entitySubtype: 'Small/Light',
        code: '160701',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Tractor Trailer Truck with Flatbed Trailer',
        entitySubtype: 'Medium',
        code: '160702',
    },
    {
        entity: 'Civilian Vehicle',
        entityType: 'Tractor Trailer Truck with Flatbed Trailer',
        entitySubtype: 'Large/Heavy',
        code: '160703',
    },
    { entity: 'Civilian Vehicle', entityType: 'Known Insurgent Vehicle', code: '160800' },
    { entity: 'Civilian Vehicle', entityType: 'Drug Vehicle', code: '160900' },
    { entity: 'Law Enforcement', code: '170000' },
    {
        entity: 'Law Enforcement',
        entityType:
            'Bureau of Alcohol, Tobacco, Firearms and Explosives (ATF) (Department of Justice)',
        code: '170100',
    },
    { entity: 'Law Enforcement', entityType: 'Border Patrol', code: '170200' },
    { entity: 'Law Enforcement', entityType: 'Customs Service', code: '170300' },
    {
        entity: 'Law Enforcement',
        entityType: 'Drug Enforcement Administration (DEA)',
        code: '170400',
    },
    { entity: 'Law Enforcement', entityType: 'Department of Justice (DOJ)', code: '170500' },
    {
        entity: 'Law Enforcement',
        entityType: 'Federal Bureau of Investigation (FBI)',
        code: '170600',
    },
    { entity: 'Law Enforcement', entityType: 'Police', code: '170700' },
    {
        entity: 'Law Enforcement',
        entityType: 'United States Secret Service (Treas) (USSS)',
        code: '170800',
    },
    {
        entity: 'Law Enforcement',
        entityType: 'Transportation Security Administration (TSA)',
        code: '170900',
    },
    { entity: 'Law Enforcement', entityType: 'Coast Guard', code: '171000' },
    { entity: 'Law Enforcement', entityType: 'US Marshals Service', code: '171100' },
    { entity: 'Pack Animals', code: '180000' },
    { entity: 'Missile Support', code: '190000' },
    { entity: 'Missile Support', entityType: 'Transloader', code: '190100' },
    { entity: 'Missile Support', entityType: 'Transporter', code: '190200' },
    { entity: 'Missile Support', entityType: 'Crane/Loading Device', code: '190300' },
    { entity: 'Missile Support', entityType: 'Propellant Transporter', code: '190400' },
    { entity: 'Missile Support', entityType: 'Warhead Transporter', code: '190500' },
    { entity: 'Other Asset', code: '200000' },
    { entity: 'Other Asset', entityType: 'Antennae', code: '200100' },
    { entity: 'Other Asset', entityType: 'Bomb', code: '200200' },
    { entity: 'Other Asset', entityType: 'Booby Trap', code: '200300' },
    { entity: 'Other Asset', entityType: 'CBRN Asset', code: '200400' },
    { entity: 'Other Asset', entityType: 'Computer System', code: '200500' },
    { entity: 'Other Asset', entityType: 'Command Launch Asset (CLE)', code: '200600' },
    { entity: 'Other Asset', entityType: 'Generator Set', code: '200700' },
    {
        entity: 'Other Asset',
        entityType: 'Ground-based Midcourse Defense (GMD) Fire Control (GFC) Center',
        code: '200800',
    },
    {
        entity: 'Other Asset',
        entityType: 'In-Flight Interceptor Communications System (IFICS) Data Terminal (IDT)',
        code: '200900',
    },
    { entity: 'Other Asset', entityType: 'Laser', code: '201000' },
    {
        entity: 'Other Asset',
        entityType: 'Military Information Support Operations (MISO)',
        code: '201100',
    },
    { entity: 'Other Asset', entityType: 'Sustainment Shipments', code: '201200' },
    { entity: 'Other Asset', entityType: 'Tent', code: '201300' },
    { entity: 'Other Asset', entityType: 'Tent', entitySubtype: 'Civilian', code: '201301' },
    { entity: 'Other Asset', entityType: 'Tent', entitySubtype: 'Military', code: '201302' },
    { entity: 'Other Asset', entityType: 'Unit Deployment Shipments', code: '201400' },
    { entity: 'Other Asset', entityType: 'Emergency Medical Operation', code: '201500' },
    {
        entity: 'Other Asset',
        entityType: 'Emergency Medical Operation',
        entitySubtype: 'Medical Evacuation Helicopter',
        code: '201501',
    },
    { entity: 'Land Mines', code: '210000' },
    { entity: 'Land Mines', entityType: 'Land Mine', code: '210100' },
    { entity: 'Land Mines', entityType: 'Antipersonnel Land mine (APL)', code: '210200' },
    { entity: 'Land Mines', entityType: 'Antitank Mine', code: '210300' },
    { entity: 'Land Mines', entityType: 'Improvised Explosives Device (IED)', code: '210400' },
    { entity: 'Land Mines', entityType: 'Less Than Lethal', code: '210500' },
    { entity: 'Sensors', code: '220000' },
    { entity: 'Sensors', entityType: 'Sensor', code: '220100' },
    { entity: 'Sensors', entityType: 'Sensor Emplaced', code: '220200' },
    { entity: 'Sensors', entityType: 'Radar', code: '220300' },
    { entity: 'Emergency Operation', code: '230000' },
    { entity: 'Emergency Operation', entityType: 'Ambulance', code: '230100' },
    { entity: 'Emergency Operation', entityType: 'Fire Fighting/Fire Protection', code: '230200' },
    { entity: 'Manual Track', code: '240000' },
    { entity: 'Rotary Wing', code: '250000' },
] as const;
export type landassetIcon = (typeof landAssetIconValues)[number];

export const landassetModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Biological', category: 'Sensor Type', code: '01' },
    { modifier: 'Chemical', category: 'Sensor Type', code: '02' },
    { modifier: 'Early Warning Radar', category: 'Sensor Type', code: '03' },
    { modifier: 'Intrusion', category: 'Sensor Type', code: '04' },
    { modifier: 'Nuclear', category: 'Sensor Type', code: '05' },
    { modifier: 'Radiological', category: 'Sensor Type', code: '06' },
    { modifier: 'Upgraded Early Warning Radar', category: 'Sensor Type', code: '07' },
    { modifier: 'Hijacking/Hijacked', category: 'Crime', code: '08' },
    { modifier: 'Civilian', category: 'Organization', code: '09' },
    { modifier: 'Tilt-Rotor', category: 'Capability', code: '10' },
    { modifier: 'Multi-purpose Blade', category: 'Capability', code: '12' },
    { modifier: 'Tank-width Mine Plow', category: 'Capability', code: '13' },
    { modifier: 'Bridge', category: 'Capability', code: '14' },
    { modifier: 'Cyberspace', category: 'Capability', code: '15' },
    { modifier: 'Armored', category: 'Capability', code: '16' },
    { modifier: 'Attack', category: 'Capability', code: '17' },
    { modifier: 'Cargo', category: 'Capability', code: '18' },
    { modifier: 'Maintenance', category: 'Capability', code: '19' },
    { modifier: 'MedEvac', category: 'Capability', code: '20' },
    { modifier: 'Petroleum, oil, and lubricants (POL)', category: 'Capability', code: '21' },
    { modifier: 'Utility', category: 'Capability', code: '22' },
    { modifier: 'Water', category: 'Capability', code: '23' },
] as const;
export type LandassetModifier1 = (typeof landassetModifier1Values)[number];

export const landassetModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Cyberspace', category: 'Capability', code: '01' },
    { modifier: 'Light', category: 'Capability', code: '02' },
    { modifier: 'Medium', category: 'Capability', code: '03' },
    { modifier: 'Railway', category: 'Capability', code: '04' },
    { modifier: 'Tracked', category: 'Capability', code: '05' },
    { modifier: 'Tractor Trailer', category: 'Capability', code: '06' },
    { modifier: 'Wheeled LTD', category: 'Capability', code: '07' },
    { modifier: 'Wheeled X', category: 'Capability', code: '08' },
] as const;
export type LandassetModifier2 = (typeof landassetModifier2Values)[number];

export const landInstallationIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Military/Civilian', code: '110000' },
    { entity: 'Military/Civilian', entityType: 'Aircraft Production/Assembly', code: '110100' },
    {
        entity: 'Military/Civilian',
        entityType: 'Ammunition and Explosives Production',
        code: '110200',
    },
    { entity: 'Military/Civilian', entityType: 'Ammunition Cache', code: '110300' },
    { entity: 'Military/Civilian', entityType: 'Armament Production', code: '110400' },
    { entity: 'Military/Civilian', entityType: 'Black List Location', code: '110500' },
    {
        entity: 'Military/Civilian',
        entityType: 'Chemical, Biological, Radiological and Nuclear (CBRN)',
        code: '110600',
    },
    { entity: 'Military/Civilian', entityType: 'Engineering Asset Production', code: '110700' },
    {
        entity: 'Military/Civilian',
        entityType: 'Engineering Asset Production',
        entitySubtype: 'Bridge',
        code: '110701',
    },
    { entity: 'Military/Civilian', entityType: 'Asset Manufacture', code: '110800' },
    { entity: 'Military/Civilian', entityType: 'Government Leadership', code: '110900' },
    { entity: 'Military/Civilian', entityType: 'Gray List Location', code: '111000' },
    { entity: 'Military/Civilian', entityType: 'Mass Grave Site', code: '111100' },
    { entity: 'Military/Civilian', entityType: 'Materiel', code: '111200' },
    { entity: 'Military/Civilian', entityType: 'Mine', code: '111300' },
    {
        entity: 'Military/Civilian',
        entityType: 'Missile and Space System Production',
        code: '111400',
    },
    { entity: 'Military/Civilian', entityType: 'Nuclear (Non CBRN Defense)', code: '111500' },
    { entity: 'Military/Civilian', entityType: 'Printed Media', code: '111600' },
    { entity: 'Military/Civilian', entityType: 'Safe House', code: '111700' },
    { entity: 'Military/Civilian', entityType: 'White List Location', code: '111800' },
    { entity: 'Military/Civilian', entityType: 'Tented Camp', code: '111900' },
    {
        entity: 'Military/Civilian',
        entityType: 'Tented Camp',
        entitySubtype: 'Displaced Persons/ Refugee/Evacuees Camp',
        code: '111901',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Tented Camp',
        entitySubtype: 'Training Camp',
        code: '111902',
    },
    { entity: 'Military/Civilian', entityType: 'Warehouse/Storage Facility', code: '112000' },
    { entity: 'Military/Civilian', entityType: 'Law Enforcement', code: '112100' },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype:
            'Bureau of Alcohol, Tobacco, Firearms and Explosives (ATF) (Department of Justice)',
        code: '112101',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'Border Patrol',
        code: '112102',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'Customs Service',
        code: '112103',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'Drug Enforcement Administration (DEA)',
        code: '112104',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'Department of Justice (DOJ)',
        code: '112105',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'Federal Bureau of Investigation (FBI)',
        code: '112106',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'Police',
        code: '112107',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'Prison',
        code: '112108',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'United States Secret Service (Treas) (USSS)',
        code: '112109',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'Transportation Security Administration (TSA)',
        code: '112110',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'Coast Guard',
        code: '112111',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Law Enforcement',
        entitySubtype: 'US Marshals Service',
        code: '112112',
    },
    { entity: 'Military/Civilian', entityType: 'Emergency Operation', code: '112200' },
    {
        entity: 'Military/Civilian',
        entityType: 'Emergency Operation',
        entitySubtype: 'Fire Station',
        code: '112201',
    },
    {
        entity: 'Military/Civilian',
        entityType: 'Emergency Operation',
        entitySubtype: 'Emergency Medical Operation',
        code: '112202',
    },
    { entity: 'Infrastructure', code: '120000' },
    { entity: 'Infrastructure', entityType: 'Agriculture and Food Infrastructure', code: '120100' },
    {
        entity: 'Infrastructure',
        entityType: 'Agriculture and Food Infrastructure',
        entitySubtype: 'Agriculture Laboratory',
        code: '120101',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Agriculture and Food Infrastructure',
        entitySubtype: 'Animal Feedlot',
        code: '120102',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Agriculture and Food Infrastructure',
        entitySubtype: 'Commercial Food Distribution Center',
        code: '120103',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Agriculture and Food Infrastructure',
        entitySubtype: 'Farm/Ranch',
        code: '120104',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Agriculture and Food Infrastructure',
        entitySubtype: 'Food Distribution',
        code: '120105',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Agriculture and Food Infrastructure',
        entitySubtype: 'Food Production Center',
        code: '120106',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Agriculture and Food Infrastructure',
        entitySubtype: 'Food Retail',
        code: '120107',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Agriculture and Food Infrastructure',
        entitySubtype: 'Grain Storage',
        code: '120108',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Banking Finance and Insurance Infrastructure',
        code: '120200',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Banking Finance and Insurance Infrastructure',
        entitySubtype: 'ATM',
        code: '120201',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Banking Finance and Insurance Infrastructure',
        entitySubtype: 'Bank',
        code: '120202',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Banking Finance and Insurance Infrastructure',
        entitySubtype: 'Bullion Storage',
        code: '120203',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Banking Finance and Insurance Infrastructure',
        entitySubtype: 'Economic Infrastructure Asset',
        code: '120204',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Banking Finance and Insurance Infrastructure',
        entitySubtype: 'Federal Reserve Bank',
        code: '120205',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Banking Finance and Insurance Infrastructure',
        entitySubtype: 'Financial Exchange',
        code: '120206',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Banking Finance and Insurance Infrastructure',
        entitySubtype: 'Financial Services, Other',
        code: '120207',
    },
    { entity: 'Infrastructure', entityType: 'Commercial Infrastructure', code: '120300' },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Chemical Plant',
        code: '120301',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Firearms Manufacturer',
        code: '120302',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Firearms Retailer',
        code: '120303',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Hazardous Material Production',
        code: '120304',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Hazardous Material Storage',
        code: '120305',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Industrial Site',
        code: '120306',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Landfill',
        code: '120307',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Pharmaceutical Manufacturer',
        code: '120308',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Contaminated Hazardous Waste Site',
        code: '120309',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Commercial Infrastructure',
        entitySubtype: 'Toxic Release Inventory',
        code: '120310',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Educational Facilities Infrastructure',
        code: '120400',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Educational Facilities Infrastructure',
        entitySubtype: 'College/University',
        code: '120401',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Educational Facilities Infrastructure',
        entitySubtype: 'School',
        code: '120402',
    },
    { entity: 'Infrastructure', entityType: 'Energy Facility Infrastructure', code: '120500' },
    {
        entity: 'Infrastructure',
        entityType: 'Energy Facility Infrastructure',
        entitySubtype: 'Electric Power',
        code: '120501',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Energy Facility Infrastructure',
        entitySubtype: 'Generation Station',
        code: '120502',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Energy Facility Infrastructure',
        entitySubtype: 'Natural Gas Facility',
        code: '120503',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Energy Facility Infrastructure',
        entitySubtype: 'Petroleum Facility',
        code: '120504',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Energy Facility Infrastructure',
        entitySubtype: 'Petroleum/Gas/Oil',
        code: '120505',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Energy Facility Infrastructure',
        entitySubtype: 'Propane Facility',
        code: '120506',
    },
    { entity: 'Infrastructure', entityType: 'Government Site Infrastructure', code: '120600' },
    { entity: 'Infrastructure', entityType: 'Medical Infrastructure', code: '120700' },
    {
        entity: 'Infrastructure',
        entityType: 'Medical Infrastructure',
        entitySubtype: 'Medical',
        code: '120701',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Medical Infrastructure',
        entitySubtype: 'Medical Treatment Facility (Hospital)',
        code: '120702',
    },
    { entity: 'Infrastructure', entityType: 'Military Infrastructure', code: '120800' },
    {
        entity: 'Infrastructure',
        entityType: 'Military Infrastructure',
        entitySubtype: 'Military Armory',
        code: '120801',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Military Infrastructure',
        entitySubtype: 'Military Base',
        code: '120802',
    },
    { entity: 'Infrastructure', entityType: 'Postal Services Infrastructure', code: '120900' },
    {
        entity: 'Infrastructure',
        entityType: 'Postal Services Infrastructure',
        entitySubtype: 'Postal Distribution Center',
        code: '120901',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Postal Services Infrastructure',
        entitySubtype: 'Post Office',
        code: '120902',
    },
    { entity: 'Infrastructure', entityType: 'Public Venues Infrastructure', code: '121000' },
    {
        entity: 'Infrastructure',
        entityType: 'Public Venues Infrastructure',
        entitySubtype: 'Enclosed Facility (Public Venue)',
        code: '121001',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Public Venues Infrastructure',
        entitySubtype: 'Open Facility (Public Venue)',
        code: '121002',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Public Venues Infrastructure',
        entitySubtype: 'Recreational Area',
        code: '121003',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Public Venues Infrastructure',
        entitySubtype: 'Religious Institution',
        code: '121004',
    },
    { entity: 'Infrastructure', entityType: 'Special Needs Infrastructure', code: '121100' },
    {
        entity: 'Infrastructure',
        entityType: 'Special Needs Infrastructure',
        entitySubtype: 'Adult Day Care',
        code: '121101',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Special Needs Infrastructure',
        entitySubtype: 'Child Day Care',
        code: '121102',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Special Needs Infrastructure',
        entitySubtype: 'Elder Care',
        code: '121103',
    },
    { entity: 'Infrastructure', entityType: 'Telecommunications Infrastructure', code: '121200' },
    {
        entity: 'Infrastructure',
        entityType: 'Telecommunications Infrastructure',
        entitySubtype: 'Broadcast Transmitter Antennae',
        code: '121201',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Telecommunications Infrastructure',
        entitySubtype: 'Telecommunications',
        code: '121202',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Telecommunications Infrastructure',
        entitySubtype: 'Telecommunications Tower',
        code: '121203',
    },
    { entity: 'Infrastructure', entityType: 'Transportation Infrastructure', code: '121300' },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Airport/Air Base',
        code: '121301',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Air Traffic Control Facility',
        code: '121302',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Bus Station',
        code: '121303',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Ferry Terminal',
        code: '121304',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Helicopter Landing Site',
        code: '121305',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Maintenance Facility',
        code: '121306',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Railhead/Railroad Station',
        code: '121307',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Rest Stop',
        code: '121308',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Sea Port/Naval Base',
        code: '121309',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Ship Yard',
        code: '121310',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Toll Facility',
        code: '121311',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Traffic Inspection Facility',
        code: '121312',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Transportation Infrastructure',
        entitySubtype: 'Tunnel',
        code: '121313',
    },
    { entity: 'Infrastructure', entityType: 'Water Supply Infrastructure', code: '121400' },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Control Valve',
        code: '121401',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Dam',
        code: '121402',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Discharge Outfall',
        code: '121403',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Ground Water Well',
        code: '121404',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Pumping Station',
        code: '121405',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Reservoir',
        code: '121406',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Storage Tower',
        code: '121407',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Surface Water Intake',
        code: '121408',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Wastewater Treatment Facility',
        code: '121409',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Water',
        code: '121410',
    },
    {
        entity: 'Infrastructure',
        entityType: 'Water Supply Infrastructure',
        entitySubtype: 'Water Treatment',
        code: '121411',
    },
] as const;
export type LandinstallationIcon = (typeof landInstallationIconValues)[number];

export const landinstallationModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Biological', category: 'CBRN Type', code: '01' },
    { modifier: 'Chemical', category: 'CBRN Type', code: '02' },
    { modifier: 'Nuclear', category: 'CBRN Type', code: '03' },
    { modifier: 'Radiological', category: 'CBRN Type', code: '04' },
    { modifier: 'Decontamination', category: 'CBRN Type', code: '05' },
    { modifier: 'Coal', category: 'Electric Power Type', code: '06' },
    { modifier: 'Geothermal', category: 'Electric Power Type', code: '07' },
    { modifier: 'Hydroelectric', category: ' Electric Power Type', code: '08' },
    { modifier: 'Natural Gas', category: 'Electric Power Type', code: '09' },
    { modifier: 'Petroleum', category: 'Electric Power Type', code: '10' },
    { modifier: 'Civilian', category: 'Operation', code: '11' },
    { modifier: 'Civilian Telephone', category: 'Telecommunication Type', code: '12' },
    { modifier: 'Civilian Television', category: 'Telecommunication Type', code: '13' },
    { modifier: 'Cyberspace', category: 'Capability', code: '14' },
] as const;
export type landinstallationModifier1 = (typeof landinstallationModifier1Values)[number];

export const landinstallationModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Biological Warfare Production', category: 'CBRN', code: '01' },
    { modifier: 'Chemical Warfare Production', category: 'CBRN', code: '02' },
    { modifier: 'Nuclear Warfare Production', category: 'CBRN', code: '03' },
    { modifier: 'Radiological Warfare Production', category: 'CBRN', code: '04' },
    { modifier: 'Atomic Energy Reactor', category: 'CBRN', code: '05' },
    { modifier: 'Nuclear Material Production', category: 'CBRN', code: '06' },
    { modifier: 'Nuclear Material Storage', category: 'CBRN', code: '07' },
    { modifier: 'Weapons Grade Production', category: 'CBRN', code: '08' },
    { modifier: 'Cyberspace', category: 'Capability', code: '09' },
] as const;
export type landinstallationModifier2 = (typeof landinstallationModifier2Values)[number];

export const controlMeasureIconValues = [
    { entity: 'Command and Control Lines', code: '110000' },
    {
        entity: 'Command and Control Lines',
        entityType: 'Boundary',
        geometry: 'Line',
        code: '110100',
    },
    {
        entity: 'Command and Control Lines',
        entityType: 'Light Line',
        geometry: 'Line',
        code: '110200',
    },
    {
        entity: 'Command and Control Lines',
        entityType: 'Engineer Work Line',
        geometry: 'Line',
        code: '110300',
    },
    { entity: 'Command and Control Areas', code: '120000' },
    {
        entity: 'Command and Control Areas',
        entityType: 'Area of Operations',
        geometry: 'Area',
        code: '120100',
    },
    {
        entity: 'Command and Control Areas',
        entityType: 'Named Area of Interest',
        geometry: 'Area',
        code: '120200',
    },
    {
        entity: 'Command and Control Areas',
        entityType: 'Targeted Area of Interest',
        geometry: 'Area',
        code: '120300',
    },
    {
        entity: 'Command and Control Areas',
        entityType: 'Airfield Zone',
        geometry: 'Area',
        code: '120400',
    },
    {
        entity: 'Command and Control Areas',
        entityType: 'Base Camp',
        geometry: 'Area',
        code: '120500',
    },
    {
        entity: 'Command and Control Areas',
        entityType: 'Guerrilla Base',
        geometry: 'Area',
        code: '120600',
    },
    { entity: 'Command and Control Points', code: '130000' },
    {
        entity: 'Command and Control Points',
        entityType: 'Unspecified Control Point',
        geometry: 'Point',
        code: '130100',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Amnesty Point',
        geometry: 'Point',
        code: '130200',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Checkpoint',
        geometry: 'Point',
        code: '130300',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Center of Main Effort',
        geometry: 'Point',
        code: '130400',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Contact Point',
        geometry: 'Point',
        code: '130500',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Coordinating Point',
        geometry: 'Point',
        code: '130600',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Decision Point',
        geometry: 'Point',
        code: '130700',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Distress Call',
        geometry: 'Point',
        code: '130800',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Entry Control Point',
        geometry: 'Point',
        code: '130900',
    },
    { entity: 'Command and Control Points', entityType: 'Fly-To-Point', code: '131000' },
    {
        entity: 'Command and Control Points',
        entityType: 'Fly-To-Point',
        entitySubtype: 'Sonobuoy',
        geometry: 'Point',
        code: '131001',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Fly-To-Point',
        entitySubtype: 'Weapon',
        geometry: 'Point',
        code: '131002',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Fly-To-Point',
        entitySubtype: 'Normal',
        geometry: 'Point',
        code: '131003',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Linkup Point',
        geometry: 'Point',
        code: '131100',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Passage Point',
        geometry: 'Point',
        code: '131200',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Point of Interest',
        geometry: 'Point',
        code: '131300',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Point of Interest',
        entitySubtype: 'Launch Event',
        geometry: 'Point',
        code: '131301',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Rally Point',
        geometry: 'Point',
        code: '131400',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Release Point',
        geometry: 'Point',
        code: '131500',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Start Point',
        geometry: 'Point',
        code: '131600',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Special Point',
        geometry: 'Point',
        code: '131700',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Waypoint',
        geometry: 'Point',
        code: '131800',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Airfield',
        geometry: 'Point',
        code: '131900',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Target Handover',
        geometry: 'Point',
        code: '132000',
    },
    {
        entity: 'Command and Control Points',
        entityType: 'Key Terrain',
        geometry: 'Point',
        code: '132100',
    },
    { entity: 'Maneuver Lines', code: '140000' },
    {
        entity: 'Maneuver Lines',
        entityType: 'Forward Line of Troops',
        geometry: 'Line',
        code: '140100',
    },
    { entity: 'Maneuver Lines', entityType: 'Phase Line', geometry: 'Line', code: '140300' },
    {
        entity: 'Maneuver Lines',
        entityType: 'Forward Edge of the Battle Area',
        geometry: 'Line',
        code: '140400',
    },
    {
        entity: 'Maneuver Lines',
        entityType: 'Principle Direction of Fire',
        geometry: 'Line',
        code: '140500',
    },
    {
        entity: 'Maneuver Lines',
        entityType: 'Direction of Attack',
        geometry: 'Line',
        code: '140600',
    },
    {
        entity: 'Maneuver Lines',
        entityType: 'Direction of Attack',
        entitySubtype: 'Friendly Aviation',
        geometry: 'Line',
        code: '140601',
    },
    {
        entity: 'Maneuver Lines',
        entityType: 'Direction of Attack',
        entitySubtype: 'Friendly Main Attack /Decisive',
        geometry: 'Line',
        code: '140602',
    },
    {
        entity: 'Maneuver Lines',
        entityType: 'Direction of Attack',
        entitySubtype: 'Friendly Supporting Attack',
        geometry: 'Line',
        code: '140603',
    },
    {
        entity: 'Maneuver Lines',
        entityType: 'Direction of Attack',
        entitySubtype: 'Feint',
        geometry: 'Line',
        code: '140605',
    },
    {
        entity: 'Maneuver Lines',
        entityType: 'Final Coordination Line',
        geometry: 'Line',
        code: '140700',
    },
    { entity: 'Maneuver Lines', entityType: 'Infiltration Lane', geometry: 'Line', code: '140800' },
    { entity: 'Maneuver Lines', entityType: 'Limit of Advance', geometry: 'Line', code: '140900' },
    { entity: 'Maneuver Lines', entityType: 'Line of Departure', geometry: 'Line', code: '141000' },
    {
        entity: 'Maneuver Lines',
        entityType: 'Line of Departure/Line of Contact',
        geometry: 'Line',
        code: '141100',
    },
    {
        entity: 'Maneuver Lines',
        entityType: 'Probable Line of Deployment',
        geometry: 'Line',
        code: '141200',
    },
    { entity: 'Maneuver Lines', entityType: 'Airhead Line', geometry: 'Line', code: '141300' },
    { entity: 'Maneuver Lines', entityType: 'Bridgehead Line', geometry: 'Line', code: '141400' },
    { entity: 'Maneuver Lines', entityType: 'Holding Line', geometry: 'Line', code: '141500' },
    { entity: 'Maneuver Lines', entityType: 'Release Line', geometry: 'Line', code: '141600' },
    { entity: 'Maneuver Lines', entityType: 'Ambush', geometry: 'Line', code: '141700' },
    { entity: 'Maneuver Areas', code: '150000' },
    { entity: 'Maneuver Areas', entityType: 'Area', code: '150100' },
    { entity: 'Maneuver Areas', entityType: 'Assembly Area', geometry: 'Area', code: '150200' },
    {
        entity: 'Maneuver Areas',
        entityType: 'Occupied Assembly Area',
        geometry: 'Area',
        code: '150300',
    },
    { entity: 'Maneuver Areas', entityType: 'Action Area', code: '150500' },
    {
        entity: 'Maneuver Areas',
        entityType: 'Action Area',
        entitySubtype: 'Joint Tactical Action Area (JTAA)',
        geometry: 'Area',
        code: '150501',
    },
    {
        entity: 'Maneuver Areas',
        entityType: 'Action Area',
        entitySubtype: 'Submarine Action Area (SAA)',
        geometry: 'Area',
        code: '150502',
    },
    {
        entity: 'Maneuver Areas',
        entityType: 'Action Area',
        entitySubtype: 'Submarine-Generated Action Area (SGAA)',
        geometry: 'Area',
        code: '150503',
    },
    { entity: 'Maneuver Areas', entityType: 'Drop Zone', geometry: 'Area', code: '150600' },
    { entity: 'Maneuver Areas', entityType: 'Extraction Zone', geometry: 'Area', code: '150700' },
    { entity: 'Maneuver Areas', entityType: 'Landing Zone', geometry: 'Area', code: '150800' },
    { entity: 'Maneuver Areas', entityType: 'Pick–Up Zone', geometry: 'Area', code: '150900' },
    { entity: 'Maneuver Areas', entityType: 'Fortified Area', geometry: 'Area', code: '151000' },
    {
        entity: 'Maneuver Areas',
        entityType: 'Limited Access Area',
        geometry: 'Area',
        code: '151100',
    },
    { entity: 'Maneuver Areas', entityType: 'Battle Position', geometry: 'Area', code: '151200' },
    {
        entity: 'Maneuver Areas',
        entityType: 'Battle Position',
        entitySubtype: 'Prepared (P) but not Occupied',
        geometry: 'Area',
        code: '151202',
    },
    {
        entity: 'Maneuver Areas',
        entityType: 'Battle Position',
        entitySubtype: 'Strong Point',
        geometry: 'Area',
        code: '151203',
    },
    {
        entity: 'Maneuver Areas',
        entityType: 'Battle Position',
        entitySubtype: 'Contain',
        geometry: 'Area',
        code: '151204',
    },
    {
        entity: 'Maneuver Areas',
        entityType: 'Battle Position',
        entitySubtype: 'Retain',
        geometry: 'Area',
        code: '151205',
    },
    {
        entity: 'Maneuver Areas',
        entityType: 'Engagement Area (EA)',
        geometry: 'Area',
        code: '151300',
    },
    { entity: 'Maneuver Areas', entityType: 'Axis of Advance', code: '151400' },
    {
        entity: 'Maneuver Areas',
        entityType: 'Axis of Advance',
        entitySubtype: 'Friendly Airborne/Aviation',
        geometry: 'Line',
        code: '151401',
    },
    {
        entity: 'Maneuver Areas',
        entityType: 'Axis of Advance',
        entitySubtype: 'Attack Helicopter',
        geometry: 'Line',
        code: '151402',
    },
    {
        entity: 'Maneuver Areas',
        entityType: 'Axis of Advance',
        entitySubtype: 'Main Attack',
        geometry: 'Line',
        code: '151403',
    },
    {
        entity: 'Maneuver Areas',
        entityType: 'Axis of Advance',
        entitySubtype: 'Supporting Attack',
        geometry: 'Line',
        code: '151404',
    },
    { entity: 'Maneuver Areas', entityType: 'Assault Position', geometry: 'Area', code: '151500' },
    { entity: 'Maneuver Areas', entityType: 'Attack Position', geometry: 'Area', code: '151600' },
    { entity: 'Maneuver Areas', entityType: 'Objective', geometry: 'Area', code: '151700' },
    { entity: 'Maneuver Areas', entityType: 'Encirclement', code: '151800' },
    { entity: 'Maneuver Areas', entityType: 'Penetration Box', geometry: 'Area', code: '151900' },
    {
        entity: 'Maneuver Areas',
        entityType: 'Attack by Fire Position',
        geometry: 'Area',
        code: '152000',
    },
    { entity: 'Maneuver Areas', entityType: 'Support by Fire', geometry: 'Area', code: '152100' },
    {
        entity: 'Maneuver Areas',
        entityType: 'Search Area/Reconnaissance Area',
        geometry: 'Area',
        code: '152200',
    },
    { entity: 'Maneuver Points', code: '160000' },
    {
        entity: 'Maneuver Points',
        entityType: 'Observation Post/Outpost (unspecified)',
        geometry: 'Point',
        code: '160100',
    },
    {
        entity: 'Maneuver Points',
        entityType: 'Observation Post/Outpost (specified)',
        geometry: 'Point',
        code: '160200',
    },
    {
        entity: 'Maneuver Points',
        entityType: 'Observation Post/Outpost (specified)',
        entitySubtype: 'Reconnaissance Outpost',
        geometry: 'Point',
        code: '160201',
    },
    {
        entity: 'Maneuver Points',
        entityType: 'Observation Post/Outpost (specified)',
        entitySubtype: 'Forward Observer/Spotter Outpost/Position',
        geometry: 'Point',
        code: '160202',
    },
    {
        entity: 'Maneuver Points',
        entityType: 'Observation Post/Outpost (specified)',
        entitySubtype: 'CBRN Observation Outpost',
        geometry: 'Point',
        code: '160203',
    },
    {
        entity: 'Maneuver Points',
        entityType: 'Observation Post/Outpost (specified)',
        entitySubtype: 'Sensor Outpost /Listening Post',
        geometry: 'Point',
        code: '160204',
    },
    {
        entity: 'Maneuver Points',
        entityType: 'Observation Post/Outpost (specified)',
        entitySubtype: 'Combat Outpost',
        geometry: 'Point',
        code: '160205',
    },
    {
        entity: 'Maneuver Points',
        entityType: 'Target Reference Point',
        geometry: 'Point',
        code: '160300',
    },
    {
        entity: 'Maneuver Points',
        entityType: 'Point of Departure',
        geometry: 'Point',
        code: '160400',
    },
    { entity: 'Airspace Control (Corridors) Areas', code: '170000' },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Air Corridor',
        geometry: 'Area',
        code: '170100',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Low Level Transit Route',
        geometry: 'Area',
        code: '170200',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Minimum–Risk Route',
        geometry: 'Area',
        code: '170300',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Safe Lane',
        geometry: 'Area',
        code: '170400',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Standard Use Army Aircraft Flight Route',
        geometry: 'Area',
        code: '170500',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Transit Corridor',
        geometry: 'Area',
        code: '170600',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Unmanned Aircraft (UA) Route',
        geometry: 'Area',
        code: '170700',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Base Defense Zone',
        geometry: 'Area',
        code: '170800',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'High–Density Airspace Control Zone',
        geometry: 'Area',
        code: '170900',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Restricted Operations Zone',
        geometry: 'Area',
        code: '171000',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Air-to-Air Restricted Operating Zone',
        geometry: 'Area',
        code: '171100',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Unmanned Aircraft Restricted Operating Zone',
        geometry: 'Area',
        code: '171200',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Weapon Engagement Zone',
        geometry: 'Area',
        code: '171300',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Fighter Engagement Zone',
        geometry: 'Area',
        code: '171400',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Joint Engagement Zone',
        geometry: 'Area',
        code: '171500',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Missile Engagement Zone',
        geometry: 'Area',
        code: '171600',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Low Altitude Missile Engagement Zone',
        geometry: 'Area',
        code: '171700',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'High Altitude Missile Engagement Zone',
        geometry: 'Area',
        code: '171800',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Short Range Air Defense Engagement Zone',
        geometry: 'Area',
        code: '171900',
    },
    {
        entity: 'Airspace Control (Corridors) Areas',
        entityType: 'Weapons Free Zone',
        geometry: 'Area',
        code: '172000',
    },
    { entity: 'Airspace Control Points', code: '180000' },
    {
        entity: 'Airspace Control Points',
        entityType: 'Air Control Point',
        geometry: 'Point',
        code: '180100',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Communications Checkpoint',
        geometry: 'Point',
        code: '180200',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Downed Aircraft Pick–up Point',
        geometry: 'Point',
        code: '180300',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Pop–up Point',
        geometry: 'Point',
        code: '180400',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Air Control Rendezvous',
        geometry: 'Point',
        code: '180500',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Tactical Air Navigation (TACAN)',
        geometry: 'Point',
        code: '180600',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Combat Air Patrol (CAP)Station',
        geometry: 'Point',
        code: '180700',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Airborne Early Warning (AEW) Station',
        geometry: 'Point',
        code: '180800',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'ASW (Helo and F/W) Station',
        geometry: 'Point',
        code: '180900',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Strike Initial Point',
        geometry: 'Point',
        code: '181000',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Replenishment Station',
        geometry: 'Point',
        code: '181100',
    },
    { entity: 'Airspace Control Points', entityType: 'Tanking', geometry: 'Point', code: '181200' },
    {
        entity: 'Airspace Control Points',
        entityType: 'Antisubmarine Warfare, Rotary Wing',
        geometry: 'Point',
        code: '181300',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Surface Combat Air Patrol (SUCAP) – Fixed Wing',
        geometry: 'Point',
        code: '181400',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'SUCAP – Rotary Wing',
        geometry: 'Point',
        code: '181500',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'MIW – Fixed Wing',
        geometry: 'Point',
        code: '181600',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'MIW – Rotary Wing',
        geometry: 'Point',
        code: '181700',
    },
    { entity: 'Airspace Control Points', entityType: 'Tomcat', geometry: 'Point', code: '181800' },
    { entity: 'Airspace Control Points', entityType: 'Rescue', geometry: 'Point', code: '181900' },
    {
        entity: 'Airspace Control Points',
        entityType: 'Unmanned Aerial System (UAS/UA)',
        geometry: 'Point',
        code: '182000',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Vertical Takeoff and Landing (VTOL) Tactical Unmanned Aircraft (VTUA)',
        geometry: 'Point',
        code: '182100',
    },
    { entity: 'Airspace Control Points', entityType: 'Orbit', geometry: 'Point', code: '182200' },
    {
        entity: 'Airspace Control Points',
        entityType: 'Orbit – Figure Eight',
        geometry: 'Point',
        code: '182300',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Orbit – Race Track',
        geometry: 'Point',
        code: '182400',
    },
    {
        entity: 'Airspace Control Points',
        entityType: 'Orbit – Random Closed',
        geometry: 'Point',
        code: '182500',
    },
    { entity: 'Airspace Control Lines', code: '190000' },
    {
        entity: 'Airspace Control Lines',
        entityType: 'Identification Friend or Foe Off Line',
        geometry: 'Line',
        code: '190100',
    },
    {
        entity: 'Airspace Control Lines',
        entityType: 'Identification Friend or Foe On Line',
        geometry: 'Line',
        code: '190200',
    },
    { entity: 'Maritime Control Areas', code: '200000' },
    { entity: 'Maritime Control Areas', entityType: 'Launch Area', code: '200100' },
    {
        entity: 'Maritime Control Areas',
        entityType: 'Launch Area',
        entitySubtype: 'Ellipse/Circle',
        geometry: 'Area',
        code: '200101',
    },
    { entity: 'Maritime Control Areas', entityType: 'Defended Area', code: '200200' },
    {
        entity: 'Maritime Control Areas',
        entityType: 'Defended Area',
        entitySubtype: 'Ellipse/Circle',
        geometry: 'Area',
        code: '200201',
    },
    {
        entity: 'Maritime Control Areas',
        entityType: 'Defended Area',
        entitySubtype: 'Rectangle',
        geometry: 'Area',
        code: '200202',
    },
    {
        entity: 'Maritime Control Areas',
        entityType: 'No Attack (NOTACK) Zone',
        geometry: 'Area',
        code: '200300',
    },
    {
        entity: 'Maritime Control Areas',
        entityType: 'Ship Area of Interest',
        geometry: 'Point',
        code: '200400',
    },
    {
        entity: 'Maritime Control Areas',
        entityType: 'Ship Area of Interest',
        entitySubtype: 'Ellipse/Circle',
        geometry: 'Area',
        code: '200401',
    },
    {
        entity: 'Maritime Control Areas',
        entityType: 'Ship Area of Interest',
        entitySubtype: 'Rectangle',
        geometry: 'Area',
        code: '200402',
    },
    {
        entity: 'Maritime Control Areas',
        entityType: 'Active Maneuver Area',
        geometry: 'Point',
        code: '200500',
    },
    { entity: 'Maritime Control Areas', entityType: 'Cued Acquisition Doctrine', code: '200600' },
    { entity: 'Maritime Control Areas', entityType: 'Radar Search Doctrine', code: '200700' },
    { entity: 'Maritime Control Points', code: '210000' },
    {
        entity: 'Maritime Control Points',
        entityType: 'Plan Ship',
        geometry: 'Point',
        code: '210100',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Aim Point',
        geometry: 'Point',
        code: '210200',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Defended Asset',
        geometry: 'Point',
        code: '210300',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Drop Point',
        geometry: 'Point',
        code: '210400',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Entry Point',
        geometry: 'Point',
        code: '210500',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Air Detonation',
        geometry: 'Point',
        code: '210600',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Ground Zero',
        geometry: 'Point',
        code: '210700',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Impact Point',
        geometry: 'Point',
        code: '210800',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Predicted Impact Point',
        geometry: 'Point',
        code: '210900',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Launched Torpedo',
        geometry: 'Point',
        code: '211000',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Missile Detection Point',
        geometry: 'Point',
        code: '211100',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Acoustic Countermeasure (Decoy)',
        geometry: 'Point',
        code: '211200',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Electronic Countermeasures (ECM) Decoy',
        geometry: 'Point',
        code: '211300',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Brief Contact',
        geometry: 'Point',
        code: '211400',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Datum Lost Contact',
        geometry: 'Point',
        code: '211500',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'BT Buoy Drop',
        geometry: 'Point',
        code: '211600',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Reported Bottomed Sub',
        geometry: 'Point',
        code: '211700',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Moving Haven',
        geometry: 'Point',
        code: '211800',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Screen Center',
        geometry: 'Point',
        code: '211900',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Lost Contact',
        geometry: 'Point',
        code: '212000',
    },
    { entity: 'Maritime Control Points', entityType: 'Sinker', geometry: 'Point', code: '212100' },
    {
        entity: 'Maritime Control Points',
        entityType: 'Trial Track',
        geometry: 'Point',
        code: '212200',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Acoustic Fix',
        geometry: 'Point',
        code: '212300',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Electromagnetic Fix',
        geometry: 'Point',
        code: '212400',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Electromagnetic – Magnetic Anomaly Detection (MAD)',
        geometry: 'Point',
        code: '212500',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Optical Fix',
        geometry: 'Point',
        code: '212600',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Formation',
        geometry: 'Point',
        code: '212700',
    },
    { entity: 'Maritime Control Points', entityType: 'Harbor', geometry: 'Point', code: '212800' },
    {
        entity: 'Maritime Control Points',
        entityType: 'Harbor Entrance Point',
        geometry: 'Point',
        code: '212900',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Harbor Entrance Point',
        entitySubtype: 'A',
        geometry: 'Point',
        code: '212901',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Harbor Entrance Point',
        entitySubtype: 'Q',
        geometry: 'Point',
        code: '212902',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Harbor Entrance Point',
        entitySubtype: 'X',
        geometry: 'Point',
        code: '212903',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Harbor Entrance Point',
        entitySubtype: 'Y',
        geometry: 'Point',
        code: '212904',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Dip Position',
        geometry: 'Point',
        code: '213000',
    },
    { entity: 'Maritime Control Points', entityType: 'Search', geometry: 'Point', code: '213100' },
    {
        entity: 'Maritime Control Points',
        entityType: 'Search Area',
        geometry: 'Point',
        code: '213200',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Search Center',
        geometry: 'Point',
        code: '213300',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Navigational Reference Point',
        geometry: 'Point',
        code: '213400',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        geometry: 'Point',
        code: '213500',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Ambient Noise',
        geometry: 'Point',
        code: '213501',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Air Transportable Communication',
        geometry: 'Point',
        code: '213502',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Barra',
        geometry: 'Point',
        code: '213503',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Bathythermograph Transmitting',
        geometry: 'Point',
        code: '213504',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Command Active Multi-Beam (CAMBS)',
        geometry: 'Point',
        code: '213505',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Command Active Sonobuoy Directional Command Active Sonobuoy System (CASS)',
        geometry: 'Point',
        code: '213506',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Directional Frequency Analysis and Recording (DIFAR)',
        geometry: 'Point',
        code: '213507',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Directional Command Active Sonobuoy System (DICASS)',
        geometry: 'Point',
        code: '213508',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Expendable Reliable Acoustic Path Sonobuoy (ERAPS)',
        geometry: 'Point',
        code: '213509',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Expired',
        geometry: 'Point',
        code: '213510',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Kingpin',
        geometry: 'Point',
        code: '213511',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Low Frequency Analysis and Recording (LOFAR)',
        geometry: 'Point',
        code: '213512',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Pattern Center',
        geometry: 'Point',
        code: '213513',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Range Only',
        geometry: 'Point',
        code: '213514',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sonobuoy',
        entitySubtype: 'Vertical Line Array Directional Frequency Analysis and Recording (DIFAR)',
        geometry: 'Point',
        code: '213515',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Reference Point',
        geometry: 'Point',
        code: '213600',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Special Point',
        geometry: 'Point',
        code: '213700',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Navigational Reference Point(Points)',
        geometry: 'Point',
        code: '213800',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Data Link Reference Point',
        geometry: 'Point',
        code: '213900',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Vital Area Center',
        geometry: 'Point',
        code: '214100',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Corridor Tab Point',
        geometry: 'Point',
        code: '214200',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Enemy Point',
        geometry: 'Point',
        code: '214300',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Marshall Point',
        geometry: 'Point',
        code: '214400',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Position and Intended Movement (PIM)',
        geometry: 'Point',
        code: '214500',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Pre-Landfall Waypoint',
        geometry: 'Point',
        code: '214600',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Estimated Position (EP)',
        geometry: 'Point',
        code: '214700',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Waypoint',
        geometry: 'Point',
        code: '214800',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'General Sea Subsurface Station',
        geometry: 'Point',
        code: '214900',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Submarine Sea Subsurface Station',
        geometry: 'Point',
        code: '215000',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Submarine Antisubmarine Warfare Sea Subsurface Station',
        geometry: 'Point',
        code: '215100',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Unmanned Underwater Vehicle Sea Subsurface Station',
        geometry: 'Point',
        code: '215200',
    },
    {
        entity: 'Maritime Control Points',
        entityType:
            'Antisubmarine Warfare (ASW) Unmanned Underwater Vehicle Sea Subsurface Station',
        geometry: 'Point',
        code: '215300',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Mine Warfare Unmanned Underwater Vehicle Sea Subsurface Station',
        geometry: 'Point',
        code: '215400',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sea Surface Warfare Unmanned Underwater Vehicle Subsurface Station',
        geometry: 'Point',
        code: '215500',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'General Sea Surface Station',
        geometry: 'Point',
        code: '215600',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Antisubmarine Warfare (ASW) Sea Surface Station',
        geometry: 'Point',
        code: '215700',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Mine Warfare Sea Surface Station',
        geometry: 'Point',
        code: '215800',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Non-Combatant Sea Surface Station',
        geometry: 'Point',
        code: '215900',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Picket Sea Surface Station',
        geometry: 'Point',
        code: '216000',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Rendezvous Sea Surface Station',
        geometry: 'Point',
        code: '216100',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Replenishment at Sea Surface Station',
        geometry: 'Point',
        code: '216200',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Rescue Sea Surface Station',
        geometry: 'Point',
        code: '216300',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Surface Warfare Sea Surface Station',
        geometry: 'Point',
        code: '216400',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Unmanned Underwater Vehicle Sea Surface Station',
        geometry: 'Point',
        code: '216500',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Antisubmarine Warfare (ASW) Unmanned Underwater Vehicle Sea Surface Station',
        geometry: 'Point',
        code: '216600',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Mine Warfare Unmanned Underwater Vehicle Sea Surface Station',
        geometry: 'Point',
        code: '216700',
    },
    {
        entity: 'Maritime Control Points',
        entityType:
            'Remote Multi-Mission Vehicle Mine Warfare Unmanned Underwater Sea Surface Station',
        geometry: 'Point',
        code: '216800',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Surface Warfare Mine Warfare Unmanned Underwater Vehicle Sea Surface Station',
        geometry: 'Point',
        code: '216900',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Shore Control Station',
        geometry: 'Point',
        code: '217000',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'General Route',
        geometry: 'Point',
        code: '217100',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Diversion Route',
        geometry: 'Point',
        code: '217200',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Position and Intended Movement (PIM) Route',
        geometry: 'Point',
        code: '217300',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Picket Route',
        geometry: 'Point',
        code: '217400',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Point R Route',
        geometry: 'Point',
        code: '217500',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Rendezvous Route',
        geometry: 'Point',
        code: '217600',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Waypoint Route',
        geometry: 'Point',
        code: '217700',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Clutter, Stationary or Cease Reporting',
        geometry: 'Point',
        code: '217800',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Tentative or Provisional Track',
        geometry: 'Point',
        code: '217900',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Distressed Vessel',
        geometry: 'Point',
        code: '218000',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Ditched Aircraft/Downed Aircraft',
        geometry: 'Point',
        code: '218100',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Person in Water/Bailout',
        geometry: 'Point',
        code: '218200',
    },
    { entity: 'Maritime Control Points', entityType: 'Iceberg', geometry: 'Point', code: '218300' },
    {
        entity: 'Maritime Control Points',
        entityType: 'Navigational',
        geometry: 'Line',
        code: '218400',
    },
    { entity: 'Maritime Control Points', entityType: 'Oil Rig', geometry: 'Point', code: '218500' },
    {
        entity: 'Maritime Control Points',
        entityType: 'Sea Mine–Like',
        geometry: 'Point',
        code: '218600',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Bottom Return/Non-Mine, Mine-Like Bottom Object (NOMBO)',
        geometry: 'Point',
        code: '218700',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Bottom Return/Non-Mine, Mine-Like Bottom Object (NOMBO)/Installation Manmade',
        geometry: 'Point',
        code: '218800',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Bottom Return/Non-Mine, Mine-Like Bottom Object (NOMBO)/Installation Manmade',
        entitySubtype: 'Marine Life',
        geometry: 'Point',
        code: '218900',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Bottom Return/Non-Mine, Mine-Like Bottom Object (NOMBO)/Installation Manmade',
        entitySubtype: 'Sea Anomaly (Wake, Current, Knuckle)',
        geometry: 'Point',
        code: '219000',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Bottom Return/Non-Mine, Mine-Like Bottom Object (NOMBO)/Installation Manmade',
        entitySubtype: 'Bottom Return/Non-MILCO, Wreck, Dangerous',
        geometry: 'Point',
        code: '219100',
    },
    {
        entity: 'Maritime Control Points',
        entityType: 'Bottom Return/Non-Mine, Mine-Like Bottom Object (NOMBO)/Installation Manmade',
        entitySubtype: 'Bottom Return/Non-MILCO, Wreck, Non Dangerous',
        geometry: 'Point',
        code: '219200',
    },
    { entity: 'Maritime Control Lines', code: '220000' },
    {
        entity: 'Maritime Control Lines',
        entityType: 'Bearing Line',
        geometry: 'Line',
        code: '220100',
    },
    {
        entity: 'Maritime Control Lines',
        entityType: 'Bearing Line',
        entitySubtype: 'Electronic',
        geometry: 'Line',
        code: '220101',
    },
    {
        entity: 'Maritime Control Lines',
        entityType: 'Bearing Line',
        entitySubtype: 'Electronic Warfare (EW)',
        geometry: 'Line',
        code: '220102',
    },
    {
        entity: 'Maritime Control Lines',
        entityType: 'Bearing Line',
        entitySubtype: 'Acoustic',
        geometry: 'Line',
        code: '220103',
    },
    {
        entity: 'Maritime Control Lines',
        entityType: 'Bearing Line',
        entitySubtype: 'Acoustic (Ambiguous)',
        geometry: 'Line',
        code: '220104',
    },
    {
        entity: 'Maritime Control Lines',
        entityType: 'Bearing Line',
        entitySubtype: 'Torpedo',
        geometry: 'Line',
        code: '220105',
    },
    {
        entity: 'Maritime Control Lines',
        entityType: 'Bearing Line',
        entitySubtype: 'Electro-Optical Intercept',
        geometry: 'Line',
        code: '220106',
    },
    {
        entity: 'Maritime Control Lines',
        entityType: 'Bearing Line',
        entitySubtype: 'Jammer',
        geometry: 'Line',
        code: '220107',
    },
    {
        entity: 'Maritime Control Lines',
        entityType: 'Bearing Line',
        entitySubtype: 'Radio Direction Finder (RDF)',
        geometry: 'Line',
        code: '220108',
    },
    { entity: 'Fires Areas', code: '240000' },
    { entity: 'Fires Areas', entityType: 'Airspace Coordination Area', code: '240100' },
    {
        entity: 'Fires Areas',
        entityType: 'Airspace Coordination Area',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '240101',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Airspace Coordination Area',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '240102',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Airspace Coordination Area',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '240103',
    },
    { entity: 'Fires Areas', entityType: 'Free Fire Area', code: '240200' },
    {
        entity: 'Fires Areas',
        entityType: 'Free Fire Area',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '240201',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Free Fire Area',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '240202',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Free Fire Area',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '240203',
    },
    { entity: 'Fires Areas', entityType: 'No Fire Area', code: '240300' },
    {
        entity: 'Fires Areas',
        entityType: 'No Fire Area',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '240301',
    },
    {
        entity: 'Fires Areas',
        entityType: 'No Fire Area',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '240302',
    },
    {
        entity: 'Fires Areas',
        entityType: 'No Fire Area',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '240303',
    },
    { entity: 'Fires Areas', entityType: 'Restricted Fire Area', code: '240400' },
    {
        entity: 'Fires Areas',
        entityType: 'Restricted Fire Area',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '240401',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Restricted Fire Area',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '240402',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Restricted Fire Area',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '240403',
    },
    { entity: 'Fires Areas', entityType: 'Position Area For Artillery (PAA)', code: '240500' },
    {
        entity: 'Fires Areas',
        entityType: 'Position Area For Artillery (PAA)',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '240501',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Position Area For Artillery (PAA)',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '240502',
    },
    { entity: 'Fires Areas', entityType: 'Point Targets', code: '240600' },
    {
        entity: 'Fires Areas',
        entityType: 'Point Targets',
        entitySubtype: 'Point or Single Target',
        geometry: 'Point',
        code: '240601',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Point Targets',
        entitySubtype: 'Nuclear Target',
        geometry: 'Point',
        code: '240602',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Point Targets',
        entitySubtype: 'Target-Recorded',
        geometry: 'Point',
        code: '240603',
    },
    { entity: 'Fires Areas', entityType: 'Linear Targets', code: '240700' },
    {
        entity: 'Fires Areas',
        entityType: 'Linear Targets',
        entitySubtype: 'Linear Target',
        geometry: 'Line',
        code: '240701',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Linear Targets',
        entitySubtype: 'Linear Smoke Target',
        geometry: 'Line',
        code: '240702',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Linear Targets',
        entitySubtype: 'Final Protective Fire (FPF)',
        geometry: 'Line',
        code: '240703',
    },
    { entity: 'Fires Areas', entityType: 'Area Targets', code: '240800' },
    {
        entity: 'Fires Areas',
        entityType: 'Area Targets',
        entitySubtype: 'Area Target',
        geometry: 'Area',
        code: '240801',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Area Targets',
        entitySubtype: 'Rectangular Target',
        geometry: 'Area',
        code: '240802',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Area Targets',
        entitySubtype: 'Circular Target',
        geometry: 'Area',
        code: '240803',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Area Targets',
        entitySubtype: 'Rectangular Target – Single Target',
        geometry: 'Area',
        code: '240804',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Area Targets',
        entitySubtype: 'Series or Groups of Targets',
        geometry: 'Area',
        code: '240805',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Area Targets',
        entitySubtype: 'Smoke',
        geometry: 'Area',
        code: '240806',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Area Targets',
        entitySubtype: 'Bomb Area',
        geometry: 'Area',
        code: '240808',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Fire Support Station',
        geometry: 'Point',
        code: '240900',
    },
    { entity: 'Fires Areas', entityType: 'Fire Support Area', code: '241000' },
    {
        entity: 'Fires Areas',
        entityType: 'Fire Support Area',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241001',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Fire Support Area',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241002',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Fire Support Area',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241003',
    },
    { entity: 'Fires Areas', entityType: 'Artillery Target Intelligence Zone', code: '241100' },
    {
        entity: 'Fires Areas',
        entityType: 'Artillery Target Intelligence Zone',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241101',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Artillery Target Intelligence Zone',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241102',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Artillery Target Intelligence Zone',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241103',
    },
    { entity: 'Fires Areas', entityType: 'Call for Fire Area', code: '241200' },
    {
        entity: 'Fires Areas',
        entityType: 'Call for Fire Area',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241201',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Call for Fire Area',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241202',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Call for Fire Area',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241203',
    },
    { entity: 'Fires Areas', entityType: 'Censor Zone', code: '241300' },
    {
        entity: 'Fires Areas',
        entityType: 'Censor Zone',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241301',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Censor Zone',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241302',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Censor Zone',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241303',
    },
    { entity: 'Fires Areas', entityType: 'Critical Friendly Zone', code: '241400' },
    {
        entity: 'Fires Areas',
        entityType: 'Critical Friendly Zone',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241401',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Critical Friendly Zone',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241402',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Critical Friendly Zone',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241403',
    },
    { entity: 'Fires Areas', entityType: 'Dead Space Area', code: '241500' },
    {
        entity: 'Fires Areas',
        entityType: 'Dead Space Area',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241501',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Dead Space Area',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241502',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Dead Space Area',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241503',
    },
    { entity: 'Fires Areas', entityType: 'Sensor Zone', code: '241600' },
    {
        entity: 'Fires Areas',
        entityType: 'Sensor Zone',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241601',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Sensor Zone',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241602',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Sensor Zone',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241603',
    },
    { entity: 'Fires Areas', entityType: 'Target Build–up Area', code: '241700' },
    {
        entity: 'Fires Areas',
        entityType: 'Target Build–up Area',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241701',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Target Build–up Area',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241702',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Target Build–up Area',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241703',
    },
    { entity: 'Fires Areas', entityType: 'Target Value Area', code: '241800' },
    {
        entity: 'Fires Areas',
        entityType: 'Target Value Area',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241801',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Target Value Area',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241802',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Target Value Area',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241803',
    },
    { entity: 'Fires Areas', entityType: 'Zone of Responsibility', code: '241900' },
    {
        entity: 'Fires Areas',
        entityType: 'Zone of Responsibility',
        entitySubtype: 'Irregular',
        geometry: 'Area',
        code: '241901',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Zone of Responsibility',
        entitySubtype: 'Rectangular',
        geometry: 'Area',
        code: '241902',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Zone of Responsibility',
        entitySubtype: 'Circular',
        geometry: 'Area',
        code: '241903',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Terminally Guided Munition Footprint (TGMF)',
        geometry: 'Area',
        code: '242000',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Weapon/Sensor Range fan, Circular',
        geometry: 'Area',
        code: '242100',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Weapon/Sensor Range fan, Sector',
        geometry: 'Area',
        code: '242200',
    },
    { entity: 'Fires Areas', entityType: 'Kill Box', code: '242300' },
    {
        entity: 'Fires Areas',
        entityType: 'Kill Box',
        entitySubtype: 'Irregular, Blue',
        geometry: 'Area',
        code: '242301',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Kill Box',
        entitySubtype: 'Rectangular, Blue',
        geometry: 'Area',
        code: '242302',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Kill Box',
        entitySubtype: 'Circular, Blue',
        geometry: 'Area',
        code: '242303',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Kill Box',
        entitySubtype: 'Irregular, Purple',
        geometry: 'Area',
        code: '242304',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Kill Box',
        entitySubtype: 'Rectangular, Purple',
        geometry: 'Area',
        code: '242305',
    },
    {
        entity: 'Fires Areas',
        entityType: 'Kill Box',
        entitySubtype: 'Circular, Purple',
        geometry: 'Area',
        code: '242306',
    },
    { entity: 'Fires Points', code: '250000' },
    { entity: 'Fires Points', entityType: 'Firing Point', geometry: 'Point', code: '250100' },
    { entity: 'Fires Points', entityType: 'Hide Point', geometry: 'Point', code: '250200' },
    { entity: 'Fires Points', entityType: 'Launch Point', geometry: 'Point', code: '250300' },
    { entity: 'Fires Points', entityType: 'Reload Point', geometry: 'Point', code: '250400' },
    {
        entity: 'Fires Points',
        entityType: 'Survey Control Point',
        geometry: 'Point',
        code: '250500',
    },
    { entity: 'Fire Lines', code: '260000' },
    {
        entity: 'Fire Lines',
        entityType: 'Fire Support Coordination Line (FSCL)',
        geometry: 'Line',
        code: '260100',
    },
    {
        entity: 'Fire Lines',
        entityType: 'Coordinated Fire Line (CFL)',
        geometry: 'Line',
        code: '260200',
    },
    { entity: 'Fire Lines', entityType: 'No Fire Line', geometry: 'Line', code: '260300' },
    {
        entity: 'Fire Lines',
        entityType: 'Battlefield Coordination Line',
        geometry: 'Line',
        code: '260400',
    },
    { entity: 'Fire Lines', entityType: 'Restrictive Fire Line', geometry: 'Line', code: '260500' },
    { entity: 'Fire Lines', entityType: 'Munition Flight Path', geometry: 'Line', code: '260600' },
    { entity: 'Protection Areas', code: '270000' },
    { entity: 'Protection Areas', entityType: 'Obstacle Belt', geometry: 'Area', code: '270100' },
    { entity: 'Protection Areas', entityType: 'Obstacle Zone', geometry: 'Area', code: '270200' },
    {
        entity: 'Protection Areas',
        entityType: 'Obstacle Free Zone',
        geometry: 'Area',
        code: '270300',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Obstacle Restricted Zone',
        geometry: 'Area',
        code: '270400',
    },
    { entity: 'Protection Areas', entityType: 'Obstacle Effects', code: '270500' },
    {
        entity: 'Protection Areas',
        entityType: 'Obstacle Effects',
        entitySubtype: 'Block',
        geometry: 'Area',
        code: '270501',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Obstacle Effects',
        entitySubtype: 'Disrupt',
        geometry: 'Area',
        code: '270502',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Obstacle Effects',
        entitySubtype: 'Fix',
        geometry: 'Line',
        code: '270503',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Obstacle Effects',
        entitySubtype: 'Turn',
        geometry: 'Line',
        code: '270504',
    },
    { entity: 'Protection Areas', entityType: 'Obstacle Bypass', code: '270600' },
    {
        entity: 'Protection Areas',
        entityType: 'Obstacle Bypass',
        entitySubtype: 'Easy',
        geometry: 'MultiPoint',
        code: '270601',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Obstacle Bypass',
        entitySubtype: 'Difficult',
        geometry: 'MultiPoint',
        code: '270602',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Obstacle Bypass',
        entitySubtype: 'Impossible',
        geometry: 'MultiPoint',
        code: '270603',
    },
    { entity: 'Protection Areas', entityType: 'Minefields', code: '270700' },
    {
        entity: 'Protection Areas',
        entityType: 'Minefields',
        entitySubtype: 'Minefield, Static Depiction',
        geometry: 'Point',
        code: '270701',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Minefields',
        entitySubtype: 'Minefield, Dynamic Depiction',
        geometry: 'Area',
        code: '270707',
    },
    { entity: 'Protection Areas', entityType: 'Mined Area', geometry: 'Area', code: '270800' },
    {
        entity: 'Protection Areas',
        entityType: 'Mined Area, Fenced',
        geometry: 'Area',
        code: '270801',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Unexploded Explosive Ordnance (UXO) Area',
        geometry: 'Area',
        code: '271000',
    },
    { entity: 'Protection Areas', entityType: 'Bridge or Gap', geometry: 'Line', code: '271100' },
    {
        entity: 'Protection Areas',
        entityType: 'Roadblocks, Craters and Blown Bridges',
        code: '271200',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Roadblocks, Craters and Blown Bridges',
        entitySubtype: 'Planned',
        geometry: 'Line',
        code: '271201',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Roadblocks, Craters and Blown Bridges',
        entitySubtype: 'Explosives, State of Readiness 1 (Safe)',
        geometry: 'Line',
        code: '271202',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Roadblocks, Craters and Blown Bridges',
        entitySubtype: 'Explosives, State of Readiness 2 (armed but passable)',
        geometry: 'Line',
        code: '271203',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Roadblocks, Craters and Blown Bridges',
        entitySubtype: 'Roadblock Complete (Executed)',
        geometry: 'Line',
        code: '271204',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Assault Crossing',
        geometry: 'Line',
        code: '271300',
    },
    { entity: 'Protection Areas', entityType: 'Ford Easy', geometry: 'Line', code: '271500' },
    { entity: 'Protection Areas', entityType: 'Ford Difficult', geometry: 'Line', code: '271600' },
    {
        entity: 'Protection Areas',
        entityType: 'Biological Contaminated Area',
        geometry: 'Area',
        code: '271700',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Chemical Contaminated Area',
        geometry: 'Area',
        code: '271800',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Nuclear Contaminated Area',
        geometry: 'Area',
        code: '271900',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Radiological Contaminated Area',
        geometry: 'Area',
        code: '272000',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Minimum Safe Distance Zone',
        geometry: 'Area',
        code: '272100',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Multiple Strikes – STRIKWARN',
        geometry: 'Area',
        code: '272101',
    },
    {
        entity: 'Protection Areas',
        entityType: 'Radiation Dose Rate Contour Lines',
        geometry: 'Area',
        code: '272200',
    },
    { entity: 'Protection Points', code: '280000' },
    { entity: 'Protection Points', entityType: 'Abatis', geometry: 'Line', code: '280100' },
    {
        entity: 'Protection Points',
        entityType: 'Antipersonnel Mine',
        geometry: 'Point',
        code: '280200',
    },
    {
        entity: 'Protection Points',
        entityType: 'Antipersonnel Mine',
        entitySubtype: 'Antipersonnel Mine with Directional Effects',
        geometry: 'Point',
        code: '280201',
    },
    { entity: 'Protection Points', entityType: 'Antitank Mine', geometry: 'Point', code: '280300' },
    {
        entity: 'Protection Points',
        entityType: 'Antitank Mine with Anti-handling Device',
        geometry: 'Point',
        code: '280400',
    },
    {
        entity: 'Protection Points',
        entityType: 'Wide Area Antitank Mine',
        geometry: 'Point',
        code: '280500',
    },
    {
        entity: 'Protection Points',
        entityType: 'Unspecified Mine',
        geometry: 'Point',
        code: '280600',
    },
    { entity: 'Protection Points', entityType: 'Booby Trap', geometry: 'Point', code: '280700' },
    {
        entity: 'Protection Points',
        entityType: 'Engineer Regulating Point',
        geometry: 'Point',
        code: '280800',
    },
    { entity: 'Protection Points', entityType: 'Shelter', geometry: 'Point', code: '280900' },
    {
        entity: 'Protection Points',
        entityType: 'Shelter Above Ground',
        geometry: 'Point',
        code: '281000',
    },
    {
        entity: 'Protection Points',
        entityType: 'Below Ground Shelter',
        geometry: 'Point',
        code: '281100',
    },
    { entity: 'Protection Points', entityType: 'Fort', geometry: 'Point', code: '281200' },
    {
        entity: 'Protection Points',
        entityType: 'Chemical Event',
        geometry: 'Point',
        code: '281300',
    },
    {
        entity: 'Protection Points',
        entityType: 'CHEMICAL – TOXIC INDUSTRIAL MATERIAL',
        geometry: 'Point',
        code: '281301',
    },
    {
        entity: 'Protection Points',
        entityType: 'Biological Event',
        geometry: 'Point',
        code: '281400',
    },
    { entity: 'Protection Points', entityType: 'Nuclear Event', geometry: 'Point', code: '281500' },
    {
        entity: 'Protection Points',
        entityType: 'Nuclear Fallout Producing Event',
        geometry: 'Point',
        code: '281600',
    },
    { entity: 'Protection Points', entityType: 'Radiological', geometry: 'Point', code: '281700' },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        geometry: 'Point',
        code: '281800',
    },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        entitySubtype: 'Alternate',
        geometry: 'Point',
        code: '281801',
    },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        entitySubtype: 'Asset',
        geometry: 'Point',
        code: '281802',
    },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        entitySubtype: 'Troop',
        geometry: 'Point',
        code: '281803',
    },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        entitySubtype: 'Asset/Troop',
        geometry: 'Point',
        code: '281804',
    },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        entitySubtype: 'Operational',
        geometry: 'Point',
        code: '281805',
    },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        entitySubtype: 'Thorough',
        geometry: 'Point',
        code: '281806',
    },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        entitySubtype: 'Main Asset',
        geometry: 'Point',
        code: '281807',
    },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        entitySubtype: 'Forward Troop',
        geometry: 'Point',
        code: '281808',
    },
    {
        entity: 'Protection Points',
        entityType: 'General Decontamination Point/Site',
        entitySubtype: 'Wounded Personnel',
        geometry: 'Point',
        code: '281809',
    },
    {
        entity: 'Protection Points',
        entityType: 'Tetrahedrons, Dragons Teeth, and Other Similar Obstacles',
        code: '281900',
    },
    {
        entity: 'Protection Points',
        entityType: 'Tetrahedrons, Dragons Teeth, and Other Similar Obstacles',
        entitySubtype: 'Fixed and Prefabricated',
        geometry: 'Point',
        code: '281901',
    },
    {
        entity: 'Protection Points',
        entityType: 'Tetrahedrons, Dragons Teeth, and Other Similar Obstacles',
        entitySubtype: 'Movable',
        geometry: 'Point',
        code: '281902',
    },
    {
        entity: 'Protection Points',
        entityType: 'Tetrahedrons, Dragons Teeth, and Other Similar Obstacles',
        entitySubtype: 'Movable and Prefabricated',
        geometry: 'Point',
        code: '281903',
    },
    { entity: 'Protection Points', entityType: 'Vertical Obstructions', code: '282000' },
    {
        entity: 'Protection Points',
        entityType: 'Vertical Obstructions',
        entitySubtype: 'Tower, Low',
        geometry: 'Point',
        code: '282001',
    },
    {
        entity: 'Protection Points',
        entityType: 'Vertical Obstructions',
        entitySubtype: 'Tower, High',
        geometry: 'Point',
        code: '282002',
    },
    {
        entity: 'Protection Points',
        entityType: 'Vertical Obstructions',
        entitySubtype: 'Overhead Wire',
        geometry: 'Line',
        code: '282003',
    },
    { entity: 'Protection Lines', code: '290000' },
    { entity: 'Protection Lines', entityType: 'Obstacle Line', geometry: 'Line', code: '290100' },
    {
        entity: 'Protection Lines',
        entityType: 'Obstacle Line',
        entitySubtype: 'Mineline',
        geometry: 'Line',
        code: '290101',
    },
    { entity: 'Protection Lines', entityType: 'Antitank Obstacles', code: '290200' },
    {
        entity: 'Protection Lines',
        entityType: 'Antitank Obstacles',
        entitySubtype: 'Under Construction',
        geometry: 'Line',
        code: '290201',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Antitank Obstacles',
        entitySubtype: 'Completed',
        geometry: 'Line',
        code: '290202',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Antitank Obstacles',
        entitySubtype: 'Reinforced, with Antitank Mines',
        geometry: 'Line',
        code: '290203',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Antitank Obstacles',
        entitySubtype: 'Antitank Wall',
        geometry: 'Line',
        code: '290204',
    },
    { entity: 'Protection Lines', entityType: 'Wire Obstacles', code: '290300' },
    {
        entity: 'Protection Lines',
        entityType: 'Wire Obstacles',
        entitySubtype: 'Unspecified Wire',
        geometry: 'Line',
        code: '290301',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Wire Obstacles',
        entitySubtype: 'Single Fence Wire',
        geometry: 'Line',
        code: '290302',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Wire Obstacles',
        entitySubtype: 'Double Fence Wire',
        geometry: 'Line',
        code: '290303',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Wire Obstacles',
        entitySubtype: 'Double Apron Fence',
        geometry: 'Line',
        code: '290304',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Wire Obstacles',
        entitySubtype: 'Low Wire Fence',
        geometry: 'Line',
        code: '290305',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Wire Obstacles',
        entitySubtype: 'High Wire Fence',
        geometry: 'Line',
        code: '290306',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Wire Obstacles',
        entitySubtype: 'Single Concertina',
        geometry: 'Line',
        code: '290307',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Wire Obstacles',
        entitySubtype: 'Double Strand Concertina',
        geometry: 'Line',
        code: '290308',
    },
    {
        entity: 'Protection Lines',
        entityType: 'Wire Obstacles',
        entitySubtype: 'Triple Strand Concertina',
        geometry: 'Line',
        code: '290309',
    },
    { entity: 'Protection Lines', entityType: 'Mine Cluster', geometry: 'Area', code: '290400' },
    { entity: 'Protection Lines', entityType: 'Trip Wire', geometry: 'Line', code: '290500' },
    { entity: 'Protection Lines', entityType: 'Lane', geometry: 'Line', code: '290600' },
    { entity: 'Protection Lines', entityType: 'Ferry', geometry: 'Line', code: '290700' },
    { entity: 'Protection Lines', entityType: 'Raft Site', geometry: 'Line', code: '290800' },
    { entity: 'Protection Lines', entityType: 'Fortified Line', geometry: 'Line', code: '290900' },
    {
        entity: 'Protection Lines',
        entityType: 'Fighting Position',
        geometry: 'Line',
        code: '291000',
    },
    { entity: 'Intelligence Lines', code: '300000' },
    {
        entity: 'Intelligence Lines',
        entityType: 'Intelligence Coordination Line',
        geometry: 'Line',
        code: '300100',
    },
    { entity: 'Sustainment Areas', code: '310000' },
    {
        entity: 'Sustainment Areas',
        entityType: 'Detainee Holding Area',
        geometry: 'Area',
        code: '310100',
    },
    {
        entity: 'Sustainment Areas',
        entityType: 'Enemy Prisoner or War Holding Area',
        geometry: 'Area',
        code: '310200',
    },
    {
        entity: 'Sustainment Areas',
        entityType: 'Forward Arming and Refueling Point',
        geometry: 'Area',
        code: '310300',
    },
    {
        entity: 'Sustainment Areas',
        entityType: 'Refugee Holding Area',
        geometry: 'Area',
        code: '310400',
    },
    {
        entity: 'Sustainment Areas',
        entityType: 'Regimental Support Area',
        geometry: 'Area',
        code: '310500',
    },
    {
        entity: 'Sustainment Areas',
        entityType: 'Brigade Support Area',
        geometry: 'Area',
        code: '310600',
    },
    {
        entity: 'Sustainment Areas',
        entityType: 'Division Support Area',
        geometry: 'Area',
        code: '310700',
    },
    { entity: 'Sustainment Points', code: '320000' },
    { entity: 'Sustainment Points', entityType: 'Ambulance Points', code: '320100' },
    {
        entity: 'Sustainment Points',
        entityType: 'Ambulance Points',
        entitySubtype: 'Ambulance Exchange Point',
        geometry: 'Point',
        code: '320101',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Ambulance Points',
        entitySubtype: 'Ambulance Control Point',
        geometry: 'Point',
        code: '320102',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Ambulance Points',
        entitySubtype: 'Ambulance Load Point',
        geometry: 'Point',
        code: '320103',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Ambulance Points',
        entitySubtype: 'Ambulance Relay Point',
        geometry: 'Point',
        code: '320104',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Ammunition Supply Point',
        geometry: 'Point',
        code: '320200',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Ammunition Transfer and Holding Point',
        geometry: 'Point',
        code: '320300',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Cannibalization Point',
        geometry: 'Point',
        code: '320400',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Casualty Collection Point',
        geometry: 'Point',
        code: '320500',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Civilian Collection Point',
        geometry: 'Point',
        code: '320600',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Detainee Collection Point',
        geometry: 'Point',
        code: '320700',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Enemy Prisoner of War Collection Point',
        geometry: 'Point',
        code: '320800',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Logistics Release Point',
        geometry: 'Point',
        code: '320900',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Maintenance Collection Point (MCP)',
        geometry: 'Point',
        code: '321000',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Medical Evacuation Point (MEDEVAC) Pick-Up Point',
        geometry: 'Point',
        code: '321100',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Rearm, Refuel and Resupply Point (R3P)',
        geometry: 'Point',
        code: '321200',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Refuel on the Move (ROM) Point',
        geometry: 'Point',
        code: '321300',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Traffic Control Post (TCP)',
        geometry: 'Point',
        code: '321400',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Trailer Transfer Point (TTP)',
        geometry: 'Point',
        code: '321500',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Unit Maintenance Collection Point (UNCP)',
        geometry: 'Point',
        code: '321600',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        geometry: 'Point',
        code: '321700',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'NATO Class I Supply Point',
        geometry: 'Point',
        code: '321701',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'NATO Class II Supply Point',
        geometry: 'Point',
        code: '321702',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'NATO Class III Supply Point',
        geometry: 'Point',
        code: '321703',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'NATO Class IV Supply Point',
        geometry: 'Point',
        code: '321704',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'NATO Class V Supply Point',
        geometry: 'Point',
        code: '321705',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'NATO Multiple Class Supply Point',
        geometry: 'Point',
        code: '321706',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class I Supply Point',
        geometry: 'Point',
        code: '321707',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class II Supply Point',
        geometry: 'Point',
        code: '321708',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class III Supply Point',
        geometry: 'Point',
        code: '321709',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class IV Supply Point',
        geometry: 'Point',
        code: '321710',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class V Supply Point',
        geometry: 'Point',
        code: '321711',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class VI Supply Point',
        geometry: 'Point',
        code: '321712',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class VII Supply Point',
        geometry: 'Point',
        code: '321713',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class VIII Supply Point',
        geometry: 'Point',
        code: '321714',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class IX Supply Point',
        geometry: 'Point',
        code: '321715',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'General Supply Point',
        entitySubtype: 'US Class X Supply Point',
        geometry: 'Point',
        code: '321716',
    },
    {
        entity: 'Sustainment Points',
        entityType: 'Medical Supply Point',
        geometry: 'Point',
        code: '321800',
    },
    { entity: 'Sustainment Lines', code: '330000' },
    { entity: 'Sustainment Lines', entityType: 'Moving Convoy', geometry: 'Line', code: '330100' },
    { entity: 'Sustainment Lines', entityType: 'Halted Convoy', geometry: 'Line', code: '330200' },
    {
        entity: 'Sustainment Lines',
        entityType: 'Main Supply Route',
        geometry: 'Line',
        code: '330300',
    },
    {
        entity: 'Sustainment Lines',
        entityType: 'Main Supply Route',
        entitySubtype: 'One Way Traffic',
        geometry: 'Line',
        code: '330301',
    },
    {
        entity: 'Sustainment Lines',
        entityType: 'Main Supply Route',
        entitySubtype: 'Two Way Traffic',
        geometry: 'Line',
        code: '330302',
    },
    {
        entity: 'Sustainment Lines',
        entityType: 'Main Supply Route',
        entitySubtype: 'Alternating Traffic',
        geometry: 'Line',
        code: '330303',
    },
    {
        entity: 'Sustainment Lines',
        entityType: 'Alternate Supply Route',
        geometry: 'Line',
        code: '330400',
    },
    {
        entity: 'Sustainment Lines',
        entityType: 'Alternate Supply Route',
        entitySubtype: 'One Way Traffic',
        geometry: 'Line',
        code: '330401',
    },
    {
        entity: 'Sustainment Lines',
        entityType: 'Alternate Supply Route',
        entitySubtype: 'Two Way Traffic',
        geometry: 'Line',
        code: '330402',
    },
    {
        entity: 'Sustainment Lines',
        entityType: 'Alternate Supply Route',
        entitySubtype: 'Alternating Traffic',
        geometry: 'Line',
        code: '330403',
    },
    { entity: 'Mission Tasks', code: '340000' },
    { entity: 'Mission Tasks', entityType: 'Block', geometry: 'Area', code: '340100' },
    { entity: 'Mission Tasks', entityType: 'Breach', geometry: 'Line', code: '340200' },
    { entity: 'Mission Tasks', entityType: 'Bypass', geometry: 'MultiPoint', code: '340300' },
    { entity: 'Mission Tasks', entityType: 'Canalize', geometry: 'MultiPoint', code: '340400' },
    { entity: 'Mission Tasks', entityType: 'Clear', geometry: 'Line', code: '340500' },
    { entity: 'Mission Tasks', entityType: 'Counterattack', geometry: 'Line', code: '340600' },
    {
        entity: 'Mission Tasks',
        entityType: 'Counterattack by Fire',
        geometry: 'Line',
        code: '340700',
    },
    { entity: 'Mission Tasks', entityType: 'Delay', geometry: 'Line', code: '340800' },
    { entity: 'Mission Tasks', entityType: 'Destroy', geometry: 'Point', code: '340900' },
    { entity: 'Mission Tasks', entityType: 'Disrupt', geometry: 'Area', code: '341000' },
    { entity: 'Mission Tasks', entityType: 'Fix', geometry: 'Line', code: '341100' },
    { entity: 'Mission Tasks', entityType: 'Follow and Assume', geometry: 'Line', code: '341200' },
    { entity: 'Mission Tasks', entityType: 'Follow and Support', geometry: 'Line', code: '341300' },
    { entity: 'Mission Tasks', entityType: 'Interdict', geometry: 'Point', code: '341400' },
    { entity: 'Mission Tasks', entityType: 'Isolate', geometry: 'Area', code: '341500' },
    { entity: 'Mission Tasks', entityType: 'Neutralize', geometry: 'Point', code: '341600' },
    { entity: 'Mission Tasks', entityType: 'Occupy', geometry: 'Area', code: '341700' },
    { entity: 'Mission Tasks', entityType: 'Penetrate', geometry: 'Area', code: '341800' },
    {
        entity: 'Mission Tasks',
        entityType: 'Relief in Place (RIP)',
        geometry: 'Area',
        code: '341900',
    },
    { entity: 'Mission Tasks', entityType: 'Retire/Retirement', geometry: 'Line', code: '342000' },
    { entity: 'Mission Tasks', entityType: 'Secure', geometry: 'Area', code: '342100' },
    { entity: 'Mission Tasks', entityType: 'Security', code: '342200' },
    {
        entity: 'Mission Tasks',
        entityType: 'Security',
        entitySubtype: 'Cover',
        geometry: 'Line',
        code: '342201',
    },
    {
        entity: 'Mission Tasks',
        entityType: 'Security',
        entitySubtype: 'Guard',
        geometry: 'Line',
        code: '342202',
    },
    {
        entity: 'Mission Tasks',
        entityType: 'Security',
        entitySubtype: 'Screen',
        geometry: 'Line',
        code: '342203',
    },
    { entity: 'Mission Tasks', entityType: 'Seize', geometry: 'Line', code: '342300' },
    { entity: 'Mission Tasks', entityType: 'Withdraw', geometry: 'Line', code: '342400' },
    {
        entity: 'Mission Tasks',
        entityType: 'Withdraw Under Pressure',
        geometry: 'Line',
        code: '342500',
    },
    { entity: 'Space Debris', code: '350000' },
] as const;
export type ControlmeasureIcon = (typeof controlMeasureIconValues)[number];

export const controlmeasureModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Unspecified Mine', category: 'Obstacles', code: '13' },
    { modifier: 'Antipersonnel Mine', category: 'Obstacles', code: '14' },
    { modifier: 'Antipersonnel Mine with Directional Effects', category: 'Obstacles', code: '15' },
    { modifier: 'Antitank Mine', category: 'Obstacles', code: '16' },
    { modifier: 'Antitank Mine with Antihandling Device', category: 'Obstacles', code: '17' },
    { modifier: 'Wide Area Antitank Mine', category: 'Obstacles', code: '18' },
    { modifier: 'Mine Cluster', category: 'Obstacles', code: '19' },
    {
        modifier: 'Antipersonnel Mine and Antipersonnel Mine with Directional Effects',
        category: 'Obstacles',
        code: '20',
    },
    { modifier: 'Antipersonnel Mine and Antitank Mine', category: 'Obstacles', code: '21' },
    {
        modifier: 'Antipersonnel Mine and Antitank Mine with Antihandling Device',
        category: 'Obstacles',
        code: '22',
    },
    {
        modifier: 'Antipersonnel Mine and Wide Area Antitank Mine',
        category: 'Obstacles',
        code: '23',
    },
    { modifier: 'Antipersonnel Mine and Mine Cluster', category: 'Obstacles', code: '24' },
    {
        modifier: 'Antipersonnel Mine with Directional Effects and Antitank Mine',
        category: 'Obstacles',
        code: '25',
    },
    {
        modifier:
            'Antipersonnel Mine with Directional Effects and Antitank Mine with Antihandling Device',
        category: 'Obstacles',
        code: '26',
    },
    {
        modifier: 'Antipersonnel Mine with Directional Effects and Wide Area Antitank Mine',
        category: 'Obstacles',
        code: '27',
    },
    {
        modifier: 'Antipersonnel Mine with Directional Effects and Mine Cluster',
        category: 'Obstacles',
        code: '28',
    },
    {
        modifier: 'Antitank Mine and Antitank Mine with Antihandling Device',
        category: 'Obstacles',
        code: '29',
    },
    { modifier: 'Antitank Mine and Wide Area Antitank Mine', category: 'Obstacles', code: '30' },
    { modifier: 'Antitank Mine and Mine Cluster', category: 'Obstacles', code: '31' },
    {
        modifier: 'Antitank Mine with Antihandling Device and Wide Area Antitank Mine',
        category: 'Obstacles',
        code: '32',
    },
    {
        modifier: 'Antitank Mine with Antihandling Device and Mine Cluster',
        category: 'Obstacles',
        code: '33',
    },
    { modifier: 'Wide Area Antitank Mine and Mine Cluster', category: 'Obstacles', code: '34' },
    {
        modifier:
            'Antipersonnel Mine, Antipersonnel Mine with Directional Effects, and Antitank Mine',
        category: 'Obstacles',
        code: '35',
    },
    {
        modifier:
            'Antipersonnel Mine, Antipersonnel Mine with Directional Effects, and Antitank Mine with Antihandling Device',
        category: 'Obstacles',
        code: '36',
    },
    {
        modifier:
            'Antipersonnel Mine, Antipersonnel Mine with Directional Effects, and Wide Area Antitank Mine',
        category: 'Obstacles',
        code: '37',
    },
    {
        modifier:
            'Antipersonnel Mine, Antipersonnel Mine with Directional Effects, and Mine Cluster',
        category: 'Obstacles',
        code: '38',
    },
    {
        modifier: 'Antipersonnel Mine, Antitank Mine, and Antitank Mine with Antihandling Device',
        category: 'Obstacles',
        code: '39',
    },
    {
        modifier: 'Antipersonnel Mine, Antitank Mine, and Wide Area Antitank Mine',
        category: 'Obstacles',
        code: '40',
    },
    {
        modifier: 'Antipersonnel Mine, Antitank Mine, and Mine Cluster',
        category: 'Obstacles',
        code: '41',
    },
    {
        modifier:
            'Antipersonnel Mine, Antitank Mine with Antihandling Device, and Wide Area Antitank Mine',
        category: 'Obstacles',
        code: '42',
    },
    {
        modifier: 'Antipersonnel Mine, Antitank Mine with Antihandling Device, and Mine Cluster',
        category: 'Obstacles',
        code: '43',
    },
    {
        modifier: 'Antipersonnel Mine, Wide Area Antitank Mine, and Mine Cluster',
        category: 'Obstacles',
        code: '44',
    },
    {
        modifier:
            'Antipersonnel Mine with Directional Effects, Antitank Mine, and Antitank Mine with Antihandling Device',
        category: 'Obstacles',
        code: '45',
    },
    {
        modifier:
            'Antipersonnel Mine with Directional Effects, Antitank Mine, and Wide Area Antitank Mine',
        category: 'Obstacles',
        code: '46',
    },
    {
        modifier: 'Antipersonnel Mine with Directional Effects, Antitank Mine, and Mine Cluster',
        category: 'Obstacles',
        code: '47',
    },
    {
        modifier:
            'Antipersonnel Mine with Directional Effects, Antitank Mine with Antihandling Device, and Wide Area Antitank Mine',
        category: 'Obstacles',
        code: '48',
    },
    {
        modifier:
            'Antipersonnel Mine with Directional Effects, Antitank Mine with Antihandling Device, and Mine Cluster',
        category: 'Obstacles',
        code: '49',
    },
    {
        modifier:
            'Antipersonnel Mine with Directional Effects, Wide Area Antitank Mine, and Mine Cluster',
        category: 'Obstacles',
        code: '50',
    },
] as const;
export type ControlmeasureModifier1 = (typeof controlmeasureModifier1Values)[number];

export const seaSurfaceIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Military', code: '110000' },
    { entity: 'Military Combatant', code: '120000' },
    { entity: 'Military Combatant', entityType: 'Carrier', code: '120100' },
    { entity: 'Military Combatant', entityType: 'Surface Combatant, Line', code: '120200' },
    {
        entity: 'Military Combatant',
        entityType: 'Surface Combatant, Line',
        entitySubtype: 'Battleship',
        code: '120201',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Surface Combatant, Line',
        entitySubtype: 'Cruiser',
        code: '120202',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Surface Combatant, Line',
        entitySubtype: 'Destroyer',
        code: '120203',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Surface Combatant, Line',
        entitySubtype: 'Frigate',
        code: '120204',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Surface Combatant, Line',
        entitySubtype: 'Corvette',
        code: '120205',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Surface Combatant, Line',
        entitySubtype: 'Littoral Combatant Ship',
        code: '120206',
    },
    { entity: 'Military Combatant', entityType: 'Amphibious Warfare Ship', code: '120300' },
    {
        entity: 'Military Combatant',
        entityType: 'Amphibious Warfare Ship',
        entitySubtype: 'Amphibious Command Ship',
        code: '120301',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Amphibious Warfare Ship',
        entitySubtype: 'Amphibious Assault, Non-specified',
        code: '120302',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Amphibious Warfare Ship',
        entitySubtype: 'Amphibious Assault Ship, General',
        code: '120303',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Amphibious Warfare Ship',
        entitySubtype: 'Amphibious Assault Ship, Multipurpose',
        code: '120304',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Amphibious Warfare Ship',
        entitySubtype: 'Amphibious Assault Ship, Helicopter',
        code: '120305',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Amphibious Warfare Ship',
        entitySubtype: 'Amphibious Transport Dock',
        code: '120306',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Amphibious Warfare Ship',
        entitySubtype: 'Landing Ship',
        code: '120307',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Amphibious Warfare Ship',
        entitySubtype: 'Landing Craft',
        code: '120308',
    },
    { entity: 'Military Combatant', entityType: 'Mine Warfare Ship', code: '120400' },
    {
        entity: 'Military Combatant',
        entityType: 'Mine Warfare Ship',
        entitySubtype: 'Mine Layer',
        code: '120401',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Mine Warfare Ship',
        entitySubtype: 'Mine Sweeper',
        code: '120402',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Mine Warfare Ship',
        entitySubtype: 'Mine Sweeper, Drone',
        code: '120403',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Mine Warfare Ship',
        entitySubtype: 'Mine Hunter',
        code: '120404',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Mine Warfare Ship',
        entitySubtype: 'Mine Countermeasures',
        code: '120405',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Mine Warfare Ship',
        entitySubtype: 'Mine Countermeasures, Support Ship',
        code: '120406',
    },
    { entity: 'Military Combatant', entityType: 'Patrol Boat', code: '120500' },
    {
        entity: 'Military Combatant',
        entityType: 'Patrol Boat',
        entitySubtype: 'Patrol Craft, Submarine Chaser/Escort, General',
        code: '120501',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Patrol Boat',
        entitySubtype: 'Patrol Ship, General',
        code: '120502',
    },
    { entity: 'Military Combatant', entityType: 'Decoy', code: '120600' },
    {
        entity: 'Military Combatant',
        entityType: 'Unmanned Surface Water Vehicle (USV)',
        code: '120700',
    },
    { entity: 'Military Combatant', entityType: 'Speedboat', code: '120800' },
    {
        entity: 'Military Combatant',
        entityType: 'Speedboat',
        entitySubtype: 'Rigid–Hull Inflatable Boat (RHIB)',
        code: '120801',
    },
    { entity: 'Military Combatant', entityType: 'Jet Ski', code: '120900' },
    { entity: 'Military Combatant', entityType: 'Navy Task Organization', code: '121000' },
    {
        entity: 'Military Combatant',
        entityType: 'Navy Task Organization',
        entitySubtype: 'Navy Task Element',
        code: '121001',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Navy Task Organization',
        entitySubtype: 'Navy Task Force',
        code: '121002',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Navy Task Organization',
        entitySubtype: 'Navy Task Group',
        code: '121003',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Navy Task Organization',
        entitySubtype: 'Navy Task Unit',
        code: '121004',
    },
    {
        entity: 'Military Combatant',
        entityType: 'Navy Task Organization',
        entitySubtype: 'Convoy',
        code: '121005',
    },
    { entity: 'Military Combatant', entityType: 'Sea-Based X-Band Radar', code: '121100' },
    { entity: 'Military Non Combatant', code: '130000' },
    { entity: 'Military Non Combatant', entityType: 'Auxiliary Ship', code: '130100' },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Ammunition Ship',
        code: '130101',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Naval Stores Ship',
        code: '130102',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Auxiliary Flag Ship',
        code: '130103',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Intelligence Collector',
        code: '130104',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Oceanographic Research Ship',
        code: '130105',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Survey Ship',
        code: '130106',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Hospital Ship',
        code: '130107',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Naval Cargo Ship',
        code: '130108',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Combat Support Ship, Fast',
        code: '130109',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Oiler, Replenishment',
        code: '130110',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Repair Ship',
        code: '130111',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Submarine Tender',
        code: '130112',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Auxiliary Ship',
        entitySubtype: 'Tug, Ocean Going',
        code: '130113',
    },
    { entity: 'Military Non Combatant', entityType: 'Service Craft/Yard', code: '130200' },
    {
        entity: 'Military Non Combatant',
        entityType: 'Service Craft/Yard',
        entitySubtype: 'Barge, not Self–Propelled',
        code: '130201',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Service Craft/Yard',
        entitySubtype: 'Barge, Self–Propelled',
        code: '130202',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Service Craft/Yard',
        entitySubtype: 'Tug, Harbor',
        code: '130203',
    },
    {
        entity: 'Military Non Combatant',
        entityType: 'Service Craft/Yard',
        entitySubtype: 'Launch',
        code: '130204',
    },
    { entity: 'Civilian', code: '140000' },
    { entity: 'Civilian', entityType: 'Merchant Ship', code: '140100' },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Cargo, General',
        code: '140101',
    },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Container Ship',
        code: '140102',
    },
    { entity: 'Civilian', entityType: 'Merchant Ship', entitySubtype: 'Dredge', code: '140103' },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Roll On/Roll Off',
        code: '140104',
    },
    { entity: 'Civilian', entityType: 'Merchant Ship', entitySubtype: 'Ferry', code: '140105' },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Heavy Lift',
        code: '140106',
    },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Hovercraft',
        code: '140107',
    },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Lash Carrier (with Barges)',
        code: '140108',
    },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Oiler/Tanker',
        code: '140109',
    },
    { entity: 'Civilian', entityType: 'Merchant Ship', entitySubtype: 'Passenger', code: '140110' },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Tug, Ocean Going',
        code: '140111',
    },
    { entity: 'Civilian', entityType: 'Merchant Ship', entitySubtype: 'Tow', code: '140112' },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Transport Ship, Hazardous Material',
        code: '140113',
    },
    { entity: 'Civilian', entityType: 'Merchant Ship', entitySubtype: 'Junk/Dhow', code: '140114' },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Barge, not Self–Propelled',
        code: '140115',
    },
    {
        entity: 'Civilian',
        entityType: 'Merchant Ship',
        entitySubtype: 'Hospital Ship',
        code: '140116',
    },
    { entity: 'Civilian', entityType: 'Fishing Vessel', code: '140200' },
    { entity: 'Civilian', entityType: 'Fishing Vessel', entitySubtype: 'Drifter', code: '140201' },
    { entity: 'Civilian', entityType: 'Fishing Vessel', entitySubtype: 'Trawler', code: '140202' },
    { entity: 'Civilian', entityType: 'Fishing Vessel', entitySubtype: 'Dredger', code: '140203' },
    { entity: 'Civilian', entityType: 'Law Enforcement Vessel', code: '140300' },
    { entity: 'Civilian', entityType: 'Leisure Craft, Sailing', code: '140400' },
    { entity: 'Civilian', entityType: 'Leisure Craft, Motorized', code: '140500' },
    {
        entity: 'Civilian',
        entityType: 'Leisure Craft, Motorized',
        entitySubtype: 'Rigid–Hull Inflatable Boat (RHIB)',
        code: '140501',
    },
    {
        entity: 'Civilian',
        entityType: 'Leisure Craft, Motorized',
        entitySubtype: 'Speedboat',
        code: '140502',
    },
    { entity: 'Civilian', entityType: 'Jet Ski', code: '140600' },
    { entity: 'Civilian', entityType: 'Unmanned Surface Water Vehicle (USV)', code: '140700' },
    { entity: 'Own Ship', code: '150000' },
    { entity: 'Fused Track', code: '160000' },
    { entity: 'Manual Track', code: '170000' },
] as const;
export type SeasurfaceIcon = (typeof seaSurfaceIconValues)[number];

export const seasurfaceModifier1Values = [
    { modifier: 'Unspecified', category: 'Mission Area', code: '00' },
    { modifier: 'Own Ship', category: 'Mission Area', code: '01' },
    { modifier: 'Antiair Warfare', category: 'Mission Area', code: '02' },
    { modifier: 'Antisubmarine Warfare', category: 'Mission Area', code: '03' },
    { modifier: 'Escort', category: 'Mission Area', code: '04' },
    { modifier: 'Electronic Warfare', category: 'Mission Area', code: '05' },
    {
        modifier: 'Intelligence, Surveillance, Reconnaissance',
        category: 'Mission Area',
        code: '06',
    },
    { modifier: 'Mine Countermeasures', category: 'Mission Area', code: '07' },
    { modifier: 'Missile Defense', category: 'Mission Area', code: '08' },
    { modifier: 'Medical', category: 'Mission Area', code: '09' },
    { modifier: 'Mine Warfare', category: 'Mission Area', code: '10' },
    { modifier: 'Remote Multimission Vehicle (RMV)', category: 'Mission Area', code: '11' },
    { modifier: 'Special Operations Forces (SOF)', category: 'Asset Capability', code: '12' },
    { modifier: 'Surface Warfare', category: 'Mission Area', code: '13' },
    { modifier: 'Ballistic Missile', category: 'Weapons Capability', code: '14' },
    { modifier: 'Guided Missile', category: 'Weapons Capability', code: '15' },
    { modifier: 'Other Guided Missile', category: 'Weapons Capability', code: '16' },
    { modifier: 'Torpedo', category: 'Weapons Capability', code: '17' },
    { modifier: 'Drone–Equipped', category: 'Asset Capability', code: '18' },
    { modifier: 'Helicopter–Equipped/VSTOL', category: 'Asset Capability', code: '19' },
    { modifier: 'Ballistic Missile Defense, Shooter', category: 'Mission Area', code: '20' },
    {
        modifier: 'Ballistic Missile Defense, Long-Range Surveillance and Track (LRS&T)',
        category: 'Mission Area',
        code: '21',
    },
    { modifier: 'Sea-Base X-Band', category: 'Mission Area', code: '22' },
    { modifier: 'Hijacking/Hijacked', category: 'Crime', code: '23' },
    { modifier: 'Hijacker', category: 'Crime', code: '24' },
    { modifier: 'Cyberspace', category: 'Capability', code: '25' },
] as const;
export type SeasurfaceModifier1 = (typeof seasurfaceModifier1Values)[number];

export const seasurfaceModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Nuclear Powered', category: 'Ship Propulsion', code: '01' },
    { modifier: 'Heavy', category: 'Ship Capacity', code: '02' },
    { modifier: 'Light', category: 'Ship Capacity', code: '03' },
    { modifier: 'Medium', category: 'Ship Capacity', code: '04' },
    { modifier: 'Dock', category: 'Cargo Capacity', code: '05' },
    { modifier: 'Logistics', category: 'Cargo Capacity', code: '06' },
    { modifier: 'Tank', category: 'Cargo Capacity', code: '07' },
    { modifier: 'Vehicle', category: 'Cargo Capacity', code: '08' },
    { modifier: 'Fast', category: 'Ship Mobility', code: '09' },
    { modifier: 'Air–Cushioned (US)', category: 'Ship Mobility', code: '10' },
    { modifier: 'Air–Cushioned (NATO)', category: 'Ship Mobility', code: '11' },
    { modifier: 'Hydrofoil', category: 'Ship Mobility', code: '12' },
    { modifier: 'Autonomous Control', category: 'USV Control', code: '13' },
    { modifier: 'Remotely Piloted', category: 'USV Control', code: '14' },
    { modifier: 'Expendable', category: 'USV Control', code: '15' },
    { modifier: 'Cyberspace', category: 'Capability', code: '16' },
] as const;
export type SeasurfaceModifier2 = (typeof seasurfaceModifier2Values)[number];

export const seaSubsurfaceIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Military', code: '110000' },
    { entity: 'Military', entityType: 'Submarine', code: '110100' },
    {
        entity: 'Military',
        entityType: 'Submarine',
        entitySubtype: 'Submarine, Surfaced',
        code: '110101',
    },
    {
        entity: 'Military',
        entityType: 'Submarine',
        entitySubtype: 'Submarine, Snorkeling',
        code: '110102',
    },
    {
        entity: 'Military',
        entityType: 'Submarine',
        entitySubtype: 'Submarine, Bottomed',
        code: '110103',
    },
    { entity: 'Military', entityType: 'Other Submersible', code: '110200' },
    { entity: 'Military', entityType: 'Nonsubmarine', code: '110300' },
    {
        entity: 'Military',
        entityType: 'Autonomous Underwater Vehicle (AUV)/Unmanned Underwater Vehicle (UUV)',
        code: '110400',
    },
    { entity: 'Military', entityType: 'Diver', code: '110500' },
    { entity: 'Civilian', code: '120000' },
    { entity: 'Civilian', entityType: 'Submersible', code: '120100' },
    {
        entity: 'Civilian',
        entityType: 'Autonomous Underwater Vehicle (AUV)/ Unmanned Underwater Vehicle (UUV)',
        code: '120200',
    },
    { entity: 'Civilian', entityType: 'Diver', code: '120300' },
    { entity: 'Weapon', code: '130000' },
    { entity: 'Weapon', entityType: 'Torpedo', code: '130100' },
    { entity: 'Weapon', entityType: 'Improvised Explosive Device (IED)', code: '130200' },
    { entity: 'Weapon', entityType: 'Decoy', code: '130300' },
    { entity: 'Echo Tracker Classifier (ETC) / Possible Contact (POSCON)', code: '140000' },
    { entity: 'Fused Track', code: '150000' },
    { entity: 'Manual Track', code: '160000' },
] as const;
export type SeasubsurfaceIcon = (typeof seaSubsurfaceIconValues)[number];

export const seasubsurfaceModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Antisubmarine Warfare', category: 'Mission Area', code: '01' },
    { modifier: 'Auxiliary', category: 'Mission Area', code: '02' },
    { modifier: 'Command and Control', category: 'Mission Area', code: '03' },
    {
        modifier: 'Intelligence, Surveillance, Reconnaissance',
        category: 'Mission Area',
        code: '04',
    },
    { modifier: 'Mine Countermeasures', category: 'Mission Area', code: '05' },
    { modifier: 'Mine Warfare', category: 'Mission Area', code: '06' },
    { modifier: 'Surface Warfare', category: 'Mission Area', code: '07' },
    { modifier: 'Attack', category: 'Weapons Capability', code: '08' },
    { modifier: 'Ballistic Missile', category: 'Weapons Capability', code: '09' },
    { modifier: 'Guided Missile', category: 'Weapons Capability', code: '10' },
    { modifier: 'Other Guided Missile', category: 'Weapons Capability', code: '11' },
    { modifier: 'Special Operations Forces (SOF)', category: 'Asset Capability', code: '12' },
    { modifier: 'Possible Submarine Low 1', category: 'Submarine Confidence', code: '13' },
    { modifier: 'Possible Submarine Low 2', category: 'Submarine Confidence', code: '14' },
    { modifier: 'Possible Submarine High 3', category: 'Submarine Confidence', code: '15' },
    { modifier: 'Possible Submarine High 4', category: 'Submarine Confidence', code: '16' },
    { modifier: 'Probable Submarine', category: 'Submarine Confidence', code: '17' },
    { modifier: 'Certain Submarine', category: 'Submarine Confidence', code: '18' },
    { modifier: 'Anti-torpedo Torpedo', category: 'Weapons Capability', code: '19' },
    { modifier: 'Hijacking/Hijacked', category: 'Crime', code: '20' },
    { modifier: 'Hijacker', category: 'Crime', code: '21' },
    { modifier: 'Cyberspace', category: 'Capability', code: '22' },
] as const;
export type SeasubsurfaceModifier1 = (typeof seasubsurfaceModifier1Values)[number];

export const seasubsurfaceModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Air Independent Propulsion', category: 'Ship Propulsion', code: '01' },
    { modifier: 'Diesel Electric, General', category: 'Ship Propulsion', code: '02' },
    { modifier: 'Diesel – Type 1', category: 'Ship Propulsion', code: '03' },
    { modifier: 'Diesel – Type 2', category: 'Ship Propulsion', code: '04' },
    { modifier: 'Diesel – Type 3', category: 'Ship Propulsion', code: '05' },
    { modifier: 'Nuclear Powered, General', category: 'Ship Propulsion', code: '06' },
    { modifier: 'Nuclear – Type 1', category: 'Ship Propulsion', code: '07' },
    { modifier: 'Nuclear – Type 2', category: 'Ship Propulsion', code: '08' },
    { modifier: 'Nuclear – Type 3', category: 'Ship Propulsion', code: '09' },
    { modifier: 'Nuclear – Type 4', category: 'Ship Propulsion', code: '10' },
    { modifier: 'Nuclear – Type 5', category: 'Ship Propulsion', code: '11' },
    { modifier: 'Nuclear – Type 6', category: 'Ship Propulsion', code: '12' },
    { modifier: 'Nuclear – Type 7', category: 'Ship Propulsion', code: '13' },
    { modifier: 'Autonomous Control', category: 'UUV Control', code: '14' },
    { modifier: 'Remotely Piloted', category: 'UUV Control', code: '15' },
    { modifier: 'Expendable', category: 'UUV Control', code: '16' },
    { modifier: 'Cyberspace', category: 'Capability', code: '17' },
] as const;
export type SeasubsurfaceModifier2 = (typeof seasubsurfaceModifier2Values)[number];

export const mineWarfareIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Sea Mine, General', code: '110000' },
    { entity: 'Sea Mine, General', entityType: 'Sea Mine, Bottom', code: '110100' },
    { entity: 'Sea Mine, General', entityType: 'Sea Mine, Moored', code: '110200' },
    { entity: 'Sea Mine, General', entityType: 'Sea Mine, Floating', code: '110300' },
    { entity: 'Sea Mine, General', entityType: 'Sea Mine, Rising', code: '110400' },
    { entity: 'Sea Mine, General', entityType: 'Sea Mine, Other Position', code: '110500' },
    { entity: 'Sea Mine, General', entityType: 'Kingfisher', code: '110600' },
    { entity: 'Sea Mine, General', entityType: 'Small Object, Mine–Like', code: '110700' },
    { entity: 'Sea Mine, General', entityType: 'Exercise Mine, General', code: '110800' },
    {
        entity: 'Sea Mine, General',
        entityType: 'Exercise Mine, General',
        entitySubtype: 'Exercise Mine, Bottom',
        code: '110801',
    },
    {
        entity: 'Sea Mine, General',
        entityType: 'Exercise Mine, General',
        entitySubtype: 'Exercise Mine, Moored',
        code: '110802',
    },
    {
        entity: 'Sea Mine, General',
        entityType: 'Exercise Mine, General',
        entitySubtype: 'Exercise Mine, Floating',
        code: '110803',
    },
    {
        entity: 'Sea Mine, General',
        entityType: 'Exercise Mine, General',
        entitySubtype: 'Exercise Mine, Rising',
        code: '110804',
    },
    { entity: 'Sea Mine, General', entityType: 'Neutralized Mine, General', code: '110900' },
    {
        entity: 'Sea Mine, General',
        entityType: 'Neutralized Mine, General',
        entitySubtype: 'Neutralized Mine, Bottom',
        code: '110901',
    },
    {
        entity: 'Sea Mine, General',
        entityType: 'Neutralized Mine, General',
        entitySubtype: 'Neutralized Mine, Moored',
        code: '110902',
    },
    {
        entity: 'Sea Mine, General',
        entityType: 'Neutralized Mine, General',
        entitySubtype: 'Neutralized Mine, Floating',
        code: '110903',
    },
    {
        entity: 'Sea Mine, General',
        entityType: 'Neutralized Mine, General',
        entitySubtype: 'Neutralized Mine, Rising',
        code: '110904',
    },
    {
        entity: 'Sea Mine, General',
        entityType: 'Neutralized Mine, General',
        entitySubtype: 'Neutralized Mine, Other Position',
        code: '110905',
    },
    { entity: 'Unexploded Ordnance', code: '120000' },
    { entity: 'Sea Mine Decoy', code: '130000' },
    { entity: 'Sea Mine Decoy', entityType: 'Sea Mine Decoy, Bottom', code: '130100' },
    { entity: 'Sea Mine Decoy', entityType: 'Sea Mine Decoy, Moored', code: '130200' },
    { entity: 'Mine–Like Contact (MILCO)', code: '140000' },
    { entity: 'Mine–Like Contact (MILCO)', entityType: 'MILCO - General', code: '140100' },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - General',
        entitySubtype: 'MILCO - General, Confidence Level 1',
        code: '140101',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - General',
        entitySubtype: 'MILCO - General, Confidence Level 2',
        code: '140102',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - General',
        entitySubtype: 'MILCO - General, Confidence Level 3',
        code: '140103',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - General',
        entitySubtype: 'MILCO - General, Confidence Level 4',
        code: '140104',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - General',
        entitySubtype: 'MILCO - General, Confidence Level 5',
        code: '140105',
    },
    { entity: 'Mine–Like Contact (MILCO)', entityType: 'MILCO - Bottom', code: '140200' },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Bottom',
        entitySubtype: 'MILCO - Bottom, Confidence Level 1',
        code: '140201',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Bottom',
        entitySubtype: 'MILCO - Bottom, Confidence Level 2',
        code: '140202',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Bottom',
        entitySubtype: 'MILCO - Bottom, Confidence Level 3',
        code: '140203',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Bottom',
        entitySubtype: 'MILCO - Bottom, Confidence Level 4',
        code: '140204',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Bottom',
        entitySubtype: 'MILCO - Bottom, Confidence Level 5',
        code: '140205',
    },
    { entity: 'Mine–Like Contact (MILCO)', entityType: 'MILCO - Moored', code: '140300' },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Moored',
        entitySubtype: 'MILCO - Moored, Confidence Level 1',
        code: '140301',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Moored',
        entitySubtype: 'MILCO - Moored, Confidence Level 2',
        code: '140302',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Moored',
        entitySubtype: 'MILCO - Moored, Confidence Level 3',
        code: '140303',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Moored',
        entitySubtype: 'MILCO - Moored, Confidence Level 4',
        code: '140304',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Moored',
        entitySubtype: 'MILCO - Moored, Confidence Level 5',
        code: '140305',
    },
    { entity: 'Mine–Like Contact (MILCO)', entityType: 'MILCO - Floating', code: '140400' },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Floating',
        entitySubtype: 'MILCO - Floating, Confidence Level 1',
        code: '140401',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Floating',
        entitySubtype: 'MILCO - Floating, Confidence Level 2',
        code: '140402',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Floating',
        entitySubtype: 'MILCO - Floating, Confidence Level 3',
        code: '140403',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Floating',
        entitySubtype: 'MILCO - Floating, Confidence Level 4',
        code: '140404',
    },
    {
        entity: 'Mine–Like Contact (MILCO)',
        entityType: 'MILCO - Floating',
        entitySubtype: 'MILCO - Floating, Confidence Level 5',
        code: '140405',
    },
    { entity: 'Mine–Like Echo (MILEC), General', code: '150000' },
    {
        entity: 'Mine–Like Echo (MILEC), General',
        entityType: 'Mine–Like Echo, Bottom',
        code: '150100',
    },
    {
        entity: 'Mine–Like Echo (MILEC), General',
        entityType: 'Mine–Like Echo, Moored',
        code: '150200',
    },
    {
        entity: 'Mine–Like Echo (MILEC), General',
        entityType: 'Mine–Like Echo, Floating',
        code: '150300',
    },
    { entity: 'Negative Reacquisition, General', code: '160000' },
    {
        entity: 'Negative Reacquisition, General',
        entityType: 'Negative Reacquisition, Bottom',
        code: '160100',
    },
    {
        entity: 'Negative Reacquisition, General',
        entityType: 'Negative Reacquisition, Moored',
        code: '160200',
    },
    {
        entity: 'Negative Reacquisition, General',
        entityType: 'Negative Reacquisition, Floating',
        code: '160300',
    },
    { entity: 'Obstructor', code: '170000' },
    { entity: 'Obstructor', entityType: 'Neutralized Obstructor', code: '170100' },
    { entity: 'General Mine Anchor', code: '180000' },
    { entity: 'Non-Mine Mine–Like Object (NMLO), General', code: '190000' },
    {
        entity: 'Non-Mine Mine–Like Object (NMLO), General',
        entityType: 'Non-Mine Mine–Like Object, Bottom',
        code: '190100',
    },
    {
        entity: 'Non-Mine Mine–Like Object (NMLO), General',
        entityType: 'Non-Mine Mine–Like Object, Moored',
        code: '190200',
    },
    {
        entity: 'Non-Mine Mine–Like Object (NMLO), General',
        entityType: 'Non-Mine Mine–Like Object, Floating',
        code: '190300',
    },
    { entity: 'Environmental Report Location', code: '200000' },
    { entity: 'Dive Report Location', code: '210000' },
] as const;
export type MinewarfareMainIcon = (typeof mineWarfareIconValues)[number];

export const activitiesIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Incident', code: '110000' },
    { entity: 'Incident', entityType: 'Criminal Activity Incident', code: '110100' },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Arrest',
        code: '110101',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Arson',
        code: '110102',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Attempted Criminal Activity',
        code: '110103',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Drive-by Shooting',
        code: '110104',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Drug Related',
        code: '110105',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Extortion',
        code: '110106',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Graffiti',
        code: '110107',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Killing',
        code: '110108',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Poisoning',
        code: '110109',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Civil Rioting',
        code: '110110',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Booby Trap',
        code: '110111',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Home Eviction',
        code: '110112',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Black Marketing',
        code: '110113',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Vandalism/Loot/Ransack/Plunder',
        code: '110114',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Jail Break',
        code: '110115',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Robbery',
        code: '110116',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Theft',
        code: '110117',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Burglary',
        code: '110118',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Smuggling',
        code: '110119',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Rock Throwing',
        code: '110120',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Dead Body',
        code: '110121',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Sabotage',
        code: '110122',
    },
    {
        entity: 'Incident',
        entityType: 'Criminal Activity Incident',
        entitySubtype: 'Suspicious Activity',
        code: '110123',
    },
    { entity: 'Incident', entityType: 'Bomb/Bombing', code: '110200' },
    {
        entity: 'Incident',
        entityType: 'Bomb/Bombing',
        entitySubtype: 'Bomb Threat',
        code: '110201',
    },
    { entity: 'Incident', entityType: 'IED Event', code: '110300' },
    { entity: 'Incident', entityType: 'IED Event', entitySubtype: 'IED Explosion', code: '110301' },
    {
        entity: 'Incident',
        entityType: 'IED Event',
        entitySubtype: 'Premature IED Explosion',
        code: '110302',
    },
    { entity: 'Incident', entityType: 'IED Event', entitySubtype: 'IED Cache', code: '110303' },
    {
        entity: 'Incident',
        entityType: 'IED Event',
        entitySubtype: 'IED Suicide Bomber',
        code: '110304',
    },
    { entity: 'Incident', entityType: 'Shooting', code: '110400' },
    { entity: 'Incident', entityType: 'Shooting', entitySubtype: 'Sniping', code: '110401' },
    { entity: 'Incident', entityType: 'Illegal Drug Operation', code: '110500' },
    {
        entity: 'Incident',
        entityType: 'Illegal Drug Operation',
        entitySubtype: 'Trafficking',
        code: '110501',
    },
    {
        entity: 'Incident',
        entityType: 'Illegal Drug Operation',
        entitySubtype: 'Illegal Drug Lab',
        code: '110502',
    },
    { entity: 'Incident', entityType: 'Explosion', code: '110600' },
    {
        entity: 'Incident',
        entityType: 'Explosion',
        entitySubtype: 'Grenade Explosion',
        code: '110601',
    },
    {
        entity: 'Incident',
        entityType: 'Explosion',
        entitySubtype: 'Incendiary Explosion',
        code: '110602',
    },
    {
        entity: 'Incident',
        entityType: 'Explosion',
        entitySubtype: 'Mine Explosion',
        code: '110603',
    },
    {
        entity: 'Incident',
        entityType: 'Explosion',
        entitySubtype: 'Mortar Fire Explosion',
        code: '110604',
    },
    {
        entity: 'Incident',
        entityType: 'Explosion',
        entitySubtype: 'Rocket Explosion',
        code: '110605',
    },
    {
        entity: 'Incident',
        entityType: 'Explosion',
        entitySubtype: 'Bomb Explosion',
        code: '110606',
    },
    { entity: 'Civil Disturbance', code: '120000' },
    { entity: 'Civil Disturbance', entityType: 'Demonstration', code: '120100' },
    { entity: 'Operation', code: '130000' },
    { entity: 'Operation', entityType: 'Patrolling', code: '130100' },
    {
        entity: 'Operation',
        entityType: 'Military Information Support Operation (MISO)',
        code: '130200',
    },
    {
        entity: 'Operation',
        entityType: 'Military Information Support Operation (MISO)',
        entitySubtype: 'TV and Radio Propaganda',
        code: '130201',
    },
    { entity: 'Operation', entityType: 'Foraging/Searching', code: '130300' },
    { entity: 'Operation', entityType: 'Recruitment', code: '130400' },
    { entity: 'Operation', entityType: 'Recruitment', entitySubtype: 'Willing', code: '130401' },
    {
        entity: 'Operation',
        entityType: 'Recruitment',
        entitySubtype: 'Unwilling/Coerced',
        code: '130402',
    },
    { entity: 'Operation', entityType: 'Mine Laying', code: '130500' },
    { entity: 'Operation', entityType: 'Spy', code: '130600' },
    { entity: 'Operation', entityType: 'Warrant Served', code: '130700' },
    { entity: 'Operation', entityType: 'Exfiltration', code: '130800' },
    { entity: 'Operation', entityType: 'Infiltration', code: '130900' },
    { entity: 'Operation', entityType: 'Meeting', code: '131000' },
    {
        entity: 'Operation',
        entityType: 'Meeting',
        entitySubtype: 'Polling Place/Election',
        code: '131001',
    },
    { entity: 'Operation', entityType: 'Raid on House', code: '131100' },
    { entity: 'Operation', entityType: 'Emergency Operation', code: '131200' },
    {
        entity: 'Operation',
        entityType: 'Emergency Operation',
        entitySubtype: 'Emergency Collection Evacuation Point',
        code: '131201',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Operation',
        entitySubtype: 'Emergency Food Distribution',
        code: '131202',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Operation',
        entitySubtype: 'Emergency Incident Command Center',
        code: '131203',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Operation',
        entitySubtype: 'Emergency Operations Center',
        code: '131204',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Operation',
        entitySubtype: 'Emergency Public Information Center',
        code: '131205',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Operation',
        entitySubtype: 'Emergency Shelter',
        code: '131206',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Operation',
        entitySubtype: 'Emergency Staging Area',
        code: '131207',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Operation',
        entitySubtype: 'Emergency Water Distribution Center',
        code: '131208',
    },
    { entity: 'Operation', entityType: 'Emergency Medical Operation', code: '131300' },
    {
        entity: 'Operation',
        entityType: 'Emergency Medical Operation',
        entitySubtype: 'EMT Station Location',
        code: '131301',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Medical Operation',
        entitySubtype: 'Health Department Facility',
        code: '131302',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Medical Operation',
        entitySubtype: 'Medical Facilities Outpatient',
        code: '131303',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Medical Operation',
        entitySubtype: 'Morgue',
        code: '131304',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Medical Operation',
        entitySubtype: 'Pharmacy',
        code: '131305',
    },
    {
        entity: 'Operation',
        entityType: 'Emergency Medical Operation',
        entitySubtype: 'Triage',
        code: '131306',
    },
    { entity: 'Operation', entityType: 'Fire Fighting Operation', code: '131400' },
    {
        entity: 'Operation',
        entityType: 'Fire Fighting Operation',
        entitySubtype: 'Fire Hydrant',
        code: '131401',
    },
    {
        entity: 'Operation',
        entityType: 'Fire Fighting Operation',
        entitySubtype: 'Fire Station',
        code: '131402',
    },
    {
        entity: 'Operation',
        entityType: 'Fire Fighting Operation',
        entitySubtype: 'Other Water Supply Location',
        code: '131403',
    },
    { entity: 'Operation', entityType: 'Law Enforcement Operation', code: '131500' },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype:
            'Bureau of Alcohol, Tobacco, Firearms and Explosives (ATF) (Department of Justice)',
        code: '131501',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Border Patrol',
        code: '131502',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Customs Service',
        code: '131503',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Drug Enforcement Administration (DEA)',
        code: '131504',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Department of Justice (DOJ)',
        code: '131505',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Federal Bureau of Investigation (FBI)',
        code: '131506',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Police',
        code: '131507',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Prison',
        code: '131508',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'United States Secret Service(Treas) (USSS)',
        code: '131509',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Transportation Security Administration (TSA)',
        code: '131510',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Coast Guard',
        code: '131511',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'US Marshals Service',
        code: '131512',
    },
    {
        entity: 'Operation',
        entityType: 'Law Enforcement Operation',
        entitySubtype: 'Internal Security Force',
        code: '131513',
    },
    { entity: 'Fire Event', code: '140000' },
    { entity: 'Fire Event', entityType: 'Fire Origin', code: '140100' },
    { entity: 'Fire Event', entityType: 'Smoke', code: '140200' },
    { entity: 'Fire Event', entityType: 'Hot Spot', code: '140300' },
    { entity: 'Fire Event', entityType: 'Non-Residential Fire', code: '140400' },
    { entity: 'Fire Event', entityType: 'Residential Fire', code: '140500' },
    { entity: 'Fire Event', entityType: 'School Fire', code: '140600' },
    { entity: 'Fire Event', entityType: 'Special Needs Fire', code: '140700' },
    { entity: 'Fire Event', entityType: 'Wild Fire', code: '140800' },
    { entity: 'Hazardous Materials', code: '150000' },
    { entity: 'Hazardous Materials', entityType: 'Hazardous Materials Incident', code: '150100' },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Chemical Agent',
        code: '150101',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Corrosive Material',
        code: '150102',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Hazardous when Wet',
        code: '150103',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Explosive Material',
        code: '150104',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Flammable Gas',
        code: '150105',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Flammable Liquid',
        code: '150106',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Flammable Solid',
        code: '150107',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Non-Flammable Gas',
        code: '150108',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Organic Peroxide',
        code: '150109',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Oxidizer',
        code: '150110',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Radioactive Material',
        code: '150111',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Spontaneously Combustible Material',
        code: '150112',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Toxic Gas',
        code: '150113',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Toxic Infectious Material',
        code: '150114',
    },
    {
        entity: 'Hazardous Materials',
        entityType: 'Hazardous Materials Incident',
        entitySubtype: 'Unexploded Ordnance',
        code: '150115',
    },
    { entity: 'Transportation Incident', code: '160000' },
    { entity: 'Transportation Incident', entityType: 'Air', code: '160100' },
    { entity: 'Transportation Incident', entityType: 'Marine', code: '160200' },
    { entity: 'Transportation Incident', entityType: 'Rail', code: '160300' },
    { entity: 'Transportation Incident', entityType: 'Vehicle', code: '160400' },
    { entity: 'Transportation Incident', entityType: 'Wheeled Vehicle Explosion', code: '160500' },
    { entity: 'Natural Event', code: '170000' },
    { entity: 'Natural Event', entityType: 'Geologic', code: '170100' },
    {
        entity: 'Natural Event',
        entityType: 'Geologic',
        entitySubtype: 'Aftershock',
        code: '170101',
    },
    { entity: 'Natural Event', entityType: 'Geologic', entitySubtype: 'Avalanche', code: '170102' },
    {
        entity: 'Natural Event',
        entityType: 'Geologic',
        entitySubtype: 'Earthquake Epicenter',
        code: '170103',
    },
    { entity: 'Natural Event', entityType: 'Geologic', entitySubtype: 'Landslide', code: '170104' },
    {
        entity: 'Natural Event',
        entityType: 'Geologic',
        entitySubtype: 'Subsidence',
        code: '170105',
    },
    {
        entity: 'Natural Event',
        entityType: 'Geologic',
        entitySubtype: 'Volcanic Eruption',
        code: '170106',
    },
    {
        entity: 'Natural Event',
        entityType: 'Geologic',
        entitySubtype: 'Volcanic Threat',
        code: '170107',
    },
    {
        entity: 'Natural Event',
        entityType: 'Geologic',
        entitySubtype: 'Cave Entrance',
        code: '170108',
    },
    { entity: 'Natural Event', entityType: 'Hydro-Meteorological', code: '170200' },
    {
        entity: 'Natural Event',
        entityType: 'Hydro-Meteorological',
        entitySubtype: 'Drought',
        code: '170201',
    },
    {
        entity: 'Natural Event',
        entityType: 'Hydro-Meteorological',
        entitySubtype: 'Flood',
        code: '170202',
    },
    {
        entity: 'Natural Event',
        entityType: 'Hydro-Meteorological',
        entitySubtype: 'Tsunami',
        code: '170203',
    },
    { entity: 'Natural Event', entityType: 'Infestation', code: '170300' },
    { entity: 'Natural Event', entityType: 'Infestation', entitySubtype: 'Bird', code: '170301' },
    { entity: 'Natural Event', entityType: 'Infestation', entitySubtype: 'Insect', code: '170302' },
    {
        entity: 'Natural Event',
        entityType: 'Infestation',
        entitySubtype: 'Microbial',
        code: '170303',
    },
    {
        entity: 'Natural Event',
        entityType: 'Infestation',
        entitySubtype: 'Reptile',
        code: '170304',
    },
    { entity: 'Natural Event', entityType: 'Infestation', entitySubtype: 'Rodent', code: '170305' },
    { entity: 'Individual', code: '180000' },
    { entity: 'Individual', entityType: 'Religious Leader', code: '180100' },
    { entity: 'Individual', entityType: 'Speaker', code: '180200' },
] as const;
export type ActivitiesIcon = (typeof activitiesIconValues)[number];

export const activitiesModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Assassination', category: 'Crime', code: '01' },
    { modifier: 'Execution (Wrongful Killing)', category: 'Crime', code: '02' },
    { modifier: 'Hijacking/Hijacked', category: 'Crime', code: '03' },
    { modifier: 'House–to–House', category: 'Military Information Support Operations', code: '04' },
    { modifier: 'Kidnapping', category: 'Crime', code: '05' },
    { modifier: 'Murder', category: 'Crime', code: '06' },
    { modifier: 'Piracy', category: 'Crime', code: '07' },
    { modifier: 'Rape', category: 'Crime', code: '08' },
    {
        modifier: 'Written Military Information Support Operations',
        category: 'Military Information Support Operations',
        code: '09',
    },
    { modifier: 'Pirate', category: 'Crime', code: '10' },
    { modifier: 'False', category: 'IED Category', code: '11' },
    { modifier: 'Find', category: 'IED Category', code: '12' },
    { modifier: 'Found and Cleared', category: 'IED Category', code: '13' },
    { modifier: 'Hoax (Decoy)', category: 'IED Category', code: '14' },
    { modifier: 'Attempted', category: 'Incident Qualifier', code: '15' },
    { modifier: 'Accident', category: 'Incident Qualifier', code: '16' },
    { modifier: 'Incident', category: 'Incident Qualifier', code: '17' },
    { modifier: 'Theft', category: 'Crime', code: '18' },
    { modifier: 'Hijacker', category: 'Crime', code: '19' },
    { modifier: 'Cyberspace', category: 'Capability', code: '20' },
] as const;
export type ActivitiesModifier1 = (typeof activitiesModifier1Values)[number];

export const activitiesModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Cyberspace', category: 'Capability', code: '01' },
] as const;
export type ActivitiesModifier2 = (typeof activitiesModifier2Values)[number];

export const signalsIntelligenceIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Signal Intercept', code: '110000' },
    { entity: 'Signal Intercept', entityType: 'Communications', code: '110100' },
    { entity: 'Signal Intercept', entityType: 'Jammer', code: '110200' },
    { entity: 'Signal Intercept', entityType: 'Radar', code: '110300' },
] as const;
export type SignalsintelligenceIcon = (typeof signalsIntelligenceIconValues)[number];

export const signalsintelligenceModifier1Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Anti-Aircraft Fire Control', category: 'Land/Sea Surface Radar', code: '01' },
    { modifier: 'Airborne Search and Bombing', category: 'Air Radar', code: '02' },
    { modifier: 'Airborne Intercept', category: 'Air Radar', code: '03' },
    { modifier: 'Altimeter', category: 'Air Radar', code: '04' },
    { modifier: 'Airborne Reconnaissance and Mapping', category: 'Air Radar', code: '05' },
    { modifier: 'Air Traffic Control', category: 'Air/Land/Sea Surface Radar', code: '06' },
    {
        modifier: 'Beacon Transponder (not IFF)',
        category: 'Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '07',
    },
    { modifier: 'Battlefield Surveillance', category: 'Air/Land Radar', code: '08' },
    { modifier: 'Controlled Approach', category: 'Land/Sea Surface Radar', code: '09' },
    { modifier: 'Controlled Intercept', category: 'Air/Land/Sea Surface Radar', code: '10' },
    {
        modifier: 'Cellular/Mobile',
        category: 'Air/Land/Sea Surface/Sea Subsurface Communications',
        code: '11',
    },
    { modifier: 'Coastal Surveillance', category: 'Land Radar', code: '12' },
    { modifier: 'Decoy/Mimic', category: 'Air/Land/Sea Surface/Sea Subsurface Radar', code: '13' },
    {
        modifier: 'Data Transmission',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '14',
    },
    { modifier: 'Earth Surveillance', category: 'Space Radar', code: '15' },
    {
        modifier: 'Early Warning',
        category: 'Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '16',
    },
    { modifier: 'Fire Control', category: 'Air/Land/Sea Surface Radar', code: '17' },
    { modifier: 'Ground Mapping', category: 'Air Radar', code: '18' },
    { modifier: 'Height Finding', category: 'Land/Sea Surface Radar', code: '19' },
    { modifier: 'Harbor Surveillance', category: 'Land Radar', code: '20' },
    {
        modifier: 'Identification, Friend or Foe (Interrogator)',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '21',
    },
    { modifier: 'Instrument Landing System', category: 'Land/Sea Surface Radar', code: '22' },
    { modifier: 'Ionospheric Sounding', category: 'Air/Land Radar', code: '23' },
    {
        modifier: 'Identification, Friend or Foe (Transponder)',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '24',
    },
    {
        modifier: 'Barrage Jammer',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '25',
    },
    {
        modifier: 'Click Jammer',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '26',
    },
    {
        modifier: 'Deceptive Jammer',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '27',
    },
    {
        modifier: 'Frequency Swept Jammer',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '28',
    },
    {
        modifier: 'Jammer (general)',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '29',
    },
    {
        modifier: 'Noise Jammer',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '30',
    },
    {
        modifier: 'Pulsed Jammer',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '31',
    },
    {
        modifier: 'Repeater Jammer',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '32',
    },
    {
        modifier: 'Spot Noise Jammer',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '33',
    },
    {
        modifier: 'Transponder Jammer',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '34',
    },
    { modifier: 'Missile Acquisition', category: 'Air/Land/Sea Surface Radar', code: '35' },
    {
        modifier: 'Missile Control',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '36',
    },
    { modifier: 'Missile Downlink', category: 'Air Radar', code: '37' },
    { modifier: 'Meteorological', category: 'Air/Land/Sea Surface Radar', code: '38' },
    {
        modifier: 'Multi-Function',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '39',
    },
    { modifier: 'Missile Guidance', category: 'Air/Land/Sea Surface Radar', code: '40' },
    { modifier: 'Missile Homing', category: 'Air Radar', code: '41' },
    {
        modifier: 'Missile Tracking',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '42',
    },
    {
        modifier: 'Navigational/General',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '43',
    },
    {
        modifier: 'Navigational/Distance Measuring Asset',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '44',
    },
    {
        modifier: 'Navigation/Terrain Following',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '45',
    },
    {
        modifier: 'Navigational/Weather Avoidance',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '46',
    },
    {
        modifier: 'Omni-Line of Sight (LOS)',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Jammer',
        code: '47',
    },
    { modifier: 'Proximity Use', category: 'Air Radar', code: '48' },
    {
        modifier: 'Point-to-Point Line of Sight (LOS)',
        category: 'Air/Land/Sea Surface/Sea Subsurface Communications',
        code: '49',
    },
    {
        modifier: 'Instrumentation',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '50',
    },
    {
        modifier: 'Range Only',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '51',
    },
    { modifier: 'Sonobuoy', category: 'Sea Surface/Sea Subsurface Radar', code: '52' },
    { modifier: 'Satellite Downlink', category: 'Space Communications', code: '53' },
    { modifier: 'Space', category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar', code: '54' },
    {
        modifier: 'Surface Search',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '55',
    },
    { modifier: 'Shell Tracking', category: 'Land Radar', code: '56' },
    {
        modifier: 'Satellite Uplink',
        category: 'Air/Land/Sea Surface/Sea Subsurface Communications',
        code: '57',
    },
    {
        modifier: 'Target Acquisition',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '58',
    },
    { modifier: 'Target Illumination', category: 'Air/Land/Sea Surface Radar', code: '59' },
    { modifier: 'Tropospheric Scatter', category: 'Land Communications', code: '60' },
    {
        modifier: 'Target Tracking',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '61',
    },
    {
        modifier: 'Unknown',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '62',
    },
    {
        modifier: 'Video Remoting',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '63',
    },
    {
        modifier: 'Experimental',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Radar',
        code: '64',
    },
    {
        modifier: 'Cyberspace',
        category: 'Space/Air/Land/Sea Surface/Sea Subsurface Capability',
        code: '65',
    },
] as const;
export type SignalsintelligenceModifier1 = (typeof signalsintelligenceModifier1Values)[number];

export const signalsintelligenceModifier2Values = [
    { modifier: 'Unspecified', category: '', code: '00' },
    { modifier: 'Cyberspace', category: 'Capability', code: '01' },
] as const;
export type SignalsintelligenceModifier2 = (typeof signalsintelligenceModifier2Values)[number];

export const cyberspaceIconValues = [
    { entity: 'Unspecified', code: '000000' },
    { entity: 'Mission Force', entityType: 'Combat Mission Team', code: '110100' },
    { entity: 'Mission Force', entityType: 'National Mission Team', code: '110200' },
    { entity: 'Mission Force', entityType: 'Cyber Protection Team', code: '110300' },
    { entity: 'Mission Force', entityType: 'Nation State Cyber Threat Actor', code: '110400' },
    { entity: 'Mission Force', entityType: 'Non Nation State Cyber Threat Actor', code: '110500' },
] as const;
export type CyberspaceIcon = (typeof cyberspaceIconValues)[number];

export const airSymbolSet = {
    code: '01',
    name: 'Air',
    icons: airIconValues,
    modifier1Values: airModifier1Values,
    modifier2Values: airModifier2Values,
} as const;

export const airMissileSymbolSet = {
    code: '02',
    name: 'Air Missile',
    icons: airMissileIconValues,
    modifier1Values: airMissileModifier1Values,
    modifier2Values: airMissileModifier2Values,
} as const;

export const spaceSymbolSet = {
    code: '05',
    name: 'Space',
    icons: spaceIconValues,
    modifier1Values: spaceModifier1Values,
    modifier2Values: spaceModifier2Values,
} as const;

export const spacemissileSymbolSet = {
    code: '06',
    name: 'Space Missile',
    icons: spaceMissileIconValues,
    modifier2Values: spacemissileModifier2Values,
} as const;

export const landunitSymbolSet = {
    code: '10',
    name: 'Land Unit',
    icons: landUnitIconValues,
    modifier1Values: landunitModifier1Values,
    modifier2Values: landunitModifier2Values,
    echelonValues: echelonValues,
} as const;

export const landcivilianSymbolSet = {
    code: '11',
    name: 'Land Civilian',
    icons: landCivilianIconValues,
    modifier1Values: landcivilianModifier1Values,
    modifier2Values: landcivilianModifier2Values,
} as const;

export const landassetSymbolSet = {
    code: '15',
    name: 'Land Asset',
    icons: landAssetIconValues,
    modifier1Values: landassetModifier1Values,
    modifier2Values: landassetModifier2Values,
    mobilityValues: mobilityValues,
} as const;

export const landinstallationSymbolSet = {
    code: '20',
    name: 'Land Installation',
    icons: landInstallationIconValues,
    modifier1Values: landinstallationModifier1Values,
    modifier2Values: landinstallationModifier2Values,
} as const;

/*
export const controlmeasureSymbolSet = { 
    code: '25', 
    name: 'Control Measure', 
    icons: controlMeasureIconValues, 
    modifier1Values: controlmeasureModifier1Values, 
} as const 
*/

export const seasurfaceSymbolSet = {
    code: '30',
    name: 'Sea Surface',
    icons: seaSurfaceIconValues,
    modifier1Values: seasurfaceModifier1Values,
    modifier2Values: seasurfaceModifier2Values,
    towedArrayValues: towedArrayValues,
} as const;

export const seasubsurfaceSymbolSet = {
    code: '35',
    name: 'Sea Subsurface',
    icons: seaSubsurfaceIconValues,
    modifier1Values: seasubsurfaceModifier1Values,
    modifier2Values: seasubsurfaceModifier2Values,
    towedArrayValues: towedArrayValues,
} as const;

export const minewarfareSymbolSet = {
    code: '36',
    name: 'Mine Warfare',
    icons: mineWarfareIconValues,
} as const;

export const activitiesSymbolSet = {
    code: '40',
    name: 'Activities',
    icons: activitiesIconValues,
    modifier1Values: activitiesModifier1Values,
    modifier2Values: activitiesModifier2Values,
} as const;

export const signalsIntelligenceSpaceSymbolSet = {
    code: '50',
    name: 'Signals Intelligence – Space',
    icons: signalsIntelligenceIconValues,
    modifier1Values: signalsintelligenceModifier1Values,
    modifier2Values: signalsintelligenceModifier2Values,
} as const;

export const signalsIntelligenceAirSymbolSet = {
    code: '51',
    name: 'Signals Intelligence – Air',
    icons: signalsIntelligenceIconValues,
    modifier1Values: signalsintelligenceModifier1Values,
    modifier2Values: signalsintelligenceModifier2Values,
} as const;

export const signalsIntelligenceLandSymbolSet = {
    code: '52',
    name: 'Signals Intelligence – Land',
    icons: signalsIntelligenceIconValues,
    modifier1Values: signalsintelligenceModifier1Values,
    modifier2Values: signalsintelligenceModifier2Values,
} as const;

export const signalsIntelligenceSurfaceSymbolSet = {
    code: '53',
    name: 'Signals Intelligence – Surface',
    icons: signalsIntelligenceIconValues,
    modifier1Values: signalsintelligenceModifier1Values,
    modifier2Values: signalsintelligenceModifier2Values,
} as const;

export const signalsIntelligenceSubsurfaceSymbolSet = {
    code: '54',
    name: 'Signals Intelligence – Subsurface',
    icons: signalsIntelligenceIconValues,
    modifier1Values: signalsintelligenceModifier1Values,
    modifier2Values: signalsintelligenceModifier2Values,
} as const;

export const cyberspaceSymbolSet = {
    code: '60',
    name: 'Cyberspace',
    icons: cyberspaceIconValues,
} as const;

export const symbolSetValues = [
    airSymbolSet,
    airMissileSymbolSet,
    spaceSymbolSet,
    spacemissileSymbolSet,
    landunitSymbolSet,
    landcivilianSymbolSet,
    landassetSymbolSet,
    landinstallationSymbolSet,
    //controlmeasureSymbolSet,
    seasurfaceSymbolSet,
    seasubsurfaceSymbolSet,
    minewarfareSymbolSet,
    activitiesSymbolSet,
    signalsIntelligenceSpaceSymbolSet,
    signalsIntelligenceAirSymbolSet,
    signalsIntelligenceLandSymbolSet,
    signalsIntelligenceSurfaceSymbolSet,
    signalsIntelligenceSubsurfaceSymbolSet,
    cyberspaceSymbolSet,
] as const;
export type SymbolSetCode = (typeof symbolSetValues)[number]['code'];

export interface Icon {
    readonly entity: string;
    readonly entityType?: string;
    readonly entitySubtype?: string;
    readonly code: string;
}

export interface SymbolSet {
    readonly code: SymbolSetCode;
    readonly icons: readonly Icon[];
    readonly modifier1Values?: readonly IconModifier[];
    readonly modifier2Values?: readonly IconModifier[];
    readonly towedArrayValues?: typeof towedArrayValues;
    readonly mobilityValues?: typeof mobilityValues;
    readonly echelonValues?: typeof echelonValues;
}

export interface IconModifier {
    readonly modifier: string;
    readonly category: string;
    readonly code: string;
}

export const reinforcedReducedValues = [
    {
        title: '+',
        value: '+',
        tags: ['+'],
    },
    {
        title: '-',
        value: '-',
        tags: ['-'],
    },
    {
        title: '±',
        value: '±',
        tags: ['+-', '+/-', '±'],
    },
    {
        title: 'None',
        value: undefined,
        tags: ['none', 'None', 'n/a', ''],
    },
] as const;

export type ReinforcedReduced = (typeof reinforcedReducedValues)[number]['value'];
